/* ============================================================================
 * OrchestrIQ :: CostArchitecture.tsx
 * Cost Architecture & Unit Economics - the working Data Hub.
 *
 * Self-contained module. Imports only: react, ./lib/supabase, ./lib/CostEngine
 * Nothing in App.tsx imports this yet - safe to deploy standalone.
 * ========================================================================== */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { supabase } from "./lib/supabase";
import VoiceField from "./components/VoiceField";
import {
  diagnose, effectiveCostPerBaseUnit, naiveCostPerBaseUnit, yieldLabel, num,
  type CostWorkspace, type CaResource, type CaOffering, type CaBomLine,
  type CaCostPool, type CaChannel, type CaOfferingChannel, type CaBenchmark,
  type CaBusinessContext, type ResourceClass, type OfferingType,
  type PortfolioDiagnosis, type Opportunity,
} from "./lib/CostEngine";
import { computeCapacity, computeRates, costActivity } from "./lib/WorkforceEngine";
import { offeringBreakEven, priceForTargetMargin, priceChangeImpact } from "./lib/PricingEngine";
import { activityBreakdown, allocationBreakdown } from "./lib/CostTransparencyEngine";
import { compareSuppliers, makeVsBuy, type SupplierOption } from "./lib/ProcurementEngine";
import { calculateDepreciation, type CapexItem } from "./lib/CapexEngine";
import { calculateWorkingCapital } from "./lib/WorkingCapitalEngine";
import { presentValue, futureValue, netPresentValue, internalRateOfReturn } from "./lib/TimeValueEngine";
import { runScenario } from "./lib/ScenarioEngine";
import {
  validate, summarise,
  type Finding, type Severity,
} from "./lib/CostValidator";
import {
  researchBlueprint, blueprintToRows, blueprintStats,
  type Blueprint, type BlueprintResult, type Verify,
} from "./lib/BusinessBlueprint";

/* ---------------------------------------------------------------- constants */

const CLASSES: Array<{ v: ResourceClass; label: string; hint: string }> = [
  { v: "MATERIAL",    label: "Material",     hint: "Physical inputs consumed in the product" },
  { v: "LABOUR",      label: "Labour",       hint: "People time - staff, contractors" },
  { v: "EQUIPMENT",   label: "Equipment",    hint: "Machine or asset time" },
  { v: "SUBCONTRACT", label: "Subcontract",  hint: "Work bought in from outside" },
  { v: "DIGITAL",     label: "Software",     hint: "Licences, cloud, API calls" },
  { v: "FACILITY",    label: "Space",        hint: "Rent charged per unit of use" },
  { v: "ENERGY",      label: "Energy",       hint: "Power, fuel, gas" },
  { v: "LOGISTICS",   label: "Freight",      hint: "Transport and delivery" },
  { v: "PACKAGING",   label: "Packaging",    hint: "Boxes, labels, wrapping" },
  { v: "OTHER",       label: "Other",        hint: "Anything else you buy" },
];

const OFFERING_TYPES: Array<{ v: OfferingType; label: string }> = [
  { v: "UNIT",          label: "Product unit" },
  { v: "BATCH",         label: "Batch / run" },
  { v: "PROJECT",       label: "Project / engagement" },
  { v: "BILLABLE_HOUR", label: "Billable hour" },
  { v: "SUBSCRIPTION",  label: "Subscription" },
  { v: "TRANSACTION",   label: "Per transaction" },
  { v: "SUB_ASSEMBLY",  label: "Sub-recipe (not sold directly)" },
];

const ARCHETYPES = [
  { v: "manufacturing_discrete", label: "Manufacturing - discrete (parts, assemblies)" },
  { v: "manufacturing_process",  label: "Manufacturing - process (steel, chemicals, cement)" },
  { v: "food_beverage",          label: "Food & beverage (restaurant, bakery, food plant)" },
  { v: "retail_trading",         label: "Retail / trading" },
  { v: "professional_services",  label: "Professional services (consulting, agency, legal)" },
  { v: "saas_digital",           label: "SaaS / digital product" },
  { v: "logistics_transport",    label: "Logistics & transport" },
  { v: "healthcare",             label: "Healthcare delivery" },
  { v: "construction",           label: "Construction & contracting" },
  { v: "hospitality",            label: "Hotels & hospitality" },
  { v: "education",              label: "Education & training" },
  { v: "agriculture",            label: "Agriculture" },
  { v: "other",                  label: "Other" },
];

const CHANNEL_TYPES = [
  { v: "direct",       label: "Direct / own store" },
  { v: "marketplace",  label: "Marketplace" },
  { v: "aggregator",   label: "Aggregator (Swiggy, Zomato, etc.)" },
  { v: "distributor",  label: "Distributor" },
  { v: "reseller",     label: "Reseller / partner" },
  { v: "retail",       label: "Retail chain" },
  { v: "field_sales",  label: "Field sales" },
  { v: "inside_sales", label: "Inside sales" },
  { v: "export",       label: "Export" },
];

const ALLOCATION_BASES = [
  { v: "revenue",          label: "By revenue share" },
  { v: "units",            label: "By units made" },
  { v: "direct_cost",      label: "By direct cost" },
  { v: "constraint_hours", label: "By bottleneck hours" },
  { v: "equal",            label: "Split equally" },
];

type TabKey = "start" | "setup" | "inputs" | "products" | "channels" | "diagnostics" | "history";

// ── MODULE 1 — COST PROJECT LIFECYCLE ────────────────────────────────────────
// THE CONFIRMED GAP: every table below (resources, offerings, channels...) was
// scoped to a single ongoing workspace per user - never a discrete, nameable,
// completable "Cost Project" with a lifecycle and a History, which is the
// very first thing the product spec asks for. This wraps the existing live
// workspace in a project concept: complete a project and its ENTIRE current
// state is frozen as a full-fidelity snapshot, then the live workspace clears
// for the next one. Nothing about the existing tables changed to build this -
// the snapshot is additive, so nothing already working can break.
interface CaProject {
  id: string; user_id: string; name: string; description: string | null;
  status: "draft" | "discovery" | "modeling" | "review" | "complete";
  created_at: string; updated_at: string; completed_at: string | null;
}
interface CaProjectSnapshot {
  id: string; project_id: string; created_at: string;
  business_context: any; resources: any[]; offerings: any[]; bom_lines: any[];
  cost_pools: any[]; channels: any[]; offering_channels: any[]; diagnosis: any;
}

/* ------------------------------------------------------------------ styling */

const V = (name: string, fallback: string) => `var(--oiq-${name}, ${fallback})`;

const S: Record<string, React.CSSProperties> = {
  wrap:      { padding: "14px 16px 40px", color: V("ink", "#e6edf3"), fontSize: 13 },
  h1:        { fontSize: 17, fontWeight: 700, margin: 0, letterSpacing: -0.2 },
  sub:       { fontSize: 11, color: V("muted", "#8b98a5"), marginTop: 3 },
  tabs:      { display: "flex", gap: 4, margin: "14px 0 16px", flexWrap: "wrap", borderBottom: `1px solid ${V("border", "#1e2a38")}`, paddingBottom: 0 },
  tab:       { padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", background: "transparent", border: "none", borderBottom: "2px solid transparent", color: V("muted", "#8b98a5"), borderRadius: 0 },
  tabOn:     { color: V("accent", "#4ADE80"), borderBottom: `2px solid ${V("accent", "#4ADE80")}` },
  card:      { background: V("surface", "#0d1520"), border: `1px solid ${V("border", "#1e2a38")}`, borderRadius: V("radius", "8px") as string, padding: 14, marginBottom: 14 },
  cardH:     { fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, color: V("muted", "#8b98a5"), marginBottom: 10 },
  table:     { width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed", minWidth: 720 },
  th:        { textAlign: "left", padding: "7px 8px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: V("muted", "#8b98a5"), borderBottom: `1px solid ${V("border", "#1e2a38")}`, whiteSpace: "nowrap" },
  td:        { padding: "5px 8px", borderBottom: `1px solid ${V("faint", "#16202c")}`, verticalAlign: "middle", overflow: "hidden", textOverflow: "ellipsis" },
  inp:       { width: "100%", background: V("bg", "#070c18"), border: `1px solid ${V("border", "#1e2a38")}`, borderRadius: 5, color: V("ink", "#e6edf3"), padding: "5px 7px", fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" },
  btn:       { background: V("accent", "#4ADE80"), color: V("accentText", "#06210f"), border: "none", borderRadius: 6, padding: "7px 13px", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  btnGhost:  { background: "transparent", color: V("muted", "#8b98a5"), border: `1px solid ${V("border", "#1e2a38")}`, borderRadius: 6, padding: "6px 11px", fontSize: 11, fontWeight: 600, cursor: "pointer" },
  btnDel:    { background: "transparent", color: V("muted", "#8b98a5"), border: "none", cursor: "pointer", fontSize: 15, lineHeight: 1, padding: "2px 6px" },
  tile:      { flex: "1 1 150px", minWidth: 140, background: V("surface", "#0d1520"), border: `1px solid ${V("border", "#1e2a38")}`, borderRadius: V("radius", "8px") as string, padding: "11px 13px" },
  tileL:     { fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: V("muted", "#8b98a5"), fontWeight: 600 },
  tileV:     { fontSize: 19, fontWeight: 700, marginTop: 5, letterSpacing: -0.4 },
  tileS:     { fontSize: 10, color: V("muted", "#8b98a5"), marginTop: 3 },
  empty:     { textAlign: "center", padding: "38px 20px", color: V("muted", "#8b98a5") },
  chip:      { display: "inline-block", padding: "2px 7px", borderRadius: 20, fontSize: 9.5, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase" },
  note:      { fontSize: 11, color: V("muted", "#8b98a5"), lineHeight: 1.55 },
  grid2:     { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 11 },
  lbl:       { fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: V("muted", "#8b98a5"), marginBottom: 4, display: "block" },
  tableWide: { width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed", minWidth: 1080 },
  scroll:    { overflowX: "auto", WebkitOverflowScrolling: "touch" },
};

const OK   = { bg: "rgba(74,222,128,.12)", fg: "#4ADE80" };
const WARN = { bg: "rgba(251,191,36,.12)", fg: "#FBBF24" };
const BAD  = { bg: "rgba(248,113,113,.12)", fg: "#F87171" };
const NEU  = { bg: "rgba(139,152,165,.12)", fg: "#8b98a5" };

/* ---------------------------------------------------------------- utilities */

function uid(): string {
  const c: any = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function fmtMoney(v: number, cur = "INR"): string {
  const sym = cur === "INR" ? "\u20B9" : cur === "USD" ? "$" : cur === "EUR" ? "\u20AC" : cur + " ";
  const n = Math.abs(v);
  const s = n >= 1e7 ? (n / 1e7).toFixed(2) + " Cr"
          : n >= 1e5 ? (n / 1e5).toFixed(2) + " L"
          : Math.round(n).toLocaleString("en-IN");
  return (v < 0 ? "-" : "") + sym + s;
}

/* ------------------------------------------------------------ input widgets */

interface CellProps { value: any; onChange: (v: any) => void; placeholder?: string; width?: number | string; align?: "left" | "right"; }

const TextCell: React.FC<CellProps> = ({ value, onChange, placeholder, width }) => (
  <input style={{ ...S.inp, width: width ?? "100%" }} value={value ?? ""} placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)} />
);

const NumCell: React.FC<CellProps & { suffix?: string }> = ({ value, onChange, placeholder, width, suffix }) => {
  const [raw, setRaw] = useState<string>(value == null ? "" : String(value));
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setRaw(value == null ? "" : String(value)); }, [value]);
  return (
    <div style={{ position: "relative" }}>
      <input inputMode="decimal" style={{ ...S.inp, width: width ?? "100%", textAlign: "right", paddingRight: suffix ? 22 : 7 }}
        value={raw} placeholder={placeholder}
        onFocus={() => { focused.current = true; }}
        onBlur={() => { focused.current = false; const n = parseFloat(raw); onChange(Number.isFinite(n) ? n : null); setRaw(Number.isFinite(n) ? String(n) : ""); }}
        onChange={(e) => { const t = e.target.value; setRaw(t); const n = parseFloat(t); if (Number.isFinite(n)) onChange(n); else if (t === "") onChange(null); }} />
      {suffix && <span style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: V("muted", "#8b98a5"), pointerEvents: "none" }}>{suffix}</span>}
    </div>
  );
};

const SelectCell: React.FC<{ value: any; onChange: (v: string) => void; options: Array<{ v: string; label: string }>; width?: number | string }> =
  ({ value, onChange, options, width }) => (
  <select style={{ ...S.inp, width: width ?? "100%", cursor: "pointer" }} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
    {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
  </select>
);

const Chip: React.FC<{ tone: { bg: string; fg: string }; children: React.ReactNode }> = ({ tone, children }) => (
  <span style={{ ...S.chip, background: tone.bg, color: tone.fg }}>{children}</span>
);

const Tile: React.FC<{ label: string; value: string; sub?: string; tone?: string }> = ({ label, value, sub, tone }) => (
  <div style={S.tile}>
    <div style={S.tileL}>{label}</div>
    <div style={{ ...S.tileV, color: tone || V("ink", "#e6edf3") }}>{value}</div>
    {sub && <div style={S.tileS}>{sub}</div>}
  </div>
);

const Empty: React.FC<{ title: string; body: string; action?: React.ReactNode }> = ({ title, body, action }) => (
  <div style={S.empty}>
    <div style={{ fontSize: 13, fontWeight: 700, color: V("ink", "#e6edf3"), marginBottom: 6 }}>{title}</div>
    <div style={{ ...S.note, maxWidth: 440, margin: "0 auto 14px" }}>{body}</div>
    {action}
  </div>
);

/* ---------------------------------------------------------- field-level flag */

export type FlagLookup = (entityId: string, field: string) => Finding[];
export type AcceptFn = (f: Finding, reason: string) => void;

const SEV_TONE2: Record<Severity, { bg: string; fg: string }> = { error: BAD, warning: WARN, info: NEU };

/**
 * Wraps a single input. If that exact field has a problem it outlines the box,
 * shows a marker, and opens the reason + consequence right where the value was
 * typed. Popover is position:fixed so table overflow cannot clip it.
 */
const Flag: React.FC<{
  findings: Finding[];
  onAccept: AcceptFn;
  children: React.ReactNode;
}> = ({ findings, onAccept, children }) => {
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setAsking(null); }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (!findings.length) return <>{children}</>;

  const sev: Severity = findings.some((f) => f.severity === "error") ? "error"
    : findings.some((f) => f.severity === "warning") ? "warning" : "info";
  const tone = SEV_TONE2[sev];

  const toggle = () => {
    if (!open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      const w = 340;
      setPos({
        top: Math.min(r.bottom + 6, (typeof window !== "undefined" ? window.innerHeight : 800) - 300),
        left: Math.max(8, Math.min(r.left, (typeof window !== "undefined" ? window.innerWidth : 1200) - w - 8)),
      });
    }
    setOpen(!open); setAsking(null);
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div style={{ outline: `1.5px solid ${tone.fg}`, outlineOffset: 1, borderRadius: 6, background: tone.bg }}>
        {children}
      </div>
      <button onClick={toggle} title={findings[0].title}
        style={{ position: "absolute", top: -7, right: -7, width: 17, height: 17, borderRadius: "50%",
                 border: "none", background: tone.fg, color: "#fff", fontSize: 11, fontWeight: 800,
                 lineHeight: "17px", padding: 0, cursor: "pointer", zIndex: 3 }}>!</button>

      {open && pos && (
        <div style={{ position: "fixed", top: pos.top, left: pos.left, width: 340, zIndex: 9999,
                      background: V("surface", "#0d1520"), border: `1px solid ${tone.fg}`,
                      borderRadius: 8, padding: 13, boxShadow: "0 10px 32px rgba(0,0,0,.42)" }}>
          {findings.map((f, i) => (
            <div key={f.id} style={{ marginTop: i ? 13 : 0, paddingTop: i ? 11 : 0,
                                     borderTop: i ? `1px solid ${V("faint", "#16202c")}` : "none" }}>
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 5 }}>
                <Chip tone={SEV_TONE2[f.severity]}>{f.severity === "error" ? "Wrong" : f.severity === "warning" ? "Check" : "Note"}</Chip>
                <span style={{ fontSize: 12, fontWeight: 700 }}>{f.title}</span>
              </div>
              <div style={{ ...S.note, marginBottom: 9 }}>{f.why}</div>

              {f.impact && (
                <div style={{ display: "flex", gap: 7, marginBottom: 9 }}>
                  <div style={{ flex: 1, background: V("bg", "#070c18"), border: `1px solid ${BAD.fg}`, borderRadius: 5, padding: "7px 8px" }}>
                    <div style={{ fontSize: 8.5, textTransform: "uppercase", letterSpacing: .4, color: V("muted", "#8b98a5"), fontWeight: 700 }}>Yours: {f.currentValue}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, marginTop: 3 }}>{f.impact.withCurrent}</div>
                  </div>
                  <div style={{ flex: 1, background: V("bg", "#070c18"), border: `1px solid ${OK.fg}`, borderRadius: 5, padding: "7px 8px" }}>
                    <div style={{ fontSize: 8.5, textTransform: "uppercase", letterSpacing: .4, color: V("muted", "#8b98a5"), fontWeight: 700 }}>If {f.suggestedValue}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, marginTop: 3, color: OK.fg }}>{f.impact.withSuggested}</div>
                  </div>
                </div>
              )}
              {!f.impact && f.suggestedValue && (
                <div style={{ ...S.note, marginBottom: 9 }}>Suggested: <strong style={{ color: OK.fg }}>{f.suggestedValue}</strong></div>
              )}
              {f.impact && <div style={{ fontSize: 9.5, color: V("muted", "#8b98a5"), marginBottom: 9 }}>{f.impact.label}</div>}

              {asking === f.id ? (
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  <input style={{ ...S.inp, flex: "1 1 100%" }} value={reason} autoFocus
                    placeholder="Why is it correct? (optional)"
                    onChange={(e) => setReason(e.target.value)} />
                  <button style={S.btn} onClick={() => { onAccept(f, reason); setOpen(false); setAsking(null); setReason(""); }}>Confirm</button>
                  <button style={S.btnGhost} onClick={() => setAsking(null)}>Cancel</button>
                </div>
              ) : (
                <button style={S.btnGhost} onClick={() => setAsking(f.id)}>My value is correct &mdash; keep it</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ============================================================================
 * MAIN COMPONENT
 * ========================================================================== */

export interface CostArchitectureProps {
  showToast?: (msg: string, kind?: string) => void;
  companyName?: string;
  onDiagnosis?: (d: PortfolioDiagnosis) => void;
  /** (prompt, useWebSearch) => text. The research ladder toggles search itself. */
  callAI?: (prompt: string, useWebSearch: boolean) => Promise<string>;
  /** THE FIX: without this, voice input here could only ever be the free
   *  native browser engine - there was no way for this module to know
   *  whether the user had an OpenAI key at all. Same key-lookup function
   *  already threaded into Workspace and Live Boardroom. */
  getProviderKey?: (providerId: string) => string | undefined;
}

export default function CostArchitecture({ showToast, companyName, onDiagnosis, callAI, getProviderKey }: CostArchitectureProps) {
  const openaiKey = getProviderKey?.("openai");
  const [tab, setTab] = useState<TabKey>("start");
  const [showSOP, setShowSOP] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [ctx, setCtx] = useState<CaBusinessContext | null>(null);
  const [resources, setResources] = useState<CaResource[]>([]);
  // MODULE 08 — alternative supplier quotes, one resource can have several.
  const [supplierOptions, setSupplierOptions] = useState<Array<SupplierOption & { resource_id: string }>>([]);
  const [offerings, setOfferings] = useState<CaOffering[]>([]);
  const [bomLines, setBomLines] = useState<CaBomLine[]>([]);
  const [costPools, setCostPools] = useState<CaCostPool[]>([]);
  const [capexItems, setCapexItems] = useState<Array<CapexItem & { linked_cost_pool_id?: string | null }>>([]); // MODULE 9
  const [channels, setChannels] = useState<CaChannel[]>([]);
  const [offeringChannels, setOfferingChannels] = useState<CaOfferingChannel[]>([]);
  const [benchmarks, setBenchmarks] = useState<CaBenchmark[]>([]);
  const [openOffering, setOpenOffering] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Set<string>>(new Set());

  // MODULE 1 — projects and their frozen history.
  const [projects, setProjects] = useState<CaProject[]>([]);
  const [activeProject, setActiveProject] = useState<CaProject | null>(null);
  const [snapshots, setSnapshots] = useState<CaProjectSnapshot[]>([]);
  const [viewingSnapshot, setViewingSnapshot] = useState<CaProjectSnapshot | null>(null);
  const [completing, setCompleting] = useState(false);

  const toast = useCallback((m: string, k?: string) => { if (showToast) showToast(m, k); }, [showToast]);

  /* ---------------------------------------------------------------- loading */

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: auth } = await supabase.auth.getUser();
        const uidNow = auth?.user?.id ?? null;
        if (cancelled) return;
        setUserId(uidNow);
        if (!uidNow) { setErr("Sign in to use Cost Architecture."); setLoading(false); return; }

        const [bc, rs, of, bl, cp, ch, oc, bm, vo, pr, so, cx] = await Promise.all([
          supabase.from("ca_business_context").select("*").eq("user_id", uidNow).maybeSingle(),
          supabase.from("ca_resources").select("*").eq("user_id", uidNow).order("created_at", { ascending: true }),
          supabase.from("ca_offerings").select("*").eq("user_id", uidNow).order("created_at", { ascending: true }),
          supabase.from("ca_bom_lines").select("*").eq("user_id", uidNow).order("sequence", { ascending: true }),
          supabase.from("ca_cost_pools").select("*").eq("user_id", uidNow),
          supabase.from("ca_channels").select("*").eq("user_id", uidNow),
          supabase.from("ca_offering_channels").select("*").eq("user_id", uidNow),
          supabase.from("ca_benchmarks").select("*"),
          supabase.from("ca_validation_overrides").select("finding_id").eq("user_id", uidNow),
          supabase.from("ca_projects").select("*").eq("user_id", uidNow).order("updated_at", { ascending: false }),
          // MODULE 8 — alternative supplier quotes, additive to the load
          // that already exists here; nothing above it changed.
          supabase.from("ca_supplier_options").select("*").eq("user_id", uidNow),
          // MODULE 9 — CAPEX items, also purely additive.
          supabase.from("ca_capex_items").select("*").eq("user_id", uidNow),
        ]);
        if (cancelled) return;
        setSupplierOptions((so.data as any[]) ?? []);
        setCapexItems((cx.data as CapexItem[]) ?? []);

        // MODULE 1: the active project is whichever one is not yet complete.
        // If someone genuinely has none yet (shouldn't happen after the
        // migration that gave every existing user a default one), create it
        // here rather than leaving the workspace project-less.
        const allProjects = (pr.data as CaProject[]) ?? [];
        let active = allProjects.find(p => p.status !== "complete") ?? null;
        if (!active) {
          const { data: created } = await supabase.from("ca_projects")
            .insert({ user_id: uidNow, name: "My Cost Model", status: "draft" }).select().single();
          active = created as CaProject;
          allProjects.unshift(active);
        }
        setProjects(allProjects);
        setActiveProject(active);

        setCtx((bc.data as CaBusinessContext) ?? null);
        setResources((rs.data as CaResource[]) ?? []);
        setOfferings((of.data as CaOffering[]) ?? []);
        setBomLines((bl.data as CaBomLine[]) ?? []);
        setCostPools((cp.data as CaCostPool[]) ?? []);
        setChannels((ch.data as CaChannel[]) ?? []);
        setOfferingChannels((oc.data as CaOfferingChannel[]) ?? []);
        setBenchmarks((bm.data as CaBenchmark[]) ?? []);
        setOverrides(new Set(((vo.data as Array<{ finding_id: string }>) ?? []).map((x) => x.finding_id)));
        setTab((of.data ?? []).length ? "diagnostics" : "start");
      } catch (e: any) {
        if (!cancelled) setErr(e?.message || "Could not load your cost data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* ------------------------------------------------------------ persistence */

  const queue = useRef<Map<string, { table: string; row: any }>>(new Map());
  const timer = useRef<any>(null);

  // THE SECOND CONFIRMED CAUSE OF LOST WORK: every edit is queued here and
  // saved 900ms later, so typing feels instant. But the cleanup below used to
  // CANCEL that pending timer on unmount without ever running it - so an
  // edit made right before switching away, closing the tab, or navigating to
  // a different module could be discarded with no warning at all. hasUnsaved
  // now makes that risk VISIBLE rather than invisible, and flush() is called
  // for real whenever the component is about to go away, not just cleared.
  const [hasUnsaved, setHasUnsaved] = useState(false);

  const flush = useCallback(async () => {
    if (!queue.current.size) return;
    const batch = Array.from(queue.current.values());
    queue.current.clear();
    setSaving(true);
    try {
      const byTable = new Map<string, any[]>();
      for (const b of batch) {
        const arr = byTable.get(b.table) || [];
        arr.push(b.row);
        byTable.set(b.table, arr);
      }
      for (const [table, rows] of Array.from(byTable.entries())) {
        const clean = rows.map((r) => { const { effective_cost_per_base_unit, created_at, updated_at, ...rest } = r; return rest; });
        // Same fix as applyBlueprint: business context is uniquely keyed by
        // user_id, not id - every write path has to agree on that, not just
        // the one the original bug report happened to be reproducing.
        const conflictCol = table === "ca_business_context" ? "user_id" : "id";
        const { error } = await supabase.from(table).upsert(clean, { onConflict: conflictCol });
        if (error) throw error;
      }
      setHasUnsaved(false);
    } catch (e: any) {
      toast("Could not save: " + (e?.message || "unknown error"), "error");
      setErr(e?.message || "Save failed");
      setHasUnsaved(true); // the save genuinely failed - say so, don't pretend it's fine
    } finally {
      setSaving(false);
    }
  }, [toast]);

  const persist = useCallback((table: string, row: any) => {
    queue.current.set(table + ":" + row.id, { table, row });
    setHasUnsaved(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { void flush(); }, 900);
  }, [flush]);

  // Flush for REAL on unmount - the fix for the exact bug reported. Switching
  // to a different OrchestrIQ module unmounts this component; the old code
  // only cleared the pending timer, discarding whatever was queued. Now the
  // save actually runs before the component disappears.
  useEffect(() => () => {
    if (timer.current) { clearTimeout(timer.current); void flush(); }
  }, [flush]);

  // A safety net for the browser tab closing or reloading outright, which a
  // React unmount effect cannot reliably catch on its own.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (queue.current.size) { void flush(); e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [flush]);

  const removeRow = useCallback(async (table: string, id: string) => {
    try {
      const { error } = await supabase.from(table).delete().eq("id", id);
      if (error) throw error;
    } catch (e: any) { toast("Could not delete: " + (e?.message || ""), "error"); }
  }, [toast]);

  /* ------------------------------------------------------------- mutations */

  const patchCtx = (p: Partial<CaBusinessContext>) => {
    if (!userId) return;
    const next = { ...(ctx || { id: uid(), user_id: userId }), ...p } as CaBusinessContext;
    setCtx(next);
    persist("ca_business_context", next);
  };

  const patchRes = (id: string, p: Partial<CaResource>) => {
    setResources((prev) => {
      // THE FIX: an "AI ESTIMATE" badge that never clears, even after you've
      // actually corrected the number, becomes permanently misleading -
      // exactly the opposite of what it's for. Editing the price you were
      // actually uncertain about now counts as confirming it.
      const clearsConfidence = p.purchase_price !== undefined;
      const next = prev.map((r) => (r.id === id ? { ...r, ...p, ...(clearsConfidence ? { data_confidence: null } : {}) } : r));
      const row = next.find((r) => r.id === id);
      if (row) persist("ca_resources", row);
      return next;
    });
  };
  const patchOff = (id: string, p: Partial<CaOffering>) => {
    setOfferings((prev) => {
      const clearsConfidence = p.list_price !== undefined;
      const next = prev.map((o) => (o.id === id ? { ...o, ...p, ...(clearsConfidence ? { data_confidence: null } : {}) } : o));
      const row = next.find((o) => o.id === id);
      if (row) persist("ca_offerings", row);
      return next;
    });
  };
  const patchBom = (id: string, p: Partial<CaBomLine>) => {
    setBomLines((prev) => {
      const next = prev.map((b) => (b.id === id ? { ...b, ...p } : b));
      const row = next.find((b) => b.id === id);
      if (row) persist("ca_bom_lines", row);
      return next;
    });
  };
  const patchPool = (id: string, p: Partial<CaCostPool>) => {
    setCostPools((prev) => {
      const next = prev.map((c) => (c.id === id ? { ...c, ...p } : c));
      const row = next.find((c) => c.id === id);
      if (row) persist("ca_cost_pools", row);
      return next;
    });
  };
  const patchCh = (id: string, p: Partial<CaChannel>) => {
    setChannels((prev) => {
      const next = prev.map((c) => (c.id === id ? { ...c, ...p } : c));
      const row = next.find((c) => c.id === id);
      if (row) persist("ca_channels", row);
      return next;
    });
  };
  const patchOC = (id: string, p: Partial<CaOfferingChannel>) => {
    setOfferingChannels((prev) => {
      const next = prev.map((c) => (c.id === id ? { ...c, ...p } : c));
      const row = next.find((c) => c.id === id);
      if (row) persist("ca_offering_channels", row);
      return next;
    });
  };

  const addResource = () => {
    if (!userId) return;
    const r: CaResource = { id: uid(), user_id: userId, name: "", resource_class: "MATERIAL",
      purchase_uom: "kg", purchase_qty: 1, purchase_price: 0, base_uom: "kg",
      conversion_factor: 1, effective_yield_pct: 100, is_variable: true };
    setResources((p) => [...p, r]); persist("ca_resources", r);
  };
  const addOffering = () => {
    if (!userId) return;
    const o: CaOffering = { id: uid(), user_id: userId, name: "", offering_type: "UNIT",
      output_uom: "unit", batch_size: 1, list_price: 0, monthly_volume: 0, is_active: true };
    setOfferings((p) => [...p, o]); persist("ca_offerings", o); setOpenOffering(o.id);
  };
  const addBomLine = (offeringId: string) => {
    if (!userId || !resources.length) { toast("Add at least one input first.", "warn"); return; }
    const b: CaBomLine = { id: uid(), user_id: userId, offering_id: offeringId, child_type: "RESOURCE",
      child_resource_id: resources[0].id, qty_per_unit: 0, uom: resources[0].base_uom || "unit",
      process_scrap_pct: 0, applies_per: "UNIT", sequence: bomLines.filter((x) => x.offering_id === offeringId).length };
    setBomLines((p) => [...p, b]); persist("ca_bom_lines", b);
  };
  const addPool = () => {
    if (!userId) return;
    const c: CaCostPool = { id: uid(), user_id: userId, name: "", category: "facility",
      amount: 0, period: "monthly", allocation_basis: "revenue", is_avoidable: false };
    setCostPools((p) => [...p, c]); persist("ca_cost_pools", c);
  };
  const addChannel = () => {
    if (!userId) return;
    const c: CaChannel = { id: uid(), user_id: userId, name: "", channel_type: "direct",
      discount_pct: 0, commission_pct: 0, packaging_cost: 0, gst_pct: 0, returns_pct: 0 };
    setChannels((p) => [...p, c]); persist("ca_channels", c);
  };
  const linkChannel = (offeringId: string, channelId: string) => {
    if (!userId) return;
    if (offeringChannels.some((x) => x.offering_id === offeringId && x.channel_id === channelId)) return;
    const l: CaOfferingChannel = { id: uid(), user_id: userId, offering_id: offeringId, channel_id: channelId, volume_share_pct: 0 };
    setOfferingChannels((p) => [...p, l]); persist("ca_offering_channels", l);
  };

  const delRes = (id: string) => { setResources((p) => p.filter((r) => r.id !== id));
    setBomLines((p) => p.filter((b) => b.child_resource_id !== id)); void removeRow("ca_resources", id); };
  const delOff = (id: string) => { setOfferings((p) => p.filter((o) => o.id !== id));
    setBomLines((p) => p.filter((b) => b.offering_id !== id));
    setOfferingChannels((p) => p.filter((c) => c.offering_id !== id)); void removeRow("ca_offerings", id); };
  const delBom = (id: string) => { setBomLines((p) => p.filter((b) => b.id !== id)); void removeRow("ca_bom_lines", id); };
  // THE DIRECT FIX FOR THE REPORTED PROBLEM: previously the only way to
  // start completely fresh was the indirect "Complete Project" flow, and
  // Settings' own "Reset Data" button never actually touched this
  // module's real database rows at all - only local browser state. This
  // calls the new, verified reset_cost_architecture function, which does
  // the real thing, scoped strictly to this account.
  const delPool = (id: string) => { setCostPools((p) => p.filter((c) => c.id !== id)); void removeRow("ca_cost_pools", id); };
  // THE DIRECT FIX FOR THE REPORTED PROBLEM: previously the only way to
  // start completely fresh was the indirect "Complete Project" flow, and
  // Settings' own "Reset Data" button never actually touched this
  // module's real database rows at all - only local browser state. This
  // calls the new, verified reset_cost_architecture function, which does
  // the real thing, scoped strictly to this account.
  const resetWorkspace = async () => {
    if (!userId) return;
    if (!window.confirm("This permanently deletes ALL Cost Architecture data — every resource, offering, cost pool, and project in History. This cannot be undone. Continue?")) return;
    try {
      const { error } = await supabase.rpc("reset_cost_architecture", { p_user_id: userId });
      if (error) throw error;
      setResources([]); setOfferings([]); setBomLines([]); setCostPools([]);
      setChannels([]); setOfferingChannels([]); setProjects([]); setSnapshots([]);
      setOverrides(new Set()); setCtx(null);
      const { data: created } = await supabase.from("ca_projects")
        .insert({ user_id: userId, name: "New Cost Model", status: "draft" }).select().single();
      setActiveProject(created as CaProject);
      setTab("start");
      toast("Cost Architecture reset. Starting fresh.", "success");
    } catch (e:any) { toast("Could not reset: " + (e?.message || "unknown error"), "error"); }
  };
  // THE OTHER CONFIRMED GAP: individual resources and offerings could
  // already be deleted, but a completed project sitting in History never
  // could be - the only way to make it go away was a full workspace wipe.
  // Its snapshot cascade-deletes automatically (confirmed against the
  // schema before writing this), so removing the project row is enough.
  const delProject = async (id: string) => {
    if (!window.confirm("Delete this project from History permanently? This cannot be undone.")) return;
    setProjects((p) => p.filter((pr) => pr.id !== id));
    setSnapshots((s) => s.filter((sn) => sn.project_id !== id));
    try { const { error } = await supabase.from("ca_projects").delete().eq("id", id); if (error) throw error; }
    catch (e:any) { toast("Could not delete: " + (e?.message || "unknown error"), "error"); }
  };
  // MODULE 08 — add/remove an alternative supplier quote for a resource.
  // Deliberately its own small table, not a change to ca_resources itself:
  // the resource's own fields stay "the supplier you're actually using",
  // this is purely the other options being compared against it.
  const addSupplierOption = async (resourceId: string) => {
    if (!userId) return;
    const row = { id: uid(), resource_id: resourceId, user_id: userId, supplier_name: "New supplier",
      purchase_price: 0, freight_cost: 0, duty_cost: 0, other_landed_cost: 0 };
    setSupplierOptions((p) => [...p, row as any]);
    const { error } = await supabase.from("ca_supplier_options").insert(row);
    if (error) { showToast?.("Could not add supplier: " + error.message, "error"); setSupplierOptions((p) => p.filter((s) => s.id !== row.id)); }
  };
  const patchSupplierOption = (id: string, patch: Partial<SupplierOption>) => {
    setSupplierOptions((p) => p.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    void supabase.from("ca_supplier_options").update(patch).eq("id", id);
  };
  const delSupplierOption = (id: string) => {
    setSupplierOptions((p) => p.filter((s) => s.id !== id));
    void removeRow("ca_supplier_options", id);
  };

  // MODULE 9 — THE DESIGN CHOICE: adding or editing a CAPEX item keeps a
  // linked ca_cost_pools row in sync with its calculated monthly
  // depreciation. This is the ENTIRE mechanism by which CAPEX affects real
  // numbers - CostEngine.ts's allocateOverhead() already reads cost pools
  // correctly and is completely unmodified; depreciation simply becomes
  // "another fixed cost" the moment this sync runs.
  const syncCapexToCostPool = async (item: CapexItem, existingPoolId?: string | null) => {
    const dep = calculateDepreciation(item);
    if (existingPoolId) {
      setCostPools((p) => p.map((cp) => (cp.id === existingPoolId ? { ...cp, amount: dep.monthlyDepreciation } : cp)));
      await supabase.from("ca_cost_pools").update({ amount: dep.monthlyDepreciation }).eq("id", existingPoolId);
      return existingPoolId;
    }
    const poolId = uid();
    const pool: CaCostPool = { id: poolId, user_id: userId!, name: "Depreciation \u2014 " + item.name,
      category: "depreciation", amount: dep.monthlyDepreciation, period: "monthly",
      allocation_basis: "revenue", is_avoidable: false } as CaCostPool;
    setCostPools((p) => [...p, pool]);
    await supabase.from("ca_cost_pools").insert(pool);
    return poolId;
  };

  const addCapexItem = async () => {
    if (!userId) return;
    const item: CapexItem & { user_id: string; linked_cost_pool_id?: string } = {
      id: uid(), user_id: userId, name: "New equipment", purchase_cost: 0, salvage_value: 0, useful_life_months: 60,
    };
    const poolId = await syncCapexToCostPool(item);
    const withLink = { ...item, linked_cost_pool_id: poolId };
    setCapexItems((p) => [...p, withLink]);
    await supabase.from("ca_capex_items").insert(withLink);
  };
  const patchCapexItem = async (id: string, patch: Partial<CapexItem>, linkedPoolId?: string | null) => {
    setCapexItems((p) => p.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    await supabase.from("ca_capex_items").update(patch).eq("id", id);
    const updated = { ...capexItems.find((c) => c.id === id)!, ...patch };
    await syncCapexToCostPool(updated, linkedPoolId);
  };
  const delCapexItem = (id: string, linkedPoolId?: string | null) => {
    setCapexItems((p) => p.filter((c) => c.id !== id));
    void removeRow("ca_capex_items", id);
    // The linked fixed cost goes too - the equipment is gone, so is its depreciation.
    if (linkedPoolId) { setCostPools((p) => p.filter((cp) => cp.id !== linkedPoolId)); void removeRow("ca_cost_pools", linkedPoolId); }
  };
  const delCh = (id: string) => { setChannels((p) => p.filter((c) => c.id !== id));
    setOfferingChannels((p) => p.filter((c) => c.channel_id !== id)); void removeRow("ca_channels", id); };
  const delOC = (id: string) => { setOfferingChannels((p) => p.filter((c) => c.id !== id)); void removeRow("ca_offering_channels", id); };

  /* --------------------------------------------------- blueprint application */

  const applyBlueprint = useCallback(async (
    bp: Blueprint,
    skip: { resources: Set<string>; offerings: Set<string>; channels: Set<string>; costPools: Set<string> }
  ) => {
    if (!userId) { toast("Sign in first.", "error"); return false; }
    setSaving(true);
    try {
      const rows = blueprintToRows(bp, userId, uid, skip);

      const nextCtx = { ...(ctx || { id: uid(), user_id: userId }), ...rows.context } as CaBusinessContext;
      const w = async (table: string, data: any[], conflictCol: string = "id") => {
        if (!data.length) return;
        const clean = data.map((r) => { const { effective_cost_per_base_unit, created_at, updated_at, ...rest } = r; return rest; });
        const { error } = await supabase.from(table).upsert(clean, { onConflict: conflictCol });
        if (error) throw error;
      };

      // THE EXACT DATA-LOSS BUG: ca_business_context has its real uniqueness
      // constraint on user_id, not id - one user, one context row. Upserting
      // with onConflict:"id" meant that if the local id ever drifted even
      // slightly from what the database actually held, Postgres rejected the
      // write as a user_id constraint violation, and everything downstream
      // that depended on this row succeeding could be left in an
      // inconsistent state. Fixed to match the table's real constraint.
      // order matters: parents before the rows that reference them
      await w("ca_business_context", [nextCtx], "user_id");
      await w("ca_resources", rows.resources);
      await w("ca_offerings", rows.offerings);
      await w("ca_channels", rows.channels);
      await w("ca_bom_lines", rows.bomLines);
      await w("ca_offering_channels", rows.offeringChannels);
      await w("ca_cost_pools", rows.costPools);

      setCtx(nextCtx);
      setResources((p) => [...p, ...rows.resources]);
      setOfferings((p) => [...p, ...rows.offerings]);
      setChannels((p) => [...p, ...rows.channels]);
      setBomLines((p) => [...p, ...rows.bomLines]);
      setOfferingChannels((p) => [...p, ...rows.offeringChannels]);
      setCostPools((p) => [...p, ...rows.costPools]);

      toast(`Draft model created: ${rows.resources.length} inputs, ${rows.offerings.length} products, ${rows.bomLines.length} recipe lines.`, "success");
      setTab("products");
      return true;
    } catch (e: any) {
      const m = e?.message || "Could not save the draft.";
      setErr(m); toast("Could not apply: " + m, "error");
      return false;
    } finally { setSaving(false); }
  }, [userId, ctx, toast]);

  /* ----------------------------------------------------------- computation */

  const ws: CostWorkspace = useMemo(() => ({
    context: ctx, resources, offerings, bomLines, costPools, channels, offeringChannels, benchmarks,
  }), [ctx, resources, offerings, bomLines, costPools, channels, offeringChannels, benchmarks]);

  const dx: PortfolioDiagnosis = useMemo(() => diagnose(ws), [ws]);
  useEffect(() => { if (onDiagnosis) onDiagnosis(dx); }, [dx, onDiagnosis]);

  // MODULE 1 — COMPLETE: freeze everything currently in the live workspace as
  // a full-fidelity snapshot (spec Section 3: "preserve all components,
  // assumptions, calculations, source information"), then clear the live
  // tables so the next project starts genuinely clean. The snapshot holds the
  // actual rows as they were, not a re-derived summary - so nothing about
  // what made this project's numbers what they were can be lost or drift.
  const completeProject = useCallback(async () => {
    if (!userId || !activeProject) return;
    if (!offerings.length) { toast("Add at least one thing you sell before completing this project.", "warning"); return; }
    setCompleting(true);
    try {
      const { error: snapErr } = await supabase.from("ca_project_snapshots").insert({
        project_id: activeProject.id, user_id: userId,
        business_context: ctx || {}, resources, offerings, bom_lines: bomLines,
        cost_pools: costPools, channels, offering_channels: offeringChannels,
        diagnosis: dx,
      });
      if (snapErr) throw snapErr;

      const { error: projErr } = await supabase.from("ca_projects")
        .update({ status: "complete", completed_at: new Date().toISOString() })
        .eq("id", activeProject.id);
      if (projErr) throw projErr;

      // Clear the live workspace for the next project. Deleting by user_id
      // is safe here specifically BECAUSE everything that mattered about
      // this project was just written into the snapshot above, unchanged.
      await Promise.all([
        supabase.from("ca_resources").delete().eq("user_id", userId),
        supabase.from("ca_offerings").delete().eq("user_id", userId),
        supabase.from("ca_bom_lines").delete().eq("user_id", userId),
        supabase.from("ca_cost_pools").delete().eq("user_id", userId),
        supabase.from("ca_channels").delete().eq("user_id", userId),
        supabase.from("ca_offering_channels").delete().eq("user_id", userId),
      ]);

      const { data: created } = await supabase.from("ca_projects")
        .insert({ user_id: userId, name: "New Cost Model", status: "draft" }).select().single();

      setResources([]); setOfferings([]); setBomLines([]); setCostPools([]);
      setChannels([]); setOfferingChannels([]); setOverrides(new Set());
      setProjects(p => [created as CaProject, { ...activeProject, status: "complete" as const }, ...p.filter(x=>x.id!==activeProject.id)]);
      setActiveProject(created as CaProject);
      setTab("start");
      toast(`"${activeProject.name}" completed and saved to History. Starting a new project.`, "success");
    } catch (e: any) {
      toast("Could not complete the project: " + (e?.message || "unknown error"), "error");
    } finally { setCompleting(false); }
  }, [userId, activeProject, offerings, resources, bomLines, costPools, channels, offeringChannels, ctx, dx, toast]);

  // HISTORICAL INTELLIGENCE REUSE — starting a new project from a past
  // completed one, instead of from a blank AI discovery every time. Reuses
  // ca_project_snapshots exactly as it already exists - no schema change.
  // Every id is regenerated: this is a genuinely NEW project's data, not a
  // pointer back to the old one, so editing it can never retroactively
  // change history you already froze.
  const startFromSnapshot = useCallback(async (snapshot: CaProjectSnapshot) => {
    if (!userId || !activeProject) return;
    if (offerings.length > 0 || resources.length > 0) {
      const ok = window.confirm("This will replace what's currently in your workspace with the past project's data. Continue?");
      if (!ok) return;
    }
    const idMap = new Map<string, string>();
    const remap = (oldId: string) => { if (!idMap.has(oldId)) idMap.set(oldId, uid()); return idMap.get(oldId)!; };

    const newResources = (snapshot.resources || []).map((r: any) => ({ ...r, id: remap(r.id), user_id: userId }));
    const newOfferings = (snapshot.offerings || []).map((o: any) => ({ ...o, id: remap(o.id), user_id: userId }));
    const newBomLines = (snapshot.bom_lines || []).map((b: any) => ({
      ...b, id: uid(), user_id: userId,
      offering_id: idMap.get(b.offering_id) || b.offering_id,
      child_resource_id: b.child_resource_id ? (idMap.get(b.child_resource_id) || b.child_resource_id) : null,
      child_offering_id: b.child_offering_id ? (idMap.get(b.child_offering_id) || b.child_offering_id) : null,
    }));
    const newPools = (snapshot.cost_pools || []).map((p: any) => ({ ...p, id: remap(p.id), user_id: userId }));
    const newChannels = (snapshot.channels || []).map((c: any) => ({ ...c, id: remap(c.id), user_id: userId }));
    const newOfferingChannels = (snapshot.offering_channels || []).map((oc: any) => ({
      ...oc, id: uid(), user_id: userId,
      offering_id: idMap.get(oc.offering_id) || oc.offering_id,
      channel_id: idMap.get(oc.channel_id) || oc.channel_id,
    }));

    try {
      // Clear whatever's currently live, exactly like completing a project does.
      await Promise.all([
        supabase.from("ca_resources").delete().eq("user_id", userId),
        supabase.from("ca_offerings").delete().eq("user_id", userId),
        supabase.from("ca_bom_lines").delete().eq("user_id", userId),
        supabase.from("ca_cost_pools").delete().eq("user_id", userId),
        supabase.from("ca_channels").delete().eq("user_id", userId),
        supabase.from("ca_offering_channels").delete().eq("user_id", userId),
      ]);
      await Promise.all([
        newResources.length ? supabase.from("ca_resources").insert(newResources) : null,
        newOfferings.length ? supabase.from("ca_offerings").insert(newOfferings) : null,
        newBomLines.length ? supabase.from("ca_bom_lines").insert(newBomLines) : null,
        newPools.length ? supabase.from("ca_cost_pools").insert(newPools) : null,
        newChannels.length ? supabase.from("ca_channels").insert(newChannels) : null,
        newOfferingChannels.length ? supabase.from("ca_offering_channels").insert(newOfferingChannels) : null,
      ].filter(Boolean));

      setResources(newResources); setOfferings(newOfferings); setBomLines(newBomLines);
      setCostPools(newPools); setChannels(newChannels); setOfferingChannels(newOfferingChannels);
      toast("Copied into your current project. Every number is editable — nothing here is linked back to the old one.", "success");
    } catch (e: any) {
      toast("Could not copy that project: " + (e?.message || "unknown error"), "error");
    }
  }, [userId, activeProject, offerings.length, resources.length, toast]);

  const loadSnapshots = useCallback(async () => {
    if (!userId) return;
    const { data } = await supabase.from("ca_project_snapshots").select("*")
      .eq("user_id", userId).order("created_at", { ascending: false });
    setSnapshots((data as CaProjectSnapshot[]) ?? []);
  }, [userId]);

  // Loaded proactively on mount now, not only when History is opened - the
  // new "start from a past project" option on Start Here needs this data
  // available immediately, not after a separate click.
  useEffect(() => { void loadSnapshots(); }, [loadSnapshots]);

  // Aggregated once, from data the engine already computes per-offering per-
  // channel (ChannelEconomics.leakagePerUnit) - not a new calculation, a
  // portfolio-level rollup of an existing one, so it can never disagree with
  // what the Channels tab itself shows for any single offering.
  const marketingLeakage = useMemo(() => {
    let totalLeak = 0, totalRev = 0;
    dx.offerings.forEach((o) => {
      o.channelEconomics.forEach((ce) => {
        const vol = o.monthlyVolume * (ce.volumeSharePct / 100);
        totalLeak += ce.leakagePerUnit * vol;
        totalRev += ce.grossPrice * vol;
      });
    });
    return { totalLeak, totalRev, pct: totalRev > 0 ? (totalLeak / totalRev) * 100 : 0 };
  }, [dx]);

  const findings = useMemo(() => validate(ws, overrides), [ws, overrides]);
  const vSummary = useMemo(() => summarise(findings), [findings]);

  /** Findings indexed by entity+field so every input box can flag itself. */
  const flagIndex = useMemo(() => {
    const m = new Map<string, Finding[]>();
    for (const f of findings) {
      if (!f.entityId) continue;
      for (const k of [f.entityId + "::" + f.field, f.entityId + "::*"]) {
        const arr = m.get(k); if (arr) arr.push(f); else m.set(k, [f]);
      }
    }
    return m;
  }, [findings]);

  const flagFor = useCallback(
    (entityId: string, field: string) => flagIndex.get(entityId + "::" + field) || [],
    [flagIndex]);

  /** Per-tab counts so the user knows WHICH tab to go to. */
  const tabFlags = useMemo(() => {
    const c: Record<string, { err: number; warn: number }> = {
      setup: { err: 0, warn: 0 }, inputs: { err: 0, warn: 0 },
      products: { err: 0, warn: 0 }, channels: { err: 0, warn: 0 }, diagnostics: { err: 0, warn: 0 },
    };
    const bucket: Record<string, string> = {
      resource: "inputs", offering: "products", bom: "products",
      channel: "channels", pool: "setup", portfolio: "diagnostics",
    };
    for (const f of findings) {
      const b = bucket[f.scope]; if (!b || f.severity === "info") continue;
      if (f.severity === "error") c[b].err++; else c[b].warn++;
    }
    return c;
  }, [findings]);

  const acceptException = useCallback(async (f: Finding, reason: string) => {
    if (!userId) return;
    setOverrides((prev) => { const n = new Set(prev); n.add(f.id); return n; });
    try {
      const { error } = await supabase.from("ca_validation_overrides").upsert(
        [{ id: uid(), user_id: userId, finding_id: f.id, rule: f.rule, entity_name: f.entityName, reason: reason || null }],
        { onConflict: "user_id,finding_id" });
      if (error) throw error;
      toast("Kept your value. This will not be flagged again.", "success");
    } catch (e: any) { toast("Could not save that decision: " + (e?.message || ""), "error"); }
  }, [userId, toast]);

  const undoException = useCallback(async (findingId: string) => {
    if (!userId) return;
    setOverrides((prev) => { const n = new Set(prev); n.delete(findingId); return n; });
    try { await supabase.from("ca_validation_overrides").delete().eq("user_id", userId).eq("finding_id", findingId); }
    catch { /* the UI already reflects it */ }
  }, [userId]);

  const cur = ctx?.currency || "INR";
  const M = (v: number) => fmtMoney(v, cur);

  /* --------------------------------------------------------------- render */

  if (loading) return <div style={{ ...S.wrap, ...S.empty }}>Loading your cost model...</div>;
  if (err && !userId) return <div style={{ ...S.wrap, ...S.empty }}>{err}</div>;

  // THE FIX FOR "I don't know what Setup/Buy/Sell/Diagnostics means" - a
  // one-line, plain-language explanation for every tab, shown right under
  // the tab bar for whichever one is currently open. Nobody should have to
  // guess what a section is for.
  // TILE LAUNCHER — the answer to "every module should show as a tile".
  // Each entry now also carries an icon and a short "what lives here"
  // description, both for the new tile grid AND reused by the existing
  // compact tab bar underneath it - one source of truth, zero duplicated
  // labels that could drift apart from each other.
  // THE FIX: every description below now says what you will ACHIEVE by
  // filling it in, not just what data lives there - in plain words, no
  // accounting terms without an explanation attached.
  const TABS: Array<{ k: TabKey; label: string; count?: number; hint: string; icon: string; tileNote: string }> = [
    { k: "start",       label: "Start here", icon: "\u2728", tileNote: "Tell us what you're making or selling - we'll find the real costs for you",
      hint: "Just describe your business in plain words. We'll research real prices and build your whole cost picture automatically - you don't need to know any accounting terms to start." },
    { k: "setup",       label: "Setup", icon: "\u2699\uFE0F", tileNote: "A few basics so every number elsewhere is calculated correctly for YOU",
      hint: "Your industry, location, and currency. Getting this right means every number on every other tile is calculated for your actual business, not a generic guess." },
    { k: "inputs",      label: "What you buy",  count: resources.length, icon: "\uD83D\uDCE6", tileNote: "See the REAL cost of everything you pay for - after waste, after fees",
      hint: "Everything you spend money on - materials, people's time, rent, software. You'll see the true cost of each one after accounting for waste and fees, not just the price tag." },
    { k: "products",    label: "What you sell", count: offerings.length, icon: "\uD83C\uDFF7\uFE0F", tileNote: "Find out what price to charge to actually make money",
      hint: "For each thing you sell: what it truly costs you, what price you should charge to make a profit, and how many you need to sell before you stop losing money." },
    { k: "channels",    label: "Where you sell", count: channels.length, icon: "\uD83D\uDED2", tileNote: "See how much of each sale you actually keep, after fees",
      hint: "Every place you sell through - your own shop, a marketplace, a reseller. Each one takes a cut - this shows you exactly how much reaches your pocket after they take theirs." },
    { k: "diagnostics", label: "Diagnostics", icon: "\uD83E\uDE7A", tileNote: "The bottom line: is your business actually going to make money?",
      hint: "One clear verdict on your whole business - are you profitable, where's the biggest risk, and what's the single most important thing to fix first." },
  ];
  const tabBadge = (k: TabKey) => {
    const t = (tabFlags as any)[k]; if (!t) return null;
    if (t.err) return <span style={{ marginLeft: 5, background: BAD.bg, color: BAD.fg, borderRadius: 9, padding: "1px 6px", fontSize: 9.5, fontWeight: 800 }}>{t.err}</span>;
    if (t.warn) return <span style={{ marginLeft: 5, background: WARN.bg, color: WARN.fg, borderRadius: 9, padding: "1px 6px", fontSize: 9.5, fontWeight: 800 }}>{t.warn}</span>;
    return null;
  };

  return (
    <div style={S.wrap}>
      {showSOP && <SOPDrawer onClose={() => setShowSOP(false)} />}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1 style={S.h1}>Cost Architect</h1>
            {/* THE SOP BUTTON, top of the module, exactly where asked. */}
            <button onClick={() => setShowSOP(true)}
              style={{ ...S.btnGhost, fontSize: 10.5, padding: "5px 11px", display: "flex", alignItems: "center", gap: 5 }}>
              {"\uD83D\uDCD6"} How to use this
            </button>
          </div>
          <div style={S.sub}>
            {companyName ? companyName + " \u00B7 " : ""}Universal Cost, Management Accounting &amp; Economic Intelligence Engine.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* THE VISIBLE PROMISE: information is never wiped silently. Either
              this says "Saved" and it genuinely is, or it says "Unsaved
              changes" and tells you exactly that - never nothing. */}
          {saving && <span style={{ fontSize: 10, color: V("muted", "#8b98a5") }}>Saving\u2026</span>}
          {!saving && hasUnsaved && (
            <span style={{ fontSize: 10, color: WARN.fg, display: "flex", alignItems: "center", gap: 6 }}>
              \u25CF Unsaved changes
              <button onClick={() => void flush()} style={{ ...S.btnGhost, fontSize: 9.5, padding: "3px 8px" }}>Save now</button>
            </span>
          )}
          {!saving && !hasUnsaved && (
            <span style={{ fontSize: 10, color: OK.fg }}>\u2713 Saved</span>
          )}
          <Chip tone={dx.confidenceScore >= 70 ? OK : dx.confidenceScore >= 40 ? WARN : BAD}>
            {dx.confidenceScore}% confidence
          </Chip>
        </div>
      </div>

      {/* MODULE 1 — the project lifecycle, made visible. Section 3 of the spec:
          a Cost Project is a discrete, nameable thing with a status, not an
          invisible ongoing workspace. */}
      {activeProject && (
        <div style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 12px", marginTop:10,
          background: V("panel2","#12161f"), border:"1px solid "+V("border","#232838"), borderRadius:8, flexWrap:"wrap" }}>
          <span style={{ fontSize:9, fontWeight:800, letterSpacing:0.4, color: V("muted","#8b98a5"), textTransform:"uppercase" }}>Project</span>
          <input value={activeProject.name}
            onChange={e=>setActiveProject(p=>p?{...p,name:e.target.value}:p)}
            onBlur={e=>{ void supabase.from("ca_projects").update({name:e.target.value,updated_at:new Date().toISOString()}).eq("id",activeProject.id); }}
            style={{ fontSize:12, fontWeight:700, background:"transparent", border:"none", color: V("text","#e8ecf1"), outline:"none", minWidth:140 }} />
          <span style={{ fontSize:9.5, color: V("muted","#8b98a5"), textTransform:"capitalize" }}>{activeProject.status}</span>
          <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
            <button onClick={()=>{ setTab("history"); void loadSnapshots(); }}
              style={{ ...S.btnGhost, fontSize:10.5, padding:"5px 10px" }}>
              History{projects.filter(p=>p.status==="complete").length>0?` (${projects.filter(p=>p.status==="complete").length})`:""}
            </button>
            <button onClick={completeProject} disabled={completing || !offerings.length}
              title={!offerings.length?"Add at least one thing you sell first":"Freeze this project into History and start a new one"}
              style={{ ...S.btnGhost, fontSize:10.5, padding:"5px 10px", opacity:(completing||!offerings.length)?0.5:1 }}>
              {completing?"Completing\u2026":"Complete project"}
            </button>
          </div>
        </div>
      )}

      {/* THE TILE LAUNCHER. Every module as its own clickable card, with an
          icon and a one-line description - directly answering "every topic
          should show as a tile". This is a PURE presentation layer: clicking
          a tile calls the exact same setTab() used by the compact bar below
          it, so every existing calculation, save, and data flow underneath
          is completely untouched - only how you GET to a module changed. */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))", gap:10, marginBottom:16 }}>
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            style={{ textAlign:"left", padding:"12px 14px", borderRadius:10, cursor:"pointer",
              background: tab===t.k ? "rgba(20,184,166,0.10)" : "rgba(255,255,255,0.02)",
              border: "1px solid " + (tab===t.k ? "#14B8A6" : V("border","#232838")),
              display:"flex", flexDirection:"column", gap:6 }}>
            <div style={{ display:"flex", alignItems:"center", gap:7 }}>
              <span style={{ fontSize:18 }}>{t.icon}</span>
              <span style={{ fontSize:12.5, fontWeight:800, color: tab===t.k ? "#14B8A6" : V("ink","#e6edf3") }}>
                {t.label}{t.count != null && t.count > 0 ? ` (${t.count})` : ""}
              </span>
              {tabBadge(t.k)}
            </div>
            <div style={{ fontSize:9.5, color: V("muted","#8b98a5"), lineHeight:1.4 }}>{t.tileNote}</div>
          </button>
        ))}
        <button onClick={() => { setTab("history"); void loadSnapshots(); }}
          style={{ textAlign:"left", padding:"12px 14px", borderRadius:10, cursor:"pointer",
            background: tab==="history" ? "rgba(20,184,166,0.10)" : "rgba(255,255,255,0.02)",
            border: "1px solid " + (tab==="history" ? "#14B8A6" : V("border","#232838")),
            display:"flex", flexDirection:"column", gap:6 }}>
          <div style={{ display:"flex", alignItems:"center", gap:7 }}>
            <span style={{ fontSize:18 }}>{"\uD83D\uDCDA"}</span>
            <span style={{ fontSize:12.5, fontWeight:800, color: tab==="history" ? "#14B8A6" : V("ink","#e6edf3") }}>
              History{projects.filter(p=>p.status==="complete").length>0?` (${projects.filter(p=>p.status==="complete").length})`:""}
            </span>
          </div>
          <div style={{ fontSize:9.5, color: V("muted","#8b98a5"), lineHeight:1.4 }}>Past completed projects, saved in full</div>
        </button>
      </div>

      {/* THE OLD COMPACT TAB BAR AND ITS DUPLICATE DESCRIPTION LINE WERE
          REMOVED HERE. Your reference document is explicit: "no excessive
          tabs, no confusing navigation hierarchy." Now that every tile opens
          a drawer with its own name and description already shown at the
          top, keeping a second, flatter row of the identical buttons right
          below the tiles was exactly the clutter that principle rules out -
          not a different feature, a second way to trigger the same one. */}

      {err && userId && (
        <div style={{ ...S.card, borderColor: BAD.fg, background: BAD.bg, marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: BAD.fg }}>{err}</div>
          <button style={{ ...S.btnGhost, marginTop: 8 }} onClick={() => setErr(null)}>Dismiss</button>
        </div>
      )}

      <DataQualityBar summary={vSummary} findings={findings} overrideCount={overrides.size} goTo={setTab}
        onUndoAll={() => { overrides.forEach((id) => { void undoException(id); }); }} />

      {/* THE EXACT COMPLAINT: "what is fixed cost, what is variable cost,
          where does the marketing cost sit" - none of that was ever clearly
          called out, even though the engine was already calculating all of
          it. This surfaces figures that already exist in dx (diagnose()) -
          no new calculation logic, purely making what was already computed
          impossible to miss. Shown once real data exists, not on the
          empty-state screen where it would just be zeros. */}
      {offerings.length > 0 && (
        <div style={{ ...S.card, marginBottom: 12 }}>
          <div style={{ fontSize:10, fontWeight:800, letterSpacing:0.4, color: V("muted","#8b98a5"), textTransform:"uppercase", marginBottom:10 }}>
            Cost breakdown at a glance
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:14 }}>
            <div>
              <div style={{ fontSize:9, color: V("muted","#8b98a5"), textTransform:"uppercase" }}>Fixed cost / month</div>
              <div style={{ fontSize:17, fontWeight:800 }}>{M(dx.monthlyFixedCost)}</div>
              <div style={{ fontSize:9, color: V("muted","#8b98a5") }}>Rent, salaries, overhead \u2014 does not change with volume</div>
            </div>
            <div>
              <div style={{ fontSize:9, color: V("muted","#8b98a5"), textTransform:"uppercase" }}>Variable cost / month</div>
              <div style={{ fontSize:17, fontWeight:800 }}>{M(dx.monthlyVariableCost)}</div>
              <div style={{ fontSize:9, color: V("muted","#8b98a5") }}>Materials, direct labour \u2014 scales with what you sell</div>
            </div>
            <div>
              <div style={{ fontSize:9, color: V("muted","#8b98a5"), textTransform:"uppercase" }}>Marketing &amp; channel leakage</div>
              <div style={{ fontSize:17, fontWeight:800, color: marketingLeakage.pct > 15 ? BAD.fg : undefined }}>
                {M(marketingLeakage.totalLeak)}{marketingLeakage.totalRev > 0 ? ` (${marketingLeakage.pct.toFixed(1)}% of revenue)` : ""}
              </div>
              <div style={{ fontSize:9, color: V("muted","#8b98a5") }}>Commission, discounts, ad spend, gateway fees \u2014 per your channels</div>
            </div>
            <div>
              <div style={{ fontSize:9, color: V("muted","#8b98a5"), textTransform:"uppercase" }}>Contribution margin</div>
              <div style={{ fontSize:17, fontWeight:800 }}>{dx.contributionMarginPct.toFixed(1)}%</div>
              <div style={{ fontSize:9, color: V("muted","#8b98a5") }}>What's left of each rupee of sales after variable costs</div>
            </div>
            <div>
              <div style={{ fontSize:9, color: V("muted","#8b98a5"), textTransform:"uppercase" }}>Break-even revenue / month</div>
              <div style={{ fontSize:17, fontWeight:800 }}>{M(dx.breakevenRevenue)}</div>
              <div style={{ fontSize:9, color: V("muted","#8b98a5") }}>What you must sell just to cover fixed cost</div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          THE COST ARCHITECT HOME — ALWAYS VISIBLE, PER YOUR REFERENCE DOC
          PART 4 & 53: "the main page should immediately communicate I am
          building ONE economic model, not navigating 20 unrelated
          calculators." AI discovery and the full tile grid never disappear
          behind a tab anymore - this IS the workspace, permanently. */}
      <StartTab callAI={callAI} ctx={ctx} applyBlueprint={applyBlueprint}
        hasData={offerings.length > 0} goTo={setTab} companyName={companyName} openaiKey={openaiKey} showToast={showToast}
        resources={resources} delRes={delRes} costPools={costPools} delPool={delPool}
        channels={channels} delCh={delCh} offerings={offerings}
        completedProjects={projects.filter(p => p.status === "complete")} snapshots={snapshots} startFromSnapshot={startFromSnapshot} delProject={delProject} resetWorkspace={resetWorkspace} />
      <ExploreTheModel goTo={setTab} showToast={showToast} />

      {/* ═══════════════════════════════════════════════════════════════
          THE DRAWER — PART 54 OF YOUR REFERENCE DOCUMENT, IMPLEMENTED
          EXACTLY: "Clicking a tile should open that module in a focused
          workspace. Prefer a drawer... rather than unnecessarily
          navigating away from the main Cost Architect model. When the
          user returns, the tile should immediately show updated
          status/results."
          THIS FIXES BOTH NAMED COMPLAINTS AT ONCE: a tile no longer swaps
          the whole page to "the same old thing" - it opens a visually
          distinct panel ON TOP of the home screen, and there is now an
          unmissable "Back to Cost Architect" action at its top, always in
          the same place, every single time. */}
      {tab !== "start" && (
        <div style={{ position:"fixed", inset:0, zIndex:300, background:"rgba(5,7,12,0.72)",
          display:"flex", justifyContent:"flex-end" }} onClick={() => setTab("start")}>
          <div style={{ width:"min(880px, 94vw)", height:"100%", background: V("bg","#0d1117"),
            borderLeft:"1px solid "+V("border","#232838"), overflowY:"auto", padding:"18px 22px" }}
            onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setTab("start")}
              style={{ display:"flex", alignItems:"center", gap:6, background:"none", border:"none",
                cursor:"pointer", color:"#14B8A6", fontSize:12.5, fontWeight:800, marginBottom:16, padding:0 }}>
              {"\u2190"} Back to Cost Architect
            </button>
            <div style={{ fontSize:15, fontWeight:800, marginBottom:4 }}>
              {TABS.find(t=>t.k===tab)?.label || (tab==="history" ? "History" : "")}
            </div>
            <div style={{ fontSize:10.5, color: V("muted","#8b98a5"), marginBottom:16, lineHeight:1.5 }}>
              {TABS.find(t=>t.k===tab)?.hint}
            </div>

            {tab === "setup" && (
              <SetupTab ctx={ctx} patchCtx={patchCtx} costPools={costPools} patchPool={patchPool}
                addPool={addPool} delPool={delPool} M={M} flagFor={flagFor} onAccept={acceptException} openaiKey={openaiKey}
                capexItems={capexItems} addCapexItem={addCapexItem} patchCapexItem={patchCapexItem} delCapexItem={delCapexItem} cur={cur}
                monthlyRevenue={dx.monthlyRevenue} monthlyCogs={dx.monthlyVariableCost} />
            )}

            {tab === "inputs" && (
              <InputsTab resources={resources} patchRes={patchRes} addResource={addResource} delRes={delRes} cur={cur}
                flagFor={flagFor} onAccept={acceptException}
                supplierOptions={supplierOptions} addSupplierOption={addSupplierOption}
                patchSupplierOption={patchSupplierOption} delSupplierOption={delSupplierOption} />
            )}

            {tab === "products" && (
              <ProductsTab offerings={offerings} resources={resources} bomLines={bomLines} channels={channels}
                offeringChannels={offeringChannels} dx={dx} costPools={costPools} openOffering={openOffering} setOpenOffering={setOpenOffering}
                patchOff={patchOff} patchBom={patchBom} patchOC={patchOC} addOffering={addOffering}
                addBomLine={addBomLine} linkChannel={linkChannel} delOff={delOff} delBom={delBom} delOC={delOC} M={M}
                flagFor={flagFor} onAccept={acceptException} />
            )}

            {tab === "channels" && (
              <ChannelsTab channels={channels} patchCh={patchCh} addChannel={addChannel} delCh={delCh} cur={cur}
                flagFor={flagFor} onAccept={acceptException} />
            )}

            {tab === "diagnostics" && (
              <>
                <DiagnosticsTab dx={dx} M={M} goTo={setTab} />
                {/* COST WATERFALL — a visual read of exactly the same numbers
                    DiagnosticsTab already shows as text above, just easier
                    to grasp at a glance. Pure display: reads dx, writes
                    nothing, cannot disagree with the numbers beside it. */}
                <CostWaterfallChart dx={dx} M={M} />
                {/* MODULE 17 — TIME VALUE OF MONEY, self-contained: manages
                    its own load/save so nothing about DiagnosticsTab itself
                    needs to change to add this. */}
                <InvestmentAnalysisPanel userId={userId} cur={cur} showToast={showToast} />
                {/* MODULE 18 — SCENARIOS & WHAT-IF, also self-contained. */}
                <ScenarioPanel userId={userId} cur={cur} dx={dx} showToast={showToast} />
                {/* MODULE 20 — REPORTS & EXPORT. */}
                <ExecutiveSummaryPanel dx={dx} M={M} companyName={companyName} ctx={ctx} />
              </>
            )}

            {tab === "history" && (
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {!snapshots.length && (
                  <div style={S.card}>
                    <div style={{ fontSize:12, color: V("muted","#8b98a5") }}>
                      No completed projects yet. When you press "Complete project", everything currently in your
                      workspace is frozen here exactly as it was — components, assumptions, calculations and all.
                    </div>
                  </div>
                )}
                {snapshots.map(s => {
                  const p = projects.find(pr => pr.id === s.project_id);
                  return (
                    <div key={s.id} style={S.card}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                        <div>
                          <div style={{ fontSize:13, fontWeight:700 }}>{p?.name || "Completed project"}</div>
                          <div style={{ fontSize:10, color: V("muted","#8b98a5") }}>
                            Completed {new Date(s.created_at).toLocaleDateString()} \u00B7 {s.offerings.length} thing{s.offerings.length===1?"":"s"} sold \u00B7 {s.resources.length} input{s.resources.length===1?"":"s"}
                          </div>
                        </div>
                        <button style={S.btnGhost} onClick={()=>setViewingSnapshot(viewingSnapshot?.id===s.id?null:s)}>
                          {viewingSnapshot?.id===s.id ? "Close" : "View"}
                        </button>
                      </div>
                      {viewingSnapshot?.id===s.id && s.diagnosis && (
                        <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid "+V("border","#232838"),
                          display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:10 }}>
                          <div><div style={{fontSize:9,color:V("muted","#8b98a5"),textTransform:"uppercase"}}>Monthly Revenue</div>
                            <div style={{fontSize:14,fontWeight:700}}>{M(s.diagnosis.monthlyRevenue)}</div></div>
                          <div><div style={{fontSize:9,color:V("muted","#8b98a5"),textTransform:"uppercase"}}>Fixed Cost</div>
                            <div style={{fontSize:14,fontWeight:700}}>{M(s.diagnosis.monthlyFixedCost)}</div></div>
                          <div><div style={{fontSize:9,color:V("muted","#8b98a5"),textTransform:"uppercase"}}>Variable Cost</div>
                            <div style={{fontSize:14,fontWeight:700}}>{M(s.diagnosis.monthlyVariableCost)}</div></div>
                          <div><div style={{fontSize:9,color:V("muted","#8b98a5"),textTransform:"uppercase"}}>Contribution Margin</div>
                            <div style={{fontSize:14,fontWeight:700}}>{s.diagnosis.contributionMarginPct?.toFixed(1)}%</div></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================================
 * TAB :: SETUP
 * ========================================================================== */

const SetupTab: React.FC<{
  ctx: CaBusinessContext | null; patchCtx: (p: Partial<CaBusinessContext>) => void;
  costPools: CaCostPool[]; patchPool: (id: string, p: Partial<CaCostPool>) => void;
  addPool: () => void; delPool: (id: string) => void; M: (v: number) => string;
  flagFor: FlagLookup; onAccept: AcceptFn; openaiKey?: string;
  capexItems: Array<CapexItem & { linked_cost_pool_id?: string | null }>;
  addCapexItem: () => void;
  patchCapexItem: (id: string, p: Partial<CapexItem>, linkedPoolId?: string | null) => void;
  delCapexItem: (id: string, linkedPoolId?: string | null) => void;
  cur: string;
  monthlyRevenue: number; monthlyCogs: number;   // MODULE 10
}> = ({ ctx, patchCtx, costPools, patchPool, addPool, delPool, M, flagFor, onAccept, openaiKey,
        capexItems, addCapexItem, patchCapexItem, delCapexItem, cur, monthlyRevenue, monthlyCogs }) => {
  const monthlyFixed = costPools.reduce((s, p) => {
    const a = num(p.amount);
    return s + (p.period === "annual" ? a / 12 : p.period === "quarterly" ? a / 3 : a);
  }, 0);

  return (
    <>
      <div style={S.card}>
        <div style={S.cardH}>Your business</div>
        <div style={S.note}>
          The industry you pick decides which benchmarks you get compared against. Everything else on this
          screen works the same way whatever you make or sell.
        </div>
        <div style={{ ...S.grid2, marginTop: 12 }}>
          <div>
            <label style={S.lbl}>Industry</label>
            <SelectCell value={ctx?.business_archetype ?? ""} onChange={(v) => patchCtx({ business_archetype: v })}
              options={[{ v: "", label: "Select your industry..." }, ...ARCHETYPES]} />
          </div>
          <div>
            <label style={S.lbl}>Currency</label>
            <SelectCell value={ctx?.currency ?? "INR"} onChange={(v) => patchCtx({ currency: v })}
              options={[{ v: "INR", label: "INR \u20B9" }, { v: "USD", label: "USD $" }, { v: "EUR", label: "EUR \u20AC" }, { v: "GBP", label: "GBP \u00A3" }, { v: "AED", label: "AED" }]} />
          </div>
          <div>
            <label style={S.lbl}>Your bottleneck</label>
            <div style={{ display:"flex", gap:6, alignItems:"center" }}>
              <TextCell value={ctx?.constraint_resource_label ?? ""} onChange={(v) => patchCtx({ constraint_resource_label: v })}
                placeholder="Oven hours, machine hours, senior staff time..." />
              <VoiceField value={ctx?.constraint_resource_label ?? ""} onChange={(v) => patchCtx({ constraint_resource_label: v })}
                compact allowReadAloud={false} openaiKey={openaiKey} />
            </div>
          </div>
          <div>
            <label style={S.lbl}>Bottleneck capacity per month</label>
            <NumCell value={ctx?.constraint_capacity_per_period ?? null}
              onChange={(v) => patchCtx({ constraint_capacity_per_period: v })} suffix="hrs" />
          </div>
        </div>
        <div style={{ ...S.note, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${V("faint", "#16202c")}` }}>
          <strong style={{ color: V("ink", "#e6edf3") }}>Why the bottleneck matters:</strong> when one resource limits
          how much you can produce, the product with the best margin percentage is often the wrong one to push.
          What counts is profit per hour of that bottleneck. Naming it here lets the diagnostics work that out.
        </div>
      </div>

      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ ...S.cardH, marginBottom: 0 }}>Fixed monthly costs</div>
          <div style={{ fontSize: 12, fontWeight: 700 }}>{M(monthlyFixed)}/mo</div>
        </div>
        <div style={{ ...S.note, marginBottom: 10 }}>
          Bills you pay whether you sell one unit or a thousand - rent, salaries, software, insurance.
          These do not belong in a recipe. They get spread across products here instead.
        </div>
        {costPools.length === 0 ? (
          <Empty title="No fixed costs yet"
            body="Add rent, salaries and subscriptions so breakeven and true profit can be calculated."
            action={<button style={S.btn} onClick={addPool}>Add a fixed cost</button>} />
        ) : (
          <div style={S.scroll}>
            <table style={S.table}>
              <thead><tr>
                <th style={S.th}>What it is</th><th style={{ ...S.th, width: 120 }}>Amount</th>
                <th style={{ ...S.th, width: 110 }}>Per</th><th style={{ ...S.th, width: 170 }}>Spread across products</th>
                <th style={{ ...S.th, width: 90 }}>Can be cut?</th><th style={{ ...S.th, width: 36 }}></th>
              </tr></thead>
              <tbody>
                {costPools.map((p) => (
                  <tr key={p.id}>
                    <td style={S.td}><Flag findings={flagFor(p.id, "amount")} onAccept={onAccept}><TextCell value={p.name} onChange={(v) => patchPool(p.id, { name: v })} placeholder="Shop rent" /></Flag></td>
                    <td style={S.td}><NumCell value={p.amount} onChange={(v) => patchPool(p.id, { amount: v })} /></td>
                    <td style={S.td}><SelectCell value={p.period ?? "monthly"} onChange={(v) => patchPool(p.id, { period: v })}
                      options={[{ v: "monthly", label: "Month" }, { v: "quarterly", label: "Quarter" }, { v: "annual", label: "Year" }]} /></td>
                    <td style={S.td}><SelectCell value={p.allocation_basis ?? "revenue"} onChange={(v) => patchPool(p.id, { allocation_basis: v as any })}
                      options={ALLOCATION_BASES} /></td>
                    <td style={{ ...S.td, textAlign: "center" }}>
                      <input type="checkbox" checked={!!p.is_avoidable} onChange={(e) => patchPool(p.id, { is_avoidable: e.target.checked })} />
                    </td>
                    <td style={S.td}><button style={S.btnDel} onClick={() => delPool(p.id)} title="Remove">&times;</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button style={{ ...S.btnGhost, marginTop: 10 }} onClick={addPool}>+ Add fixed cost</button>
          </div>
        )}

        {/* MODULE 09 — CAPEX & DEPRECIATION. A big one-time purchase spread
            fairly over the years you'll use it, added automatically to your
            fixed costs above the moment it's created - open "Fixed costs"
            and you'll see a matching "Depreciation \u2014 [item name]" row
            appear, kept in sync every time this changes. */}
        <div style={{ ...S.card, marginTop: 14 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 4 }}>Big one-time purchases (CAPEX)</div>
          <div style={S.note}>
            A machine, a vehicle, a big renovation - anything expensive that will keep helping your business for
            years, not just this month. We spread its cost evenly across how long you'll use it, so one big
            purchase doesn't wrongly make one month look terrible and every other month look artificially better.
          </div>
          {capexItems.map((item) => {
            const dep = calculateDepreciation(item);
            return (
              <div key={item.id} style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed " + V("border", "#232838") }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
                  <div style={{ flex: "1 1 160px" }}><label style={S.lbl}>What is it?</label>
                    <TextCell value={item.name} onChange={(v) => patchCapexItem(item.id, { name: v }, item.linked_cost_pool_id)} /></div>
                  <div><label style={S.lbl}>Purchase cost</label>
                    <NumCell value={item.purchase_cost} onChange={(v) => patchCapexItem(item.id, { purchase_cost: v }, item.linked_cost_pool_id)} /></div>
                  <div><label style={S.lbl}>Worth at the end (optional)</label>
                    <NumCell value={item.salvage_value} onChange={(v) => patchCapexItem(item.id, { salvage_value: v }, item.linked_cost_pool_id)} /></div>
                  <div><label style={S.lbl}>Years you'll use it</label>
                    <NumCell value={item.useful_life_months ? item.useful_life_months / 12 : null}
                      onChange={(v) => patchCapexItem(item.id, { useful_life_months: (v || 0) * 12 }, item.linked_cost_pool_id)} /></div>
                  <button onClick={() => delCapexItem(item.id, item.linked_cost_pool_id)} style={{ background: "none", border: "none", color: BAD.fg, cursor: "pointer", fontSize: 13 }}>{"\u2715"}</button>
                </div>
                <div style={{ fontSize: 10.5, marginTop: 6 }}>
                  Spread over {(item.useful_life_months / 12).toFixed(1)} years, this adds
                  {" "}<strong>{fmtMoney(dep.monthlyDepreciation, cur)}</strong> to your fixed costs every month.
                  {item.purchase_date && !dep.isFullyDepreciated && (
                    <span style={{ color: V("muted", "#8b98a5") }}> Currently worth about {fmtMoney(dep.currentBookValue, cur)} on paper, with {dep.monthsRemaining} months of useful life left.</span>
                  )}
                </div>
              </div>
            );
          })}
          <button style={{ ...S.btnGhost, marginTop: 10 }} onClick={addCapexItem}>+ Add a big purchase</button>
        </div>

        {/* MODULE 10 — WORKING CAPITAL. Deliberately asks for days, not
            rupee values nobody tracks precisely - then converts to a real
            cash figure using cost data this platform already has. */}
        <WorkingCapitalPanel ctx={ctx} patchCtx={patchCtx} monthlyRevenue={monthlyRevenue} monthlyCogs={monthlyCogs} cur={cur} />
      </div>
    </>
  );
};

/* ============================================================================
 * MODULE 10 — WORKING CAPITAL PANEL.
 * ========================================================================== */
const WorkingCapitalPanel: React.FC<{
  ctx: CaBusinessContext | null; patchCtx: (p: Partial<CaBusinessContext>) => void;
  monthlyRevenue: number; monthlyCogs: number; cur: string;
}> = ({ ctx, patchCtx, monthlyRevenue, monthlyCogs, cur }) => {
  const result = useMemo(() => calculateWorkingCapital({
    inventoryDays: num(ctx?.inventory_days), receivablesDays: num(ctx?.receivables_days),
    payablesDays: num(ctx?.payables_days), monthlyRevenue, monthlyCogs,
  }), [ctx, monthlyRevenue, monthlyCogs]);

  const M = (v: number) => fmtMoney(v, cur);
  const hasInputs = ctx?.inventory_days != null || ctx?.receivables_days != null || ctx?.payables_days != null;

  return (
    <div style={{ ...S.card, marginTop: 14 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 4 }}>Cash tied up in running the business</div>
      <div style={S.note}>
        Even a profitable business can run out of cash if money is stuck in unsold stock or unpaid invoices.
        This tells you how much cash you need sitting in the bank just to keep operating — separate from profit.
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
        <div><label style={S.lbl}>Days of stock you usually hold</label>
          <NumCell value={ctx?.inventory_days} onChange={(v) => patchCtx({ inventory_days: v })} suffix="days" /></div>
        <div><label style={S.lbl}>Days customers take to pay you</label>
          <NumCell value={ctx?.receivables_days} onChange={(v) => patchCtx({ receivables_days: v })} suffix="days" /></div>
        <div><label style={S.lbl}>Days you take to pay suppliers</label>
          <NumCell value={ctx?.payables_days} onChange={(v) => patchCtx({ payables_days: v })} suffix="days" /></div>
      </div>
      {hasInputs && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px dashed " + V("border", "#232838") }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 }}>
            <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Cash conversion cycle</div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>{result.cashConversionCycleDays.toFixed(0)} days</div></div>
            <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Cash you need on hand</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: result.interpretation === "concerning" ? BAD.fg : result.interpretation === "tight" ? WARN.fg : OK.fg }}>{M(result.workingCapitalRequired)}</div></div>
          </div>
          <div style={{ fontSize: 9.5, color: V("muted", "#8b98a5"), marginTop: 8, lineHeight: 1.6 }}>
            {result.interpretation === "healthy" && "This is a healthy cycle \u2014 your suppliers are effectively helping fund your operations."}
            {result.interpretation === "tight" && "This is on the tighter side \u2014 worth keeping an eye on, especially before taking on a big new order."}
            {result.interpretation === "concerning" && "This ties up a lot of cash for a long time \u2014 worth looking at whether stock, collections, or supplier terms can improve."}
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================================================================
 * TAB :: INPUTS
 * ========================================================================== */

/* ============================================================================
 * MODULE 5 — WORKFORCE & FTE PANEL.
 * Shown inside a LABOUR resource's detail row. Builds capacity from named,
 * auditable assumptions (spec Section 12), shows the distinct rate tiers
 * (Section 13), and includes a live Activity Cost calculator matching the
 * spec's own worked test case exactly: N participants x minutes each = hours,
 * costed at this resource's rate (Section 79).
 * ========================================================================== */
const WorkforcePanel: React.FC<{
  resource: CaResource; patchRes: (id: string, p: Partial<CaResource>) => void; cur: string;
}> = ({ resource: r, patchRes, cur }) => {
  const capacity = useMemo(() => computeCapacity(r), [r]);
  const rates = useMemo(() => computeRates(r, capacity), [r, capacity]);
  const [actVolume, setActVolume] = useState<number | null>(100);
  const [actMinutes, setActMinutes] = useState<number | null>(6);
  const [actTier, setActTier] = useState<"effective" | "billable" | "salary">("effective");
  const activity = useMemo(() => costActivity(actVolume || 0, actMinutes || 0, r, rates, actTier), [actVolume, actMinutes, r, rates, actTier]);

  const field = (label: string, key: keyof CaResource, suffix?: string, placeholder?: string) => (
    <div><label style={S.lbl}>{label}</label>
      <NumCell value={r[key] as any} onChange={(v) => patchRes(r.id, { [key]: v } as any)} suffix={suffix} />
    </div>
  );

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px dashed " + V("border", "#232838") }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.4, color: V("muted", "#8b98a5"), textTransform: "uppercase", marginBottom: 9 }}>
        Capacity &amp; rates — Module 5
      </div>
      <div style={S.grid2}>
        {field("Scheduled hours / day", "sched_hours_per_day", "h")}
        {field("Working days / year", "working_days_per_year", "d")}
        {field("Paid holidays / year", "paid_holidays_per_year", "d")}
        {field("Annual leave / year", "annual_leave_days", "d")}
        {field("Sick leave / year", "sick_leave_days", "d")}
        {field("Training days / year", "training_days_per_year", "d")}
        {field("Meetings + admin loss", "meetings_admin_pct", "%")}
        {field("Utilization (demand)", "utilization_pct", "%")}
        {field("Billability", "billability_pct", "%")}
        {field("Employer add-on (PF, insurance...)", "fully_loaded_addon_pct", "%")}
        {field("Client billing rate (optional)", "client_billing_rate", "/hr")}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginTop: 12,
        padding: 10, background: V("bg", "#070c18"), borderRadius: 6 }}>
        <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Gross hours / year</div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{capacity.grossHoursPerYear.toFixed(0)}</div></div>
        <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Productive hours</div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{capacity.productiveHours.toFixed(0)}</div></div>
        <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Billable hours</div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{capacity.billableHours.toFixed(0)}</div></div>
        <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Effective hourly cost</div>
          <div style={{ fontSize: 14, fontWeight: 800 }}>{fmtMoney(rates.effectiveHourlyCost, cur)}</div></div>
        <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Billable cost rate</div>
          <div style={{ fontSize: 14, fontWeight: 800 }}>{fmtMoney(rates.billableCostRate, cur)}</div></div>
        {rates.clientBillingRate != null && (
          <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Margin on labour</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: rates.clientBillingRate > rates.billableCostRate ? OK.fg : BAD.fg }}>
              {rates.billableCostRate > 0 ? (((rates.clientBillingRate / rates.billableCostRate) - 1) * 100).toFixed(0) : "0"}%
            </div></div>
        )}
      </div>

      <div style={{ fontSize: 9.5, color: V("muted", "#8b98a5"), marginTop: 8, lineHeight: 1.6 }}>
        {capacity.grossHoursPerYear.toFixed(0)} scheduled hours \u2192 lost to {capacity.lossBreakdown.map(l => `${l.label.toLowerCase()} (${l.hours.toFixed(0)}h)`).join(", ")}
        {" \u2192 "}{capacity.productiveHours.toFixed(0)} productive hours remain.
      </div>

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px dashed " + V("border", "#232838") }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.4, color: V("muted", "#8b98a5"), textTransform: "uppercase", marginBottom: 8 }}>
          Cost an activity with this role
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div><label style={S.lbl}>Volume (participants, units...)</label>
            <NumCell value={actVolume} onChange={setActVolume} /></div>
          <div><label style={S.lbl}>Minutes per unit</label>
            <NumCell value={actMinutes} onChange={setActMinutes} /></div>
          <div><label style={S.lbl}>Rate tier</label>
            <SelectCell value={actTier} onChange={(v) => setActTier(v as any)}
              options={[{ v: "effective", label: "Effective (internal)" }, { v: "billable", label: "Billable" }, { v: "salary", label: "Salary only" }]} />
          </div>
        </div>
        <div style={{ marginTop: 8, fontSize: 11.5 }}>
          {(actVolume || 0).toLocaleString()} \u00d7 {actMinutes || 0} min = <strong>{activity.totalHours.toFixed(2)} hours</strong> of {r.name || "this role"}
          {" "}\u00d7 {fmtMoney(activity.hourlyCostUsed, cur)}/hr = <strong style={{ fontSize: 14 }}>{fmtMoney(activity.totalCost, cur)}</strong>
        </div>
      </div>
    </div>
  );
};

/* ============================================================================
 * MODULE 08 — SUPPLIER COMPARISON & MAKE-VS-BUY PANEL.
 * Reuses the exact same landed-cost formula as effectiveCostPerBaseUnit() -
 * every row here is calculated the identical way, so "cheapest" is a fair
 * comparison, not a different shortcut per row.
 * ========================================================================== */
const SupplierComparisonPanel: React.FC<{
  resource: CaResource; alternatives: Array<SupplierOption & { resource_id: string }>;
  onAdd: () => void; onPatch: (id: string, p: Partial<SupplierOption>) => void; onDelete: (id: string) => void;
  cur: string;
}> = ({ resource, alternatives, onAdd, onPatch, onDelete, cur }) => {
  const rows = useMemo(() => compareSuppliers(resource, alternatives), [resource, alternatives]);
  const M = (v: number) => fmtMoney(v, cur);

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px dashed " + V("border", "#232838") }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9 }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.4, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>
          Compare suppliers — Module 08
        </div>
        <button onClick={onAdd} style={S.btnGhost}>+ Add a supplier to compare</button>
      </div>
      <div style={{ fontSize: 9.5, color: V("muted", "#8b98a5"), marginBottom: 10 }}>
        Your current terms against any alternative quotes — same landed-cost math for every row, so the cheapest one really is the cheapest.
      </div>

      {rows.map((row) => {
        const alt = alternatives.find((a) => a.id === row.id);
        return (
          <div key={row.id} style={{ padding: "10px 0", borderBottom: "1px solid " + V("border", "#1a2030") }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {row.isCurrent
                  ? <strong style={{ fontSize: 11.5 }}>{row.supplierName} (current)</strong>
                  : <TextCell value={alt?.supplier_name ?? ""} onChange={(v) => onPatch(row.id, { supplier_name: v })} />}
                {row.isCheapest && <span style={{ fontSize: 8.5, fontWeight: 800, color: OK.fg, background: OK.bg, padding: "2px 7px", borderRadius: 8 }}>CHEAPEST</span>}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <strong style={{ fontSize: 13 }}>{M(row.landedCostPerUnit)}</strong>
                {!row.isCurrent && row.savingsVsCurrentPct != null && (
                  <span style={{ fontSize: 10, color: row.savingsVsCurrentPct > 0 ? OK.fg : BAD.fg }}>
                    {row.savingsVsCurrentPct > 0 ? "saves " : "costs "}{Math.abs(row.savingsVsCurrentPct).toFixed(0)}%
                  </span>
                )}
                {!row.isCurrent && <button onClick={() => onDelete(row.id)} style={{ background: "none", border: "none", color: BAD.fg, cursor: "pointer", fontSize: 12 }}>{"\u2715"}</button>}
              </div>
            </div>
            {!row.isCurrent && alt && (
              <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                <div><label style={S.lbl}>Price</label><NumCell value={alt.purchase_price} onChange={(v) => onPatch(row.id, { purchase_price: v })} /></div>
                <div><label style={S.lbl}>Freight</label><NumCell value={alt.freight_cost} onChange={(v) => onPatch(row.id, { freight_cost: v })} /></div>
                <div><label style={S.lbl}>Duty</label><NumCell value={alt.duty_cost} onChange={(v) => onPatch(row.id, { duty_cost: v })} /></div>
                <div><label style={S.lbl}>Other landed cost</label><NumCell value={alt.other_landed_cost} onChange={(v) => onPatch(row.id, { other_landed_cost: v })} /></div>
              </div>
            )}
          </div>
        );
      })}

      {alternatives.length === 0 && (
        <div style={{ fontSize: 10, color: V("muted", "#8b98a5"), fontStyle: "italic" }}>
          No alternatives added yet — add one to see whether you're paying more than you need to.
        </div>
      )}
    </div>
  );
};

const InputsTab: React.FC<{
  resources: CaResource[]; patchRes: (id: string, p: Partial<CaResource>) => void;
  addResource: () => void; delRes: (id: string) => void; cur: string;
  flagFor: FlagLookup; onAccept: AcceptFn;
  supplierOptions: Array<SupplierOption & { resource_id: string }>;
  addSupplierOption: (resourceId: string) => void;
  patchSupplierOption: (id: string, p: Partial<SupplierOption>) => void;
  delSupplierOption: (id: string) => void;
}> = ({ resources, patchRes, addResource, delRes, cur, flagFor, onAccept,
        supplierOptions, addSupplierOption, patchSupplierOption, delSupplierOption }) => {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [autoOpened, setAutoOpened] = useState(false);

  // A problem hidden behind a collapsed panel is a problem the user cannot find.
  useEffect(() => {
    if (autoOpened || expanded) return;
    const hidden = resources.find((r) => flagFor(r.id, "conversion_factor").length > 0);
    if (hidden) { setExpanded(hidden.id); setAutoOpened(true); }
  }, [resources, flagFor, expanded, autoOpened]);

  if (!resources.length) {
    return (
      <div style={S.card}>
        <Empty title="Nothing here yet"
          body="Add the things you buy - raw materials, staff time, machine time, software, energy. Anything that gets consumed to deliver what you sell."
          action={<button style={S.btn} onClick={addResource}>Add your first input</button>} />
      </div>
    );
  }

  return (
    <div style={S.card}>
      <div style={S.cardH}>What you buy</div>
      <div style={{ ...S.note, marginBottom: 12 }}>
        The <strong style={{ color: V("ink", "#e6edf3") }}>usable %</strong> column is the one most businesses get
        wrong. You pay for everything you buy, but only part of it reaches the finished product - trim waste on
        material, non-productive hours on staff, idle capacity on machines and software. The true cost column
        accounts for that automatically.
      </div>
      <div style={S.scroll}>
        <table style={S.table}>
          <thead><tr>
            <th style={{ ...S.th, width: "18%", minWidth: 130 }}>Name</th>
            <th style={{ ...S.th, width: 118 }}>Type</th>
            <th style={{ ...S.th, width: 92 }}>You pay</th>
            <th style={{ ...S.th, width: 74 }}>For qty</th>
            <th style={{ ...S.th, width: 86 }}>Bought as</th>
            <th style={{ ...S.th, width: 84 }}>Usable %</th>
            <th style={{ ...S.th, width: 134, textAlign: "right" }}>True cost</th>
            <th style={{ ...S.th, width: 62 }}></th>
          </tr></thead>
          <tbody>
            {resources.map((r) => {
              const eff = effectiveCostPerBaseUnit(r);
              const naive = naiveCostPerBaseUnit(r);
              const uplift = naive > 0 ? ((eff / naive) - 1) * 100 : 0;
              const isOpen = expanded === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr>
                    <td style={S.td}>
                      <Flag findings={flagFor(r.id, "recipe")} onAccept={onAccept}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <TextCell value={r.name} onChange={(v) => patchRes(r.id, { name: v })} placeholder="Butter, welder time, AWS..." />
                          {/* DATA PROVENANCE, THE CONFIRMED FIX: this used to
                              vanish the moment a discovered item was saved -
                              no way to ever again tell "AI estimated this"
                              from "I typed this myself and I know it's
                              right." Now permanent, not just during the
                              first review. */}
                          {r.data_confidence === "must_supply" && (
                            <span title="The AI could not find a real number for this - please check and correct it." style={{ fontSize: 8, fontWeight: 800, color: BAD.fg, background: BAD.bg, padding: "2px 6px", borderRadius: 8, flexShrink: 0 }}>NEEDS YOUR NUMBER</span>
                          )}
                          {r.data_confidence === "needs_check" && (
                            <span title="The AI estimated this - worth double-checking against your real cost." style={{ fontSize: 8, fontWeight: 800, color: WARN.fg, background: WARN.bg, padding: "2px 6px", borderRadius: 8, flexShrink: 0 }}>AI ESTIMATE</span>
                          )}
                        </div>
                      </Flag>
                    </td>
                    <td style={S.td}><SelectCell value={r.resource_class} onChange={(v) => patchRes(r.id, { resource_class: v as ResourceClass })}
                      options={CLASSES.map((c) => ({ v: c.v, label: c.label }))} /></td>
                    <td style={S.td}><Flag findings={flagFor(r.id, "purchase_price")} onAccept={onAccept}><NumCell value={r.purchase_price} onChange={(v) => patchRes(r.id, { purchase_price: v })} /></Flag></td>
                    <td style={S.td}><NumCell value={r.purchase_qty} onChange={(v) => patchRes(r.id, { purchase_qty: v })} /></td>
                    <td style={S.td}><TextCell value={r.purchase_uom} onChange={(v) => patchRes(r.id, { purchase_uom: v, base_uom: r.base_uom || v })} placeholder="kg" /></td>
                    <td style={S.td}><Flag findings={flagFor(r.id, "effective_yield_pct")} onAccept={onAccept}>
                      {/* HARD BOUNDS, ON TOP OF THE EXISTING SOFT WARNINGS:
                          a usable % below 0 or above 100 is not "unusual",
                          it is meaningless — you cannot use more material
                          than you bought, or a negative amount of it. The
                          validator already warns on suspicious-but-possible
                          values (e.g. 15% usable); this stops the genuinely
                          impossible ones from being entered at all. */}
                      <NumCell value={r.effective_yield_pct} onChange={(v) => {
                        const clamped = v == null ? v : Math.min(100, Math.max(0, v));
                        patchRes(r.id, { effective_yield_pct: clamped });
                      }} suffix="%" /></Flag></td>
                    <td style={{ ...S.td, textAlign: "right", fontWeight: 700, whiteSpace: "nowrap" }}>
                      {fmtMoney(eff, cur)}
                      <span style={{ fontSize: 9.5, fontWeight: 500, color: V("muted", "#8b98a5") }}> / {r.base_uom || r.purchase_uom || "unit"}</span>
                      {uplift > 0.5 && <div style={{ fontSize: 9.5, fontWeight: 600, color: WARN.fg }}>+{uplift.toFixed(1)}% hidden</div>}
                    </td>
                    <td style={{ ...S.td, whiteSpace: "nowrap" }}>
                      <button style={{ ...S.btnDel,
                          color: flagFor(r.id, "conversion_factor").length ? BAD.fg : undefined,
                          fontWeight: flagFor(r.id, "conversion_factor").length ? 800 : undefined,
                          background: flagFor(r.id, "conversion_factor").length && !isOpen ? BAD.bg : "transparent",
                          borderRadius: 4 }}
                        onClick={() => setExpanded(isOpen ? null : r.id)}
                        title={flagFor(r.id, "conversion_factor").length ? "A problem is inside here - click to open" : "More detail"}>{isOpen ? "\u2212" : "+"}</button>
                      <button style={S.btnDel} onClick={() => delRes(r.id)} title="Remove">&times;</button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr><td colSpan={8} style={{ ...S.td, background: V("bg", "#070c18"), padding: 12 }}>
                      <div style={{ fontSize: 10.5, color: V("muted", "#8b98a5"), marginBottom: 9 }}>
                        Usable % for this type means: <strong style={{ color: V("ink", "#e6edf3") }}>{yieldLabel(r.resource_class, r.yield_basis_label)}</strong>
                      </div>
                      <div style={S.grid2}>
                        <div><label style={S.lbl}>Base unit used in recipes</label>
                          <TextCell value={r.base_uom} onChange={(v) => patchRes(r.id, { base_uom: v })} placeholder="kg, hour, unit" /></div>
                        <div><label style={S.lbl}>How many base units per purchase</label>
                          <Flag findings={flagFor(r.id, "conversion_factor")} onAccept={onAccept}><NumCell value={r.conversion_factor} onChange={(v) => patchRes(r.id, { conversion_factor: v })} /></Flag></div>
                        <div><label style={S.lbl}>Freight / delivery</label>
                          <NumCell value={r.freight_cost} onChange={(v) => patchRes(r.id, { freight_cost: v })} /></div>
                        <div><label style={S.lbl}>Duty / other charges</label>
                          <NumCell value={r.duty_cost} onChange={(v) => patchRes(r.id, { duty_cost: v })} /></div>
                        <div><label style={S.lbl}>GST you can claim back</label>
                          <NumCell value={r.input_tax_credit} onChange={(v) => patchRes(r.id, { input_tax_credit: v })} /></div>
                        <div><label style={S.lbl}>Scrap you can resell</label>
                          <NumCell value={r.scrap_recovery_value} onChange={(v) => patchRes(r.id, { scrap_recovery_value: v })} /></div>
                        <div><label style={S.lbl}>Supplier</label>
                          <TextCell value={r.supplier_name} onChange={(v) => patchRes(r.id, { supplier_name: v })} placeholder="Who you buy from" /></div>
                        <div><label style={S.lbl}>Lead time (days)</label>
                          <NumCell value={r.lead_time_days} onChange={(v) => patchRes(r.id, { lead_time_days: v })} /></div>
                        <div><label style={S.lbl}>Commodity index to track</label>
                          <TextCell value={r.index_ref} onChange={(v) => patchRes(r.id, { index_ref: v })} placeholder="WPI-Wheat, LME-Copper" /></div>
                        <div style={{ display: "flex", alignItems: "flex-end", gap: 14, paddingBottom: 5 }}>
                          <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                            <input type="checkbox" checked={!!r.is_substitutable} onChange={(e) => patchRes(r.id, { is_substitutable: e.target.checked })} />
                            Alternative exists
                          </label>
                          <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                            <input type="checkbox" checked={!!r.is_bottleneck} onChange={(e) => patchRes(r.id, { is_bottleneck: e.target.checked })} />
                            This is the bottleneck
                          </label>
                        </div>
                      </div>
                      <div style={{ ...S.note, marginTop: 10 }}>
                        Landed cost {fmtMoney(naive, cur)} per {r.base_uom || "unit"} before waste.
                        After {num(r.effective_yield_pct, 100)}% usable, true cost is <strong style={{ color: V("ink", "#e6edf3") }}>{fmtMoney(eff, cur)}</strong>.
                      </div>
                      {/* MODULE 5 — shown ONLY for labour, because this build-up
                          means nothing for a kilogram of butter. Section 12's
                          exact waterfall: Scheduled -> Net Available ->
                          Productive -> Billable, each step named, none of it
                          collapsed into one guessed percentage. */}
                      {r.resource_class === "LABOUR" && (
                        <WorkforcePanel resource={r} patchRes={patchRes} cur={cur} />
                      )}

                      {/* MODULE 08 — supplier comparison, every resource type. */}
                      <SupplierComparisonPanel resource={r}
                        alternatives={supplierOptions.filter((s) => s.resource_id === r.id)}
                        onAdd={() => addSupplierOption(r.id)} onPatch={patchSupplierOption} onDelete={delSupplierOption}
                        cur={cur} />
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <button style={{ ...S.btnGhost, marginTop: 11 }} onClick={addResource}>+ Add input</button>
    </div>
  );
};

/* ============================================================================
 * TAB :: PRODUCTS
 * ========================================================================== */

const ProductsTab: React.FC<{
  offerings: CaOffering[]; resources: CaResource[]; bomLines: CaBomLine[]; channels: CaChannel[];
  offeringChannels: CaOfferingChannel[]; dx: PortfolioDiagnosis; costPools: CaCostPool[];
  openOffering: string | null; setOpenOffering: (v: string | null) => void;
  patchOff: (id: string, p: Partial<CaOffering>) => void;
  patchBom: (id: string, p: Partial<CaBomLine>) => void;
  patchOC: (id: string, p: Partial<CaOfferingChannel>) => void;
  addOffering: () => void; addBomLine: (id: string) => void; linkChannel: (o: string, c: string) => void;
  delOff: (id: string) => void; delBom: (id: string) => void; delOC: (id: string) => void;
  M: (v: number) => string;
  flagFor: FlagLookup; onAccept: AcceptFn;
}> = (p) => {
  if (!p.offerings.length) {
    return (
      <div style={S.card}>
        <Empty title="Nothing to sell yet"
          body="Add a product, service or plan. Then build its recipe from the inputs you entered, and the true cost per unit appears straight away."
          action={<button style={S.btn} onClick={p.addOffering}>Add your first product</button>} />
      </div>
    );
  }

  return (
    <>
      {p.offerings.map((o) => {
        const econ = p.dx.offerings.find((e) => e.offeringId === o.id);
        const lines = p.bomLines.filter((b) => b.offering_id === o.id);
        const links = p.offeringChannels.filter((c) => c.offering_id === o.id);
        const isOpen = p.openOffering === o.id;
        const cm = econ?.contributionMarginPct ?? 0;
        const tone = econ?.isLossMaking ? BAD : cm >= 40 ? OK : cm > 0 ? WARN : NEU;

        return (
          <div key={o.id} style={S.card}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ flex: "2 1 190px", minWidth: 160 }}>
                <label style={S.lbl}>Name</label>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <TextCell value={o.name} onChange={(v) => p.patchOff(o.id, { name: v })} placeholder="Birthday cake 1kg" />
                  {o.data_confidence === "must_supply" && (
                    <span title="The AI could not find a real number for this - please check and correct it." style={{ fontSize: 8, fontWeight: 800, color: BAD.fg, background: BAD.bg, padding: "2px 6px", borderRadius: 8, flexShrink: 0 }}>NEEDS YOUR NUMBER</span>
                  )}
                  {o.data_confidence === "needs_check" && (
                    <span title="The AI estimated this - worth double-checking against your real price." style={{ fontSize: 8, fontWeight: 800, color: WARN.fg, background: WARN.bg, padding: "2px 6px", borderRadius: 8, flexShrink: 0 }}>AI ESTIMATE</span>
                  )}
                </div>
              </div>
              <div style={{ flex: "1 1 130px" }}>
                <label style={S.lbl}>Type</label>
                <SelectCell value={o.offering_type} onChange={(v) => p.patchOff(o.id, { offering_type: v as OfferingType })}
                  options={OFFERING_TYPES.map((t) => ({ v: t.v, label: t.label }))} />
              </div>
              <div style={{ flex: "0 1 95px" }}>
                <label style={S.lbl}>Price</label>
                <Flag findings={p.flagFor(o.id, "list_price")} onAccept={p.onAccept}>
                  <NumCell value={o.list_price} onChange={(v) => p.patchOff(o.id, { list_price: v })} />
                </Flag>
              </div>
              <div style={{ flex: "0 1 90px" }}>
                <label style={S.lbl}>Vol / month</label>
                <Flag findings={p.flagFor(o.id, "monthly_volume")} onAccept={p.onAccept}>
                  <NumCell value={o.monthly_volume} onChange={(v) => p.patchOff(o.id, { monthly_volume: v })} />
                </Flag>
              </div>
              <div style={{ flex: "0 1 95px" }}>
                <label style={S.lbl}>Bottleneck min</label>
                <Flag findings={p.flagFor(o.id, "constraint_minutes_per_unit")} onAccept={p.onAccept}>
                  <NumCell value={o.constraint_minutes_per_unit} onChange={(v) => p.patchOff(o.id, { constraint_minutes_per_unit: v })} />
                </Flag>
              </div>
              <div style={{ display: "flex", gap: 5, alignItems: "flex-end", paddingBottom: 3 }}>
                <Flag findings={p.flagFor(o.id, "recipe")} onAccept={p.onAccept}>
                  <button style={S.btnGhost} onClick={() => p.setOpenOffering(isOpen ? null : o.id)}>
                    {isOpen ? "Close recipe" : `Recipe (${lines.length})`}
                  </button>
                </Flag>
                <button style={S.btnDel} onClick={() => p.delOff(o.id)} title="Remove">&times;</button>
              </div>
            </div>

            {econ && (
              <div style={{ display: "flex", gap: 16, marginTop: 12, paddingTop: 11, borderTop: `1px solid ${V("faint", "#16202c")}`, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ fontSize: 11 }}><span style={{ color: V("muted", "#8b98a5") }}>Cost to make </span><strong>{p.M(econ.marginalCostPerUnit)}</strong></span>
                <span style={{ fontSize: 11 }}><span style={{ color: V("muted", "#8b98a5") }}>Reaches bank </span><strong>{p.M(econ.blendedNetRealisation)}</strong></span>
                <span style={{ fontSize: 11 }}><span style={{ color: V("muted", "#8b98a5") }}>Profit / unit </span><strong style={{ color: tone.fg }}>{p.M(econ.contributionPerUnit)}</strong></span>
                <Chip tone={tone}>{econ.isLossMaking ? "Loses money" : cm.toFixed(1) + "% margin"}</Chip>
                {econ.contributionPerConstraintMinute != null && (
                  <span style={{ fontSize: 11, color: V("muted", "#8b98a5") }}>
                    {p.M(econ.contributionPerConstraintMinute)}/bottleneck-min
                  </span>
                )}
                {econ.paretoTop3Labels.length > 0 && (
                  <span style={{ fontSize: 10.5, color: V("muted", "#8b98a5") }}>
                    Driven by {econ.paretoTop3Labels.slice(0, 3).join(", ")} ({econ.paretoTop3SharePct}%)
                  </span>
                )}
              </div>
            )}

            {isOpen && (
              <div style={{ marginTop: 13, paddingTop: 12, borderTop: `1px solid ${V("border", "#1e2a38")}` }}>
                <div style={S.cardH}>Recipe - what goes into one {o.output_uom || "unit"}</div>
                {lines.length === 0 ? (
                  <div style={{ ...S.note, marginBottom: 9 }}>
                    Nothing added yet. Each line is one input and how much of it a single unit consumes.
                  </div>
                ) : (
                  <div style={S.scroll}>
                    <table style={S.table}>
                      <thead><tr>
                        <th style={S.th}>Input</th>
                        <th style={{ ...S.th, width: 95 }}>Qty per unit</th>
                        <th style={{ ...S.th, width: 78 }}>Waste %</th>
                        <th style={{ ...S.th, width: 105 }}>Charged per</th>
                        <th style={{ ...S.th, width: 105, textAlign: "right" }}>Cost</th>
                        <th style={{ ...S.th, width: 62, textAlign: "right" }}>Share</th>
                        <th style={{ ...S.th, width: 32 }}></th>
                      </tr></thead>
                      <tbody>
                        {lines.map((b) => {
                          const cl = econ?.costLines.find((x) => x.sourceId === (b.child_resource_id || b.child_offering_id) && x.depth === 0);
                          return (
                            <tr key={b.id}>
                              <td style={S.td}>
                                <SelectCell value={b.child_type === "RESOURCE" ? "R:" + b.child_resource_id : "O:" + b.child_offering_id}
                                  onChange={(v) => {
                                    if (v.startsWith("R:")) p.patchBom(b.id, { child_type: "RESOURCE", child_resource_id: v.slice(2), child_offering_id: null });
                                    else p.patchBom(b.id, { child_type: "OFFERING", child_offering_id: v.slice(2), child_resource_id: null });
                                  }}
                                  options={[
                                    ...p.resources.map((r) => ({ v: "R:" + r.id, label: r.name || "(unnamed input)" })),
                                    ...p.offerings.filter((x) => x.id !== o.id).map((x) => ({ v: "O:" + x.id, label: "\u21B3 " + (x.name || "(unnamed)") })),
                                  ]} />
                              </td>
                              <td style={S.td}><NumCell value={b.qty_per_unit} onChange={(v) => p.patchBom(b.id, { qty_per_unit: v })} /></td>
                              <td style={S.td}><NumCell value={b.process_scrap_pct} onChange={(v) => p.patchBom(b.id, { process_scrap_pct: v })} suffix="%" /></td>
                              <td style={S.td}><SelectCell value={b.applies_per ?? "UNIT"} onChange={(v) => p.patchBom(b.id, { applies_per: v as any })}
                                options={[{ v: "UNIT", label: "Each unit" }, { v: "BATCH", label: "Whole batch" }, { v: "PROJECT", label: "Whole project" }]} /></td>
                              <td style={{ ...S.td, textAlign: "right", fontWeight: 600 }}>{cl ? p.M(cl.lineCost) : "-"}</td>
                              <td style={{ ...S.td, textAlign: "right", color: V("muted", "#8b98a5") }}>{cl ? cl.sharePct + "%" : "-"}</td>
                              <td style={S.td}><button style={S.btnDel} onClick={() => p.delBom(b.id)}>&times;</button></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                <button style={{ ...S.btnGhost, marginTop: 9 }} onClick={() => p.addBomLine(o.id)}>+ Add recipe line</button>

                {num(o.batch_size, 1) > 1 && (
                  <div style={{ ...S.note, marginTop: 9 }}>
                    Batch size {o.batch_size}. Lines charged per batch are divided across that many units.
                  </div>
                )}

                <div style={{ ...S.cardH, marginTop: 18 }}>Where this one sells</div>
                {p.channels.length === 0 ? (
                  <div style={S.note}>No channels set up yet. Add them on the "Where you sell" tab to see commission and discount leakage.</div>
                ) : (
                  <>
                    {links.length > 0 && (
                      <div style={S.scroll}>
                        <table style={S.table}>
                          <thead><tr>
                            <th style={S.th}>Channel</th>
                            <th style={{ ...S.th, width: 100 }}>Price here</th>
                            <th style={{ ...S.th, width: 90 }}>% of volume</th>
                            <th style={{ ...S.th, width: 110, textAlign: "right" }}>Reaches bank</th>
                            <th style={{ ...S.th, width: 90, textAlign: "right" }}>Margin</th>
                            <th style={{ ...S.th, width: 32 }}></th>
                          </tr></thead>
                          <tbody>
                            {links.map((lk) => {
                              const ch = p.channels.find((c) => c.id === lk.channel_id);
                              const ce = econ?.channelEconomics.find((c) => c.channelId === lk.channel_id);
                              return (
                                <tr key={lk.id}>
                                  <td style={S.td}>{ch?.name || "(unnamed)"}</td>
                                  <td style={S.td}><NumCell value={lk.list_price_override} onChange={(v) => p.patchOC(lk.id, { list_price_override: v })} placeholder={String(o.list_price ?? "")} /></td>
                                  <td style={S.td}><Flag findings={p.flagFor(o.id, "volume_share_pct")} onAccept={p.onAccept}><NumCell value={lk.volume_share_pct} onChange={(v) => p.patchOC(lk.id, { volume_share_pct: v })} suffix="%" /></Flag></td>
                                  <td style={{ ...S.td, textAlign: "right" }}>
                                    {ce ? p.M(ce.netRealisation) : "-"}
                                    {ce && ce.leakagePct > 0 && <div style={{ fontSize: 9.5, color: ce.leakagePct > 25 ? BAD.fg : WARN.fg }}>-{ce.leakagePct}% lost</div>}
                                  </td>
                                  <td style={{ ...S.td, textAlign: "right", fontWeight: 700, color: ce?.isLossMaking ? BAD.fg : V("ink", "#e6edf3") }}>
                                    {ce ? ce.contributionMarginPct + "%" : "-"}
                                  </td>
                                  <td style={S.td}><button style={S.btnDel} onClick={() => p.delOC(lk.id)}>&times;</button></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 6, marginTop: 9, flexWrap: "wrap" }}>
                      {p.channels.filter((c) => !links.some((l) => l.channel_id === c.id)).map((c) => (
                        <button key={c.id} style={S.btnGhost} onClick={() => p.linkChannel(o.id, c.id)}>+ {c.name || "(unnamed)"}</button>
                      ))}
                    </div>
                  </>
                )}

                <div style={{ ...S.grid2, marginTop: 16 }}>
                  <div><label style={S.lbl}>Unit name</label>
                    <TextCell value={o.output_uom} onChange={(v) => p.patchOff(o.id, { output_uom: v })} placeholder="cake, tonne, engagement" /></div>
                  <div><label style={S.lbl}>Batch size</label>
                    <NumCell value={o.batch_size} onChange={(v) => p.patchOff(o.id, { batch_size: v })} /></div>
                  <div><label style={S.lbl}>Target margin %</label>
                    <NumCell value={o.target_margin_pct} onChange={(v) => p.patchOff(o.id, { target_margin_pct: v })} suffix="%" /></div>
                  {/* MODULE 12 — CUSTOMER ECONOMICS.
                      THE CONFIRMED BUG: this was gated to offering_type ===
                      "SUBSCRIPTION" only - meaning a bakery, a pickle brand,
                      a consultancy, anyone with genuine repeat customers who
                      isn't formally a "subscription" business, could never
                      even TYPE a churn rate or acquisition cost, let alone
                      see the resulting lifetime value. The calculation
                      (ltv, ltvCacRatio, cacPaybackMonths) was already
                      correct - only the gate was wrong. Widened to every
                      type except SUB_ASSEMBLY, which isn't sold directly to
                      an end customer, so "acquiring a customer" genuinely
                      doesn't apply there. Also added: expected customer
                      lifetime, which had NO input field anywhere at all -
                      the lifetime-value number depended on a field nobody
                      could ever actually fill in through the interface. */}
                  {o.offering_type !== "SUB_ASSEMBLY" && (<>
                    <div><label style={S.lbl}>Monthly churn % (customers who don't come back)</label>
                      <NumCell value={o.churn_rate_monthly_pct} onChange={(v) => p.patchOff(o.id, { churn_rate_monthly_pct: v })} suffix="%" /></div>
                    <div><label style={S.lbl}>Cost to win a customer</label>
                      <NumCell value={o.cac} onChange={(v) => p.patchOff(o.id, { cac: v })} /></div>
                    <div><label style={S.lbl}>Expected customer lifetime (months)</label>
                      <NumCell value={o.expected_lifetime_months} onChange={(v) => p.patchOff(o.id, { expected_lifetime_months: v })} suffix="mo" /></div>
                  </>)}
                </div>

                {econ && econ.ltvCacRatio != null && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px dashed " + V("border", "#232838") }}>
                    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.4, color: V("muted", "#8b98a5"), textTransform: "uppercase", marginBottom: 9 }}>
                      Customer economics — Module 12
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 }}>
                      <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Lifetime value</div>
                        <div style={{ fontSize: 16, fontWeight: 800 }}>{p.M(econ.ltv || 0)}</div></div>
                      <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>LTV : CAC ratio</div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: econ.ltvCacRatio >= 3 ? OK.fg : BAD.fg }}>{econ.ltvCacRatio}x</div></div>
                      <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Payback period</div>
                        <div style={{ fontSize: 16, fontWeight: 800 }}>{econ.cacPaybackMonths} months</div></div>
                    </div>
                    <div style={{ fontSize: 9.5, color: V("muted", "#8b98a5"), marginTop: 8 }}>
                      {econ.ltvCacRatio >= 3
                        ? "A ratio of 3x or higher is generally considered healthy — each customer is worth meaningfully more than it costs to win them."
                        : "Below 3x is generally unsustainable long-term — you're spending close to (or more than) what a customer is actually worth."}
                    </div>
                  </div>
                )}

                {/* MODULE 6 — PRICING & BREAK-EVEN, per offering, not just
                    portfolio-blended. Uses fields CostEngine.ts already
                    computes (allocatedOverheadPerUnit, contributionPerUnit) -
                    no new allocation logic, purely a new lens on numbers
                    that already existed. */}
                {econ && (
                  <PricingBreakEvenPanel econ={econ} offering={o} patchOff={p.patchOff} M={p.M} cur={p.cur} />
                )}

                {/* MODULE 7 — where cost actually concentrates in your process. */}
                <ActivityCostPanel offering={o} resources={p.resources} bomLines={p.bomLines} offerings={p.offerings} M={p.M} />

                {/* MODULE 8 — which cost pool contributed how much overhead, per offering. */}
                <AllocationPanel offering={o} dx={p.dx} costPools={p.costPools} M={p.M} />
              </div>
            )}
          </div>
        );
      })}
      <button style={S.btn} onClick={p.addOffering}>+ Add product</button>
    </>
  );
};

/* ============================================================================
 * MODULE 6 — PRICING & BREAK-EVEN PANEL.
 * ========================================================================== */
const PricingBreakEvenPanel: React.FC<{
  econ: OfferingEconomics; offering: CaOffering;
  patchOff: (id: string, p: Partial<CaOffering>) => void;
  M: (v: number) => string; cur: string;
}> = ({ econ, offering, patchOff, M, cur }) => {
  const be = useMemo(() => offeringBreakEven(econ), [econ]);
  const [whatIfPrice, setWhatIfPrice] = useState<number | null>(null);
  const variableCostPerUnit = econ.marginalCostPerUnit;
  const targetMargin = offering.target_margin_pct;
  const suggestedPrice = targetMargin != null ? priceForTargetMargin(econ.fullyLoadedCostPerUnit, targetMargin) : null;
  const impact = useMemo(() => whatIfPrice != null
    ? priceChangeImpact(econ.listPrice, econ.monthlyVolume, econ.contributionPerUnit, variableCostPerUnit, whatIfPrice)
    : null, [whatIfPrice, econ, variableCostPerUnit]);

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px dashed " + V("border", "#232838") }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.4, color: V("muted", "#8b98a5"), textTransform: "uppercase", marginBottom: 9 }}>
        Pricing &amp; break-even — Module 6
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10,
        padding: 10, background: be.isBelowBreakEven ? BAD.bg : V("bg", "#070c18"), borderRadius: 6, marginBottom: 10 }}>
        <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Break-even (this product)</div>
          <div style={{ fontSize: 14, fontWeight: 800 }}>{be.breakEvenUnits.toFixed(0)} units/mo</div></div>
        <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Break-even revenue</div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{M(be.breakEvenRevenue)}</div></div>
        <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Current volume</div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{be.currentVolume.toFixed(0)} units/mo</div></div>
        <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Margin of safety</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: be.isBelowBreakEven ? BAD.fg : OK.fg }}>
            {be.marginOfSafetyPct.toFixed(0)}%
          </div></div>
      </div>
      {be.isBelowBreakEven && (
        <div style={{ fontSize: 10.5, color: BAD.fg, marginBottom: 10 }}>
          This product is currently selling BELOW its own break-even — every unit sold at this volume is losing money once its fair share of fixed cost is counted, even if the wider business looks profitable.
        </div>
      )}

      {targetMargin != null && suggestedPrice != null && !isNaN(suggestedPrice) && (
        <div style={{ fontSize: 11, marginBottom: 10 }}>
          To hit your {targetMargin}% target margin on a fully-loaded cost of {M(econ.fullyLoadedCostPerUnit)}, price should be
          {" "}<strong style={{ fontSize: 13 }}>{M(suggestedPrice)}</strong>
          {econ.listPrice > 0 && Math.abs(suggestedPrice - econ.listPrice) > 0.5 && (
            <span style={{ color: suggestedPrice > econ.listPrice ? WARN.fg : OK.fg }}>
              {" "}(currently {M(econ.listPrice)}, {suggestedPrice > econ.listPrice ? "under" : "over"}-priced by {M(Math.abs(suggestedPrice - econ.listPrice))})
            </span>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div><label style={S.lbl}>What if price were...</label>
          <NumCell value={whatIfPrice} onChange={setWhatIfPrice} placeholder={String(econ.listPrice)} /></div>
      </div>
      {impact && (
        <div style={{ fontSize: 11, marginTop: 8 }}>
          At {M(impact.newPrice)}, you need <strong>{impact.volumeNeededForSameContribution.toFixed(0)} units/mo</strong>
          {" "}({impact.volumeChangeNeededPct >= 0 ? "+" : ""}{impact.volumeChangeNeededPct.toFixed(0)}% vs today) just to keep the same total contribution you make now.
          {!impact.isPriceIncreaseViable && (
            <span style={{ color: BAD.fg }}> This price rise still needs more volume, not less — check the assumption.</span>
          )}
        </div>
      )}
    </div>
  );
};

/* ============================================================================
 * MODULE 7 — ACTIVITY / PROCESS-STEP COST PANEL.
 * ========================================================================== */
const ActivityCostPanel: React.FC<{
  offering: CaOffering; resources: CaResource[]; bomLines: CaBomLine[]; offerings: CaOffering[]; M: (v: number) => string;
}> = ({ offering, resources, bomLines, offerings, M }) => {
  const breakdown = useMemo(() => activityBreakdown(offering, { resources, bomLines, offerings } as CostWorkspace), [offering, resources, bomLines, offerings]);
  if (!breakdown.steps.length) return null;

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px dashed " + V("border", "#232838") }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.4, color: V("muted", "#8b98a5"), textTransform: "uppercase", marginBottom: 9 }}>
        Cost by process step — Module 7
      </div>
      <div style={{ fontSize: 9.5, color: V("muted", "#8b98a5"), marginBottom: 10, lineHeight: 1.5 }}>
        Where the cost of one unit actually comes from, in the order your process happens — not just a flat ingredient list.
      </div>
      {breakdown.steps.map((s) => (
        <div key={s.stepName} style={{ marginBottom: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5 }}>
            <strong>{s.stepName}</strong>
            <span>{M(s.totalCost)} <span style={{ color: V("muted", "#8b98a5"), fontWeight: 400 }}>({s.sharePct.toFixed(0)}%)</span></span>
          </div>
          <div style={{ height: 5, background: V("bg", "#070c18"), borderRadius: 3, overflow: "hidden", marginTop: 3, marginBottom: 3 }}>
            <div style={{ height: "100%", width: `${s.sharePct}%`, background: "#8b5cf6" }} />
          </div>
          <div style={{ fontSize: 9, color: V("muted", "#8b98a5") }}>
            {s.lines.map((l) => `${l.resourceName} (${M(l.lineCost)})`).join(", ")}
          </div>
        </div>
      ))}
      {breakdown.subAssemblyCost === 0 && bomLines.some(b => b.offering_id === offering.id && b.child_type === "OFFERING") && (
        <div style={{ fontSize: 9, color: WARN.fg, marginTop: 6 }}>
          This product uses another product as a sub-assembly. Its cost is included correctly elsewhere in this
          product's total, but a step-by-step breakdown of THAT sub-assembly's own process is not shown here yet —
          a genuinely separate piece of work, stated honestly rather than guessed at.
        </div>
      )}
    </div>
  );
};

/* ============================================================================
 * MODULE 8 — COST ALLOCATION TRANSPARENCY PANEL.
 * ========================================================================== */
const AllocationPanel: React.FC<{
  offering: CaOffering; dx: PortfolioDiagnosis; costPools: CaCostPool[]; M: (v: number) => string;
}> = ({ offering, dx, costPools, M }) => {
  const allocInputs = useMemo(() => dx.offerings.map((o) => ({
    offeringId: o.offeringId, volume: o.monthlyVolume,
    revenue: o.blendedNetRealisation * o.monthlyVolume,
    directCost: o.marginalCostPerUnit * o.monthlyVolume,
    constraintMinutes: 0, labourMinutes: 0, machineMinutes: 0,
  })), [dx]);
  const result = useMemo(() => allocationBreakdown(offering.id, {
    resources: [], bomLines: [], offerings: dx.offerings.map(o => ({ id: o.offeringId } as any)),
    costPools,
  } as any, allocInputs), [offering.id, dx, costPools, allocInputs]);

  if (!result.pools.length) return null;
  const hasTimeBasedPool = result.pools.some(p => ["labour_hours", "machine_hours", "constraint_hours"].includes(p.basis));

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px dashed " + V("border", "#232838") }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.4, color: V("muted", "#8b98a5"), textTransform: "uppercase", marginBottom: 9 }}>
        Where your overhead allocation comes from — Module 8
      </div>
      <table style={{ width: "100%", fontSize: 11 }}>
        <thead><tr>
          <th style={{ textAlign: "left", fontSize: 9, color: V("muted", "#8b98a5"), paddingBottom: 4 }}>Cost pool</th>
          <th style={{ textAlign: "left", fontSize: 9, color: V("muted", "#8b98a5"), paddingBottom: 4 }}>Basis (how it's shared out)</th>
          <th style={{ textAlign: "right", fontSize: 9, color: V("muted", "#8b98a5"), paddingBottom: 4 }}>This product's share</th>
        </tr></thead>
        <tbody>
          {result.pools.map((pool) => (
            <tr key={pool.poolId}>
              <td style={{ padding: "4px 0" }}>{pool.poolName}{pool.isAvoidable && <span style={{ fontSize: 8.5, color: WARN.fg, marginLeft: 5 }}>avoidable</span>}</td>
              <td style={{ padding: "4px 0", color: V("muted", "#8b98a5"), textTransform: "capitalize" }}>{pool.basis.replace(/_/g, " ")}</td>
              <td style={{ padding: "4px 0", textAlign: "right", fontWeight: 700 }}>
                {["labour_hours", "machine_hours", "constraint_hours"].includes(pool.basis)
                  ? <span style={{ color: WARN.fg, fontWeight: 600 }} title="Needs process-time data not yet available to this panel">not shown</span>
                  : M(pool.allocatedToThisOffering)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {hasTimeBasedPool && (
        <div style={{ fontSize: 9, color: WARN.fg, marginTop: 8, lineHeight: 1.5 }}>
          One or more pools allocate by labour, machine, or constraint hours (time-based sharing). This panel does
          not yet have access to the detailed timing data needed to compute those accurately, so it says "not
          shown" rather than presenting a guessed number. The total elsewhere on this page still uses the real
          calculation — only this transparency view is limited for those specific pools, stated plainly rather
          than hidden.
        </div>
      )}
    </div>
  );
};

/* ============================================================================
 * TAB :: CHANNELS
 * ========================================================================== */

const ChannelsTab: React.FC<{
  channels: CaChannel[]; patchCh: (id: string, p: Partial<CaChannel>) => void;
  addChannel: () => void; delCh: (id: string) => void; cur: string;
  flagFor: FlagLookup; onAccept: AcceptFn;
}> = ({ channels, patchCh, addChannel, delCh, flagFor, onAccept }) => {
  if (!channels.length) {
    return (
      <div style={S.card}>
        <Empty title="No sales channels yet"
          body="Add each place you sell - your own store, a marketplace, an aggregator, a distributor. Commission, discounts and delivery costs are usually where margin quietly disappears."
          action={<button style={S.btn} onClick={addChannel}>Add a channel</button>} />
      </div>
    );
  }
  return (
    <div style={S.card}>
      <div style={S.cardH}>Where you sell</div>
      <div style={{ ...S.note, marginBottom: 12 }}>
        List price is not what you receive. Every percentage below is subtracted before the money reaches you.
        Same product, two channels, and the difference is often larger than your entire profit margin.
      </div>
      <div style={S.scroll}>
        <table style={S.tableWide}>
          <thead><tr>
            <th style={{ ...S.th, width: 130 }}>Channel</th>
            <th style={{ ...S.th, width: 145 }}>Type</th>
            <th style={{ ...S.th, width: 84 }}>Commission</th>
            <th style={{ ...S.th, width: 84 }}>Discount</th>
            <th style={{ ...S.th, width: 84 }}>Ads</th>
            <th style={{ ...S.th, width: 84 }}>Gateway</th>
            <th style={{ ...S.th, width: 84 }}>Packaging</th>
            <th style={{ ...S.th, width: 84 }}>Delivery</th>
            <th style={{ ...S.th, width: 78 }}>Returns</th>
            <th style={{ ...S.th, width: 70 }}>GST</th>
            <th style={{ ...S.th, width: 90, textAlign: "right" }}>Total drag</th>
            <th style={{ ...S.th, width: 32 }}></th>
          </tr></thead>
          <tbody>
            {channels.map((c) => {
              const drag = num(c.commission_pct) + num(c.discount_pct) + num(c.ad_spend_pct) + num(c.payment_gateway_pct) + num(c.returns_pct);
              return (
                <tr key={c.id}>
                  <td style={S.td}><TextCell value={c.name} onChange={(v) => patchCh(c.id, { name: v })} placeholder="Swiggy" /></td>
                  <td style={S.td}><SelectCell value={c.channel_type ?? "direct"} onChange={(v) => patchCh(c.id, { channel_type: v })} options={CHANNEL_TYPES} /></td>
                  <td style={S.td}><Flag findings={flagFor(c.id, "commission_pct")} onAccept={onAccept}><NumCell value={c.commission_pct} onChange={(v) => patchCh(c.id, { commission_pct: v })} suffix="%" /></Flag></td>
                  <td style={S.td}><NumCell value={c.discount_pct} onChange={(v) => patchCh(c.id, { discount_pct: v })} suffix="%" /></td>
                  <td style={S.td}><NumCell value={c.ad_spend_pct} onChange={(v) => patchCh(c.id, { ad_spend_pct: v })} suffix="%" /></td>
                  <td style={S.td}><NumCell value={c.payment_gateway_pct} onChange={(v) => patchCh(c.id, { payment_gateway_pct: v })} suffix="%" /></td>
                  <td style={S.td}><NumCell value={c.packaging_cost} onChange={(v) => patchCh(c.id, { packaging_cost: v })} /></td>
                  <td style={S.td}><NumCell value={c.delivery_subsidy} onChange={(v) => patchCh(c.id, { delivery_subsidy: v })} /></td>
                  <td style={S.td}><NumCell value={c.returns_pct} onChange={(v) => patchCh(c.id, { returns_pct: v })} suffix="%" /></td>
                  <td style={S.td}><Flag findings={flagFor(c.id, "gst_pct")} onAccept={onAccept}><NumCell value={c.gst_pct} onChange={(v) => patchCh(c.id, { gst_pct: v })} suffix="%" /></Flag></td>
                  <td style={{ ...S.td, textAlign: "right", fontWeight: 700, color: drag > 25 ? BAD.fg : drag > 12 ? WARN.fg : V("ink", "#e6edf3") }}>
                    {drag.toFixed(1)}%
                  </td>
                  <td style={S.td}><button style={S.btnDel} onClick={() => delCh(c.id)}>&times;</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <button style={{ ...S.btnGhost, marginTop: 11 }} onClick={addChannel}>+ Add channel</button>
    </div>
  );
};

/* ============================================================================
 * TAB :: DIAGNOSTICS
 * ========================================================================== */

const LEVER_LABEL: Record<string, string> = {
  YIELD_RECOVERY: "Cut waste", SUBSTITUTION: "Find alternative", DE_SPECIFICATION: "Reduce over-spec",
  BATCH_ECONOMICS: "Batch size", SUPPLIER_LEVERAGE: "Supplier terms", MAKE_VS_BUY: "Make vs buy",
  CHANNEL_MIX: "Channel mix", PRICE_ARCHITECTURE: "Pricing", OVERHEAD_REDUCTION: "Overheads",
  CONSTRAINT_REALLOCATION: "Capacity mix", PORTFOLIO_PRUNING: "Fix or drop",
};

const DiagnosticsTab: React.FC<{ dx: PortfolioDiagnosis; M: (v: number) => string; goTo: (t: TabKey) => void }> = ({ dx, M, goTo }) => {
  if (!dx.offerings.length) {
    return (
      <div style={S.card}>
        <Empty title="Nothing to analyse yet"
          body="Add what you buy, then what you sell and its recipe. Diagnostics fill in automatically as soon as one product has a recipe and a price."
          action={<button style={S.btn} onClick={() => goTo("inputs")}>Start with what you buy</button>} />
      </div>
    );
  }

  const crit = dx.dataQuality.filter((d) => d.severity === "critical");

  return (
    <>
      <div style={{ display: "flex", gap: 11, flexWrap: "wrap", marginBottom: 14 }}>
        <Tile label="Revenue / month" value={M(dx.monthlyRevenue)} />
        <Tile label="Contribution" value={dx.contributionMarginPct + "%"} sub={M(dx.monthlyContribution) + "/mo"}
          tone={dx.contributionMarginPct >= 35 ? OK.fg : dx.contributionMarginPct > 0 ? WARN.fg : BAD.fg} />
        <Tile label="Operating profit" value={M(dx.monthlyOperatingProfit)} sub={dx.operatingMarginPct + "% margin"}
          tone={dx.monthlyOperatingProfit >= 0 ? OK.fg : BAD.fg} />
        <Tile label="Breakeven" value={M(dx.breakevenRevenue)} sub={"Safety margin " + dx.marginOfSafetyPct + "%"} />
        <Tile label="Savings identified" value={M(dx.totalAnnualOpportunity)} sub="per year" tone={OK.fg} />
      </div>

      {crit.length > 0 && (
        <div style={{ ...S.card, borderColor: BAD.fg }}>
          <div style={{ ...S.cardH, color: BAD.fg }}>Fix these before trusting the numbers</div>
          {crit.slice(0, 6).map((f, i) => (
            <div key={i} style={{ fontSize: 11.5, marginBottom: 5, lineHeight: 1.5 }}>
              <strong style={{ color: BAD.fg }}>{f.field}</strong> &mdash; {f.message}
            </div>
          ))}
        </div>
      )}

      {dx.opportunities.length > 0 && (
        <div style={S.card}>
          <div style={S.cardH}>Where the money is &mdash; ranked by annual value</div>
          {dx.opportunities.slice(0, 8).map((o: Opportunity, i: number) => (
            <div key={i} style={{ padding: "11px 0", borderBottom: i < Math.min(7, dx.opportunities.length - 1) ? `1px solid ${V("faint", "#16202c")}` : "none" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 300px" }}>
                  <div style={{ display: "flex", gap: 7, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                    <Chip tone={NEU}>{LEVER_LABEL[o.lever] || o.lever}</Chip>
                    <Chip tone={o.confidence === "high" ? OK : o.confidence === "medium" ? WARN : BAD}>{o.confidence} confidence</Chip>
                    <span style={{ fontSize: 10, color: V("muted", "#8b98a5") }}>{o.difficulty} effort &middot; ~{o.timeToImpactWeeks} weeks</span>
                  </div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 4 }}>{o.title}</div>
                  <div style={{ ...S.note, marginBottom: 6 }}>{o.rationale}</div>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 10.5, color: V("muted", "#8b98a5"), lineHeight: 1.65 }}>
                    {o.evidence.map((ev, j) => <li key={j}>{ev}</li>)}
                  </ul>
                </div>
                <div style={{ textAlign: "right", minWidth: 100 }}>
                  <div style={{ fontSize: 17, fontWeight: 700, color: OK.fg, letterSpacing: -0.3 }}>{M(o.annualImpact)}</div>
                  <div style={{ fontSize: 9.5, color: V("muted", "#8b98a5") }}>per year</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {dx.benchmarkChecks.some((b) => b.status !== "no_benchmark") && (
        <div style={S.card}>
          <div style={S.cardH}>How you compare to your industry</div>
          <div style={S.scroll}>
            <table style={S.table}>
              <thead><tr>
                <th style={S.th}>Measure</th>
                <th style={{ ...S.th, width: 80, textAlign: "right" }}>You</th>
                <th style={{ ...S.th, width: 120, textAlign: "right" }}>Industry range</th>
                <th style={{ ...S.th, width: 90 }}>Verdict</th>
                <th style={S.th}>Source</th>
              </tr></thead>
              <tbody>
                {dx.benchmarkChecks.filter((b) => b.status !== "no_benchmark").map((b) => (
                  <tr key={b.metricKey}>
                    <td style={S.td}>{b.metricLabel}</td>
                    <td style={{ ...S.td, textAlign: "right", fontWeight: 700 }}>{b.actual}{b.unit}</td>
                    <td style={{ ...S.td, textAlign: "right", color: V("muted", "#8b98a5") }}>
                      {[b.low, b.mid, b.high].filter((x) => x != null).join(" / ")}{b.unit}
                    </td>
                    <td style={S.td}>
                      <Chip tone={b.status === "better" ? OK : b.status === "worse" ? BAD : NEU}>
                        {b.status === "better" ? "Ahead" : b.status === "worse" ? "Behind" : "Normal"}
                      </Chip>
                    </td>
                    <td style={{ ...S.td, fontSize: 10.5, color: V("muted", "#8b98a5") }}>
                      {b.source || "\u2014"}{b.asOf ? " (" + String(b.asOf).slice(0, 4) + ")" : ""}
                      {b.confidence === "low" && <span style={{ color: WARN.fg }}> &middot; indicative only</span>}
                      {b.benchmarkScope === "cross_industry" && <span> &middot; cross-industry</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={S.card}>
        <div style={S.cardH}>Every product, side by side</div>
        <div style={S.scroll}>
          <table style={S.table}>
            <thead><tr>
              <th style={{ ...S.th, width: "20%", minWidth: 150 }}>Product</th>
              <th style={{ ...S.th, width: 92,  textAlign: "right" }}>Price</th>
              <th style={{ ...S.th, width: 112, textAlign: "right" }}>Reaches bank</th>
              <th style={{ ...S.th, width: 108, textAlign: "right" }}>Cost to make</th>
              <th style={{ ...S.th, width: 100, textAlign: "right" }}>Profit/unit</th>
              <th style={{ ...S.th, width: 84,  textAlign: "right" }}>Margin</th>
              <th style={{ ...S.th, width: 118, textAlign: "right" }}>Per bottleneck-min</th>
              <th style={{ ...S.th, width: 110, textAlign: "right" }}>Monthly profit</th>
            </tr></thead>
            <tbody>
              {dx.offerings.map((e) => (
                <tr key={e.offeringId}>
                  <td style={S.td}>
                    <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.name || "(unnamed)"}</div>
                    {e.paretoTop3Labels.length > 0 && (
                      <div style={{ fontSize: 9.5, color: V("muted", "#8b98a5") }}>
                        {e.paretoTop3Labels.slice(0, 2).join(", ")} = {e.paretoTop3SharePct}%
                      </div>
                    )}
                  </td>
                  <td style={{ ...S.td, textAlign: "right" }}>{M(e.listPrice)}</td>
                  <td style={{ ...S.td, textAlign: "right" }}>{M(e.blendedNetRealisation)}</td>
                  <td style={{ ...S.td, textAlign: "right" }}>{M(e.marginalCostPerUnit)}</td>
                  <td style={{ ...S.td, textAlign: "right", fontWeight: 700, color: e.isLossMaking ? BAD.fg : V("ink", "#e6edf3") }}>{M(e.contributionPerUnit)}</td>
                  <td style={{ ...S.td, textAlign: "right" }}>
                    <Chip tone={e.isLossMaking ? BAD : e.contributionMarginPct >= 40 ? OK : WARN}>{e.contributionMarginPct}%</Chip>
                  </td>
                  <td style={{ ...S.td, textAlign: "right", color: V("muted", "#8b98a5") }}>
                    {e.contributionPerConstraintMinute != null ? M(e.contributionPerConstraintMinute) : "\u2014"}
                  </td>
                  <td style={{ ...S.td, textAlign: "right", fontWeight: 600 }}>{M(e.monthlyContribution)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {dx.topResourcesBySpend.length > 0 && (
        <div style={S.card}>
          <div style={S.cardH}>Biggest spends &mdash; annual</div>
          {dx.topResourcesBySpend.slice(0, 10).map((t) => (
            <div key={t.resourceId} style={{ marginBottom: 9 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, marginBottom: 3 }}>
                <span>
                  <strong>{t.name}</strong>
                  <span style={{ color: V("muted", "#8b98a5") }}> &middot; {t.resourceClass.toLowerCase()}</span>
                  {t.yieldPct < 95 && <span style={{ color: WARN.fg }}> &middot; only {t.yieldPct}% usable</span>}
                  {t.supplier && <span style={{ color: V("muted", "#8b98a5") }}> &middot; {t.supplier}</span>}
                </span>
                <span style={{ fontWeight: 700 }}>{M(t.annualSpend)} <span style={{ color: V("muted", "#8b98a5"), fontWeight: 400 }}>{t.sharePct}%</span></span>
              </div>
              <div style={{ height: 4, background: V("faint", "#16202c"), borderRadius: 2, overflow: "hidden" }}>
                <div style={{ height: "100%", width: Math.min(100, t.sharePct) + "%", background: V("accent", "#4ADE80"), opacity: 0.75 }} />
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ ...S.note, textAlign: "center", padding: "6px 0 0" }}>
        Data completeness {dx.completenessScore}% &middot; confidence {dx.confidenceScore}%.
        Every figure is calculated from what you entered &mdash; nothing is estimated or guessed.
      </div>
    </>
  );
};

/* ============================================================================
 * COST WATERFALL — a visual read of Revenue -> Variable Cost -> Fixed Cost
 * -> Operating Profit, each bar stepping down from where the last one
 * ended. Pure SVG, no chart library dependency, reads dx directly so it can
 * never show a number that disagrees with the text elsewhere on this page.
 * ========================================================================== */
const CostWaterfallChart: React.FC<{ dx: PortfolioDiagnosis; M: (v: number) => string }> = ({ dx, M }) => {
  const revenue = Math.max(0, dx.monthlyRevenue);
  const varCost = Math.max(0, dx.monthlyVariableCost);
  const fixedCost = Math.max(0, dx.monthlyFixedCost);
  const profit = dx.monthlyOperatingProfit;

  if (revenue <= 0) return null; // nothing meaningful to draw yet

  const steps = [
    { label: "Revenue", value: revenue, start: 0, end: revenue, color: "#14B8A6" },
    { label: "Variable cost", value: -varCost, start: revenue, end: revenue - varCost, color: "#F59E0B" },
    { label: "Fixed cost", value: -fixedCost, start: revenue - varCost, end: revenue - varCost - fixedCost, color: "#EF4444" },
    { label: "Operating profit", value: profit, start: 0, end: profit, color: profit >= 0 ? "#4ADE80" : "#EF4444", isTotal: true },
  ];

  const maxVal = Math.max(revenue, Math.abs(profit), 1);
  const H = 160, W = 100; // percentage-based, scales to container width
  const barW = W / (steps.length * 1.6);
  const gap = barW * 0.6;
  const toY = (v: number) => H - (v / maxVal) * H;

  return (
    <div style={{ ...S.card, marginTop: 16 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 4 }}>Where the money goes — cost waterfall</div>
      <div style={S.note}>The exact same numbers as above, read as a picture — how revenue steps down through costs to what's actually left.</div>
      <svg viewBox={`0 0 ${W} ${H + 30}`} style={{ width: "100%", height: 220, marginTop: 10 }} preserveAspectRatio="xMidYMid meet">
        {steps.map((s, i) => {
          const x = i * (barW + gap) + gap / 2;
          const top = toY(Math.max(s.start, s.end));
          const bottom = toY(Math.min(s.start, s.end));
          const height = Math.max(1, bottom - top);
          return (
            <g key={i}>
              <rect x={x} y={top} width={barW} height={height} fill={s.color} rx={0.8} />
              <text x={x + barW / 2} y={H + 10} textAnchor="middle" fontSize={3.6} fill="#8b98a5">{s.label}</text>
              <text x={x + barW / 2} y={top - 2.5} textAnchor="middle" fontSize={3.6} fontWeight={700} fill={s.color}>
                {s.isTotal ? M(s.value) : (s.value < 0 ? "−" : "") + M(Math.abs(s.value))}
              </text>
              {i > 0 && i < steps.length && !s.isTotal && (
                <line x1={x - gap / 2} y1={toY(s.start)} x2={x} y2={toY(s.start)} stroke="#5A6480" strokeWidth={0.3} strokeDasharray="1,1" />
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

/* ============================================================================
 * MODULE 17 — TIME VALUE OF MONEY: PV, FV, NPV, IRR.
 * Self-contained: manages its own load and save, so DiagnosticsTab itself
 * needed zero changes to gain this.
 * ========================================================================== */
const InvestmentAnalysisPanel: React.FC<{
  userId: string | null; cur: string; showToast?: (m: string, k?: string) => void;
}> = ({ userId, cur, showToast }) => {
  const [scenarios, setScenarios] = useState<Array<{ id: string; name: string; discount_rate_pct: number; cash_flows: number[] }>>([]);
  const [loaded, setLoaded] = useState(false);
  const M = (v: number) => fmtMoney(v, cur);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      const { data } = await supabase.from("ca_investment_scenarios").select("*").eq("user_id", userId).order("created_at", { ascending: true });
      setScenarios((data as any[]) ?? []);
      setLoaded(true);
    })();
  }, [userId]);

  const addScenario = async () => {
    if (!userId) return;
    const row = { id: uid(), user_id: userId, name: "New investment", discount_rate_pct: 12, cash_flows: [-100000, 30000, 30000, 30000, 30000, 30000] };
    setScenarios((p) => [...p, row]);
    await supabase.from("ca_investment_scenarios").insert(row);
  };
  const patchScenario = (id: string, patch: Partial<{ name: string; discount_rate_pct: number; cash_flows: number[] }>) => {
    setScenarios((p) => p.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    void supabase.from("ca_investment_scenarios").update(patch).eq("id", id);
  };
  const delScenario = (id: string) => {
    setScenarios((p) => p.filter((s) => s.id !== id));
    void supabase.from("ca_investment_scenarios").delete().eq("id", id);
  };

  if (!loaded) return null;

  return (
    <div style={{ ...S.card, marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700 }}>Is a big investment actually worth it? — Module 17</div>
        <button onClick={addScenario} style={S.btnGhost}>+ Add an investment to check</button>
      </div>
      <div style={S.note}>
        Enter what you'd spend now and what you expect back over the following years. This tells you whether it's
        genuinely worth it once you account for the fact that money today is worth more than the same money later.
      </div>

      {scenarios.map((sc) => {
        const npv = netPresentValue(sc.cash_flows, sc.discount_rate_pct);
        const irr = internalRateOfReturn(sc.cash_flows);
        return (
          <div key={sc.id} style={{ marginTop: 12, paddingTop: 12, borderTop: "1px dashed " + V("border", "#232838") }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <TextCell value={sc.name} onChange={(v) => patchScenario(sc.id, { name: v })} />
              <div><label style={S.lbl}>Your cost of capital</label>
                <NumCell value={sc.discount_rate_pct} onChange={(v) => patchScenario(sc.id, { discount_rate_pct: v ?? 12 })} suffix="%" /></div>
              <button onClick={() => delScenario(sc.id)} style={{ background: "none", border: "none", color: BAD.fg, cursor: "pointer", fontSize: 13 }}>{"\u2715"}</button>
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
              {sc.cash_flows.map((cf, i) => (
                <div key={i}><label style={S.lbl}>{i === 0 ? "Now" : "Year " + i}</label>
                  <NumCell value={cf} onChange={(v) => {
                    const next = [...sc.cash_flows]; next[i] = v ?? 0; patchScenario(sc.id, { cash_flows: next });
                  }} /></div>
              ))}
              <button onClick={() => patchScenario(sc.id, { cash_flows: [...sc.cash_flows, 0] })}
                style={{ ...S.btnGhost, alignSelf: "flex-end" }}>+ year</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginTop: 10 }}>
              <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Net present value</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: npv >= 0 ? OK.fg : BAD.fg }}>{M(npv)}</div></div>
              <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Real rate of return</div>
                <div style={{ fontSize: 15, fontWeight: 800 }}>{irr.irrPct != null ? irr.irrPct.toFixed(1) + "%" : "Not calculable"}</div></div>
            </div>
            <div style={{ fontSize: 9.5, color: V("muted", "#8b98a5"), marginTop: 6 }}>
              {npv >= 0
                ? "Positive — this is expected to be worth more than what you put in, even after accounting for the time value of money."
                : "Negative — on these numbers, this investment is expected to cost you more than it returns."}
            </div>
          </div>
        );
      })}
      {!scenarios.length && <div style={{ fontSize: 10, color: V("muted", "#8b98a5"), fontStyle: "italic", marginTop: 8 }}>No investments checked yet.</div>}
    </div>
  );
};

/* ============================================================================
 * MODULE 18 — SCENARIOS & WHAT-IF PANEL.
 * Reuses ca_scenarios, a table that already existed with exactly the right
 * shape (assumptions jsonb, computed_snapshot jsonb) - zero schema changes
 * needed. Price and volume changes are applied multiplicatively, matching
 * how they actually interact, not added together as if independent.
 * ========================================================================== */
const ScenarioPanel: React.FC<{
  userId: string | null; cur: string; dx: PortfolioDiagnosis; showToast?: (m: string, k?: string) => void;
}> = ({ userId, cur, dx, showToast }) => {
  const [scenarios, setScenarios] = useState<Array<{ id: string; name: string; scenario_type: string; assumptions: any }>>([]);
  const [loaded, setLoaded] = useState(false);
  const M = (v: number) => fmtMoney(v, cur);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      const { data } = await supabase.from("ca_scenarios").select("*").eq("user_id", userId).order("created_at", { ascending: true });
      setScenarios((data as any[]) ?? []);
      setLoaded(true);
    })();
  }, [userId]);

  const addScenario = async () => {
    if (!userId) return;
    const row = { id: uid(), user_id: userId, name: "What if...", scenario_type: "custom",
      assumptions: { priceChangePct: 0, volumeChangePct: 0, variableCostChangePct: 0, fixedCostChangePct: 0 } };
    setScenarios((p) => [...p, row]);
    await supabase.from("ca_scenarios").insert(row);
  };
  const patchScenario = (id: string, assumptionsPatch: any, namePatch?: string) => {
    setScenarios((p) => p.map((s) => (s.id === id ? { ...s, name: namePatch ?? s.name, assumptions: { ...s.assumptions, ...assumptionsPatch } } : s)));
    const updated = scenarios.find((s) => s.id === id);
    void supabase.from("ca_scenarios").update({ name: namePatch ?? updated?.name, assumptions: { ...updated?.assumptions, ...assumptionsPatch } }).eq("id", id);
  };
  const delScenario = (id: string) => {
    setScenarios((p) => p.filter((s) => s.id !== id));
    void supabase.from("ca_scenarios").delete().eq("id", id);
  };

  if (!loaded) return null;

  return (
    <div style={{ ...S.card, marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700 }}>What if things changed? — Module 18</div>
        <button onClick={addScenario} style={S.btnGhost}>+ Add a scenario</button>
      </div>
      <div style={S.note}>
        Try a change without actually touching your real numbers — a price rise, a cost increase, a drop in sales — and see what it would do to your profit before it happens.
      </div>

      {scenarios.map((sc) => {
        const result = runScenario(
          { monthlyRevenue: dx.monthlyRevenue, monthlyVariableCost: dx.monthlyVariableCost, monthlyFixedCost: dx.monthlyFixedCost },
          sc.assumptions
        );
        return (
          <div key={sc.id} style={{ marginTop: 12, paddingTop: 12, borderTop: "1px dashed " + V("border", "#232838") }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <TextCell value={sc.name} onChange={(v) => patchScenario(sc.id, {}, v)} />
              <button onClick={() => delScenario(sc.id)} style={{ background: "none", border: "none", color: BAD.fg, cursor: "pointer", fontSize: 13 }}>{"\u2715"}</button>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
              <div><label style={S.lbl}>Price change</label>
                <NumCell value={sc.assumptions.priceChangePct} onChange={(v) => patchScenario(sc.id, { priceChangePct: v ?? 0 })} suffix="%" /></div>
              <div><label style={S.lbl}>Volume change</label>
                <NumCell value={sc.assumptions.volumeChangePct} onChange={(v) => patchScenario(sc.id, { volumeChangePct: v ?? 0 })} suffix="%" /></div>
              <div><label style={S.lbl}>Cost change</label>
                <NumCell value={sc.assumptions.variableCostChangePct} onChange={(v) => patchScenario(sc.id, { variableCostChangePct: v ?? 0 })} suffix="%" /></div>
              <div><label style={S.lbl}>Fixed cost change</label>
                <NumCell value={sc.assumptions.fixedCostChangePct} onChange={(v) => patchScenario(sc.id, { fixedCostChangePct: v ?? 0 })} suffix="%" /></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
              <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Profit today</div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{M(result.base.operatingProfit)}</div></div>
              <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Profit under this scenario</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: result.profitChangeAbsolute >= 0 ? OK.fg : BAD.fg }}>{M(result.projected.operatingProfit)}</div></div>
            </div>
            <div style={{ fontSize: 10, marginTop: 6, color: result.profitChangeAbsolute >= 0 ? OK.fg : BAD.fg }}>
              {result.profitChangeAbsolute >= 0 ? "+" : ""}{M(result.profitChangeAbsolute)} per month
              {result.profitChangePct != null && ` (${result.profitChangePct >= 0 ? "+" : ""}${result.profitChangePct.toFixed(0)}%)`}
            </div>
          </div>
        );
      })}
      {!scenarios.length && <div style={{ fontSize: 10, color: V("muted", "#8b98a5"), fontStyle: "italic", marginTop: 8 }}>No scenarios yet.</div>}
    </div>
  );
};

/* ============================================================================
 * MODULE 20 — REPORTS & EXPORT: EXECUTIVE SUMMARY.
 * Uses the browser's own native print function - genuinely reliable, no new
 * dependencies, and it already correctly handles "save as PDF" on every
 * modern browser's print dialog. @media print rules hide everything else on
 * the page so only this clean summary prints or saves.
 * ========================================================================== */
const ExecutiveSummaryPanel: React.FC<{
  dx: PortfolioDiagnosis; M: (v: number) => string; companyName?: string; ctx: CaBusinessContext | null;
}> = ({ dx, M, companyName, ctx }) => {
  return (
    <div style={{ ...S.card, marginTop: 16 }} id="oiq-executive-summary">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #oiq-executive-summary, #oiq-executive-summary * { visibility: visible; }
          #oiq-executive-summary { position: absolute; top: 0; left: 0; width: 100%; }
        }
      `}</style>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700 }}>Executive summary — Module 20</div>
        <button onClick={() => window.print()} style={S.btnGhost}>Print / Save as PDF</button>
      </div>
      <div style={S.note}>A clean summary of your whole business, ready to print or save as a PDF from your browser's own print dialog.</div>

      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 800 }}>{companyName || "Your Business"}</div>
        <div style={{ fontSize: 10, color: V("muted", "#8b98a5") }}>{ctx?.industry_label || ""} {ctx?.geography ? "\u00B7 " + ctx.geography : ""} \u00B7 Generated {new Date().toLocaleDateString()}</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginTop: 14 }}>
        <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Monthly revenue</div><div style={{ fontSize: 16, fontWeight: 800 }}>{M(dx.monthlyRevenue)}</div></div>
        <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Fixed cost</div><div style={{ fontSize: 16, fontWeight: 800 }}>{M(dx.monthlyFixedCost)}</div></div>
        <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Variable cost</div><div style={{ fontSize: 16, fontWeight: 800 }}>{M(dx.monthlyVariableCost)}</div></div>
        <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Operating profit</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: dx.monthlyOperatingProfit >= 0 ? OK.fg : BAD.fg }}>{M(dx.monthlyOperatingProfit)}</div></div>
        <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Contribution margin</div><div style={{ fontSize: 16, fontWeight: 800 }}>{dx.contributionMarginPct?.toFixed(1)}%</div></div>
        <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Break-even revenue</div><div style={{ fontSize: 16, fontWeight: 800 }}>{M(dx.breakevenRevenue)}</div></div>
        <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Confidence score</div><div style={{ fontSize: 16, fontWeight: 800 }}>{dx.confidenceScore}%</div></div>
        <div><div style={{ fontSize: 9, color: V("muted", "#8b98a5"), textTransform: "uppercase" }}>Savings identified</div><div style={{ fontSize: 16, fontWeight: 800, color: OK.fg }}>{M(dx.totalAnnualOpportunity)}/yr</div></div>
      </div>

      {dx.opportunities?.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>Top opportunities</div>
          {dx.opportunities.slice(0, 5).map((o: Opportunity, i: number) => (
            <div key={i} style={{ fontSize: 10.5, padding: "4px 0", color: V("muted", "#8b98a5") }}>{i + 1}. {o.title} — {M(o.annualImpact)}/yr</div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ============================================================================
 * TAB :: START HERE
 * Describe the business in plain words. The system researches it, drafts the
 * whole cost model, and hands back a checklist to verify - instead of a blank
 * form that assumes the user is already an expert in this industry.
 * ========================================================================== */

const VERIFY_STYLE: Record<Verify, { tone: { bg: string; fg: string }; label: string }> = {
  confirmed:    { tone: OK,   label: "Researched" },
  needs_check:  { tone: WARN, label: "Check this" },
  must_supply:  { tone: BAD,  label: "You must enter" },
};

const EXAMPLES = [
  "A bakery in Lucknow making birthday cakes, cookies and multigrain bread. Sells walk-in and on Swiggy and Zomato.",
  "A 40-seat casual dining restaurant in Pune serving North Indian food, about half the orders come from delivery apps.",
  "A steel re-rolling mill in Ghaziabad buying HRC coil and making angles and channels for local fabricators.",
  "A 6-person management consulting firm in Bengaluru doing market entry projects and monthly advisory retainers.",
  "A D2C skincare brand selling on our own website and Amazon, we get the products made by a third-party manufacturer.",
];

/* ============================================================================
 * "EXPLORE THE MODEL" — the full 20-module map, matching the reference
 * design directly and numbered the same way. Every entry is honest about
 * its real state: a BUILT module navigates to exactly where that work
 * actually lives today (several modules are real but currently live nested
 * inside an existing tab, not yet split into their own dedicated screen -
 * that is named explicitly rather than implied to be something it isn't).
 * A module not yet built is visibly greyed out and says so when clicked,
 * rather than being hidden or silently doing nothing.
 * ========================================================================== */
interface ModuleMapEntry {
  n: string; title: string; desc: string; icon: string;
  target: TabKey | null;   // null = not built yet
  livesIn?: string;        // shown when the module is real but nested inside another tab
}
// THE FIX: every single one of these now says what you'll be able to DO or
// KNOW once it's filled in - in words anyone can understand, whether or not
// they've ever run a business before. No term is used without saying what
// it means in plain English right there in the same sentence.
const MODULE_MAP: ModuleMapEntry[] = [
  { n:"01", title:"Project Setup", desc:"A few basic facts so every number elsewhere gets calculated correctly for YOUR business.", icon:"\uD83D\uDCC4", target:"setup" },
  { n:"02", title:"AI Discovery", desc:"Tell us what you're making or selling - we'll find real costs and fill in the rest for you.", icon:"\u2728", target:"start" },
  { n:"03", title:"Cost Components", desc:"See every single thing you spend money on, listed out clearly.", icon:"\uD83D\uDCE6", target:"inputs" },
  { n:"04", title:"Resources & Workforce", desc:"Find out what an employee or worker really costs you per hour, once everything is accounted for.", icon:"\uD83D\uDC65", target:"inputs", livesIn:"open a Labour resource in What You Buy" },
  { n:"05", title:"Activities & Cost Drivers", desc:"See exactly which step of your process costs the most money.", icon:"\uD83D\uDCCB", target:"products", livesIn:"open a product in What You Sell" },
  { n:"06", title:"Capacity & Yield", desc:"Understand how much you lose to waste, and what that's really costing you.", icon:"\u2699\uFE0F", target:"inputs", livesIn:"each resource's yield % in What You Buy" },
  { n:"07", title:"Cost Allocation", desc:"See exactly which of your bills (rent, salaries) are really being paid for by which product.", icon:"\uD83D\uDD00", target:"products", livesIn:"open a product in What You Sell" },
  { n:"08", title:"Procurement & Suppliers", desc:"Compare suppliers side by side to see who actually costs you less once everything is included.", icon:"\uD83D\uDED2", target:"inputs", livesIn:"open a resource in What You Buy" },
  { n:"09", title:"CAPEX & OPEX", desc:"Work out the true cost of a big one-time purchase, spread fairly over the years you'll use it.", icon:"\uD83C\uDFE2", target:"setup", livesIn:"Setup tab, below Fixed Costs" },
  { n:"10", title:"Working Capital", desc:"Find out how much cash you need sitting in the bank just to keep the business running.", icon:"\uD83D\uDCB0", target:"setup", livesIn:"Setup tab, below CAPEX" },
  { n:"11", title:"Unit Economics", desc:"See the exact profit you make (or lose) on a single unit sold.", icon:"\uD83D\uDCCA", target:"products" },
  { n:"12", title:"Customer Economics", desc:"Find out if a typical customer is worth more than what it costs you to win them.", icon:"\uD83D\uDC64", target:"products", livesIn:"open a product in What You Sell" },
  { n:"13", title:"Marketing & Sales", desc:"See what it actually costs to find and win one new customer.", icon:"\uD83D\uDCE2", target:"channels" },
  { n:"14", title:"Pricing Engine", desc:"Get told exactly what price to charge to hit the profit margin you want.", icon:"\uD83C\uDFF7\uFE0F", target:"products", livesIn:"open a product in What You Sell" },
  { n:"15", title:"Break-even Analysis", desc:"Find the exact number you need to sell before you stop losing money and start earning.", icon:"\uD83D\uDCC8", target:"products", livesIn:"open a product in What You Sell" },
  { n:"16", title:"Profitability & ROI", desc:"See the full picture of whether the business actually makes money overall.", icon:"\uD83D\uDCC8", target:"diagnostics" },
  { n:"17", title:"Time Value of Money", desc:"See what a big future cost or return is really worth in today's money.", icon:"\u23F1\uFE0F", target:"diagnostics", livesIn:"Diagnostics tab" },
  { n:"18", title:"Scenarios & What-If", desc:"See what happens to your profit if prices, costs, or sales change - before it actually happens.", icon:"\uD83D\uDD00", target:"diagnostics", livesIn:"Diagnostics tab" },
  { n:"19", title:"Optimization", desc:"Get concrete suggestions on where you can cut cost or improve profit right now.", icon:"\uD83C\uDFAF", target:"diagnostics", livesIn:"Diagnostics tab" },
  { n:"20", title:"Reports & Export", desc:"Get a clean summary of everything, ready to share or print.", icon:"\uD83D\uDCC4", target:"diagnostics", livesIn:"Diagnostics tab" },
];

// ============================================================================
// THE SOP — written by having actually built and personally verified every
// one of these 20 modules, with the exact real pitfalls found along the way,
// not a generic description of what each one "should" do.
// ============================================================================
interface SopEntry { n: string; title: string; concept: string; steps: string[]; pitfall: string; troubleshoot: string; }
// THE FIX: every entry below now leads with the actual underlying concept and
// the EXACT formula this platform calculates with — not a rewording of the
// button labels. These are the real formulas from the engine files actually
// written and numerically verified against known answers earlier in this
// project — copied from that source, not reconstructed from memory.
const SOP_CONTENT: SopEntry[] = [
  { n: "01", title: "Project Setup",
    concept: "This isn't a calculation — it's the context every other formula on this page depends on. Your industry, location, and currency don't get computed from anything; they're the inputs that make every OTHER formula here apply to YOUR business specifically, rather than a generic template.",
    steps: ["Fill in your industry, location, and currency here first, before entering any costs.", "Every price and calculation elsewhere on this page assumes this is set correctly for YOUR business."],
    pitfall: "Changing currency later does NOT convert numbers you already entered — ₹500 typed under INR stays \"500\" if you switch to USD. Set currency right at the very start.",
    troubleshoot: "If numbers look wrong everywhere, check this tab first — a wrong industry or currency here quietly throws off everything downstream." },

  { n: "02", title: "AI Discovery (Start Here)",
    concept: "Not a formula — a research step. The AI is asked to find REAL current prices, typical wastage %, and typical channel fees for a business like yours, then structure them into the same data model every other module reads. Every discovered number is tagged: \"confirmed\" (AI is confident), \"needs_check\" (AI estimated), or \"must_supply\" (AI genuinely couldn't find a number) — this tag is now saved permanently as the \"AI ESTIMATE\" / \"NEEDS YOUR NUMBER\" badges you see later, not discarded after the first look.",
    steps: ["Describe your business in plain words — what you make or sell, roughly where, and how you sell it (shop, online, wholesale).", "Press \"Research and build my cost model\" — it saves automatically the moment it finishes, no second click needed.", "Review the categorized results that appear — delete anything that doesn't apply to your business."],
    pitfall: "Vague descriptions get vague results. \"I sell food\" produces a weak, generic model. \"I make 500g jars of mango pickle, sold through local shops and Instagram\" produces a real, specific one.",
    troubleshoot: "If it says a generic placeholder template was used instead of real research, that means every AI attempt failed for this description — try rephrasing more specifically, or try again; a 45-second limit per attempt means it won't hang forever." },

  { n: "03", title: "Cost Components (What You Buy)",
    concept: "THE FORMULA: Effective Cost per Unit = (Purchase Price + Freight + Duty + Other Landed Cost − Input Tax Credit − Scrap Recovery Value) ÷ (Purchase Quantity × Conversion Factor × Yield%). This is called \"landed cost\" — everything it actually costs you to get one usable unit into your hands, not the sticker price alone. The \"Yield%\" divides at the end specifically because waste means you pay for more than you actually use.",
    steps: ["Review every material, ingredient, or supply the AI found.", "Add anything missing with \"+ Add input\".", "Check the \"Usable %\" (yield) on each one — this matters more than people expect."],
    pitfall: "An \"AI ESTIMATE\" or \"NEEDS YOUR NUMBER\" badge next to an item's name means exactly what it says — check that price against reality. Editing the price clears the badge automatically once you've corrected it.",
    troubleshoot: "If a cost looks too low, check whether freight, duty, or other landed costs are filled in — the \"sticker price\" alone is rarely the true cost." },

  { n: "04", title: "Resources & Workforce",
    concept: "THE FORMULA, a waterfall: Gross Hours/Year = Scheduled Hours/Day × Working Days/Year. Net Available = Gross − (Holidays+Leave+Sick+Training days × Hours/Day). Productive Hours = Net Available × (1 − Meetings/Admin%). Effective Hourly Cost = (Annual Salary × (1 + Employer Add-on%)) ÷ Productive Hours. This exists because Salary ÷ Scheduled Hours understates true cost — it ignores that paid time isn't all productive time.",
    steps: ["Open any resource marked \"Labour\".", "Fill in scheduled hours, leave, and how much of paid time is actually productive.", "The Activity Cost calculator at the bottom lets you cost a specific task — e.g. \"100 units × 6 minutes each\"."],
    pitfall: "The \"effective hourly cost\" shown will be noticeably HIGHER than salary ÷ scheduled hours. That's correct, not a bug — it accounts for leave, meetings, and admin time that salary-only math ignores.",
    troubleshoot: "If the effective cost seems too high, check the \"meetings + admin\" and \"utilization\" percentages — small changes there move this number a lot." },

  { n: "05", title: "Activities & Cost Drivers",
    concept: "THE FORMULA: for every ingredient/resource line tagged to a process step (Mixing, Baking, Packaging...), Line Cost = Effective Cost per Unit × Quantity per Unit × (1 ÷ (1 − Scrap%)). All lines sharing the same step name are summed into that step's total, and each step's share of the whole product's cost is shown as a percentage. This exists to answer \"which part of my process actually costs the most\" — not just \"what's my total cost.\"",
    steps: ["Open a product in What You Sell.", "The \"Cost by process step\" section groups costs by the production step each ingredient is tagged to."],
    pitfall: "This only groups correctly if each input has a step name filled in (Mixing, Baking, Packaging, etc.). Untagged items land in \"Unassigned\".",
    troubleshoot: "If everything shows as one lump under \"Unassigned\", go back to What You Buy and check the step name on each BOM line." },

  { n: "06", title: "Capacity & Yield",
    concept: "THE FORMULA: this compares Naive Cost (Purchase Price ÷ Quantity, ignoring waste) against Effective Cost (the same, divided again by Yield%). The gap between the two IS the hidden cost of waste — a real rupee number, not an abstract percentage. If you buy 1kg at ₹100 with 80% yield, effective cost per usable kg is ₹125, not ₹100.",
    steps: ["On each resource in What You Buy, set the realistic \"Usable %\" — how much of what you buy actually ends up in the finished product."],
    pitfall: "100% yield is almost never realistic. Trimming, spoilage, and rejects are normal — leaving this at 100% quietly understates your true cost.",
    troubleshoot: "If your break-even number seems optimistic, this is the first place to check — a too-generous yield number is the most common cause." },

  { n: "07", title: "Cost Allocation",
    concept: "THE FORMULA depends on the pool's chosen \"basis\": revenue-basis gives each product (Its Own Monthly Revenue ÷ Total Portfolio Revenue) × Pool Amount; units-basis uses Volume instead of Revenue; direct-cost-basis uses each product's own direct cost as the share; equal-basis simply divides the pool evenly across every product. This exists because a shared bill (rent, one manager's salary) has to be split across everything it supports SOMEHOW — the basis you choose decides HOW fairly, and different bases genuinely give different answers.",
    steps: ["Open a product in What You Sell.", "\"Where your overhead allocation comes from\" shows exactly which fixed cost pool contributed how much to this specific product."],
    pitfall: "Pools that allocate by labour, machine, or constraint hours show \"not shown\" here — that data isn't tracked yet, stated honestly rather than guessed. The total elsewhere still uses the correct number.",
    troubleshoot: "If a pool's allocation looks too high or low for a product, check its \"basis\" — revenue-based allocation gives more to your best-selling products, which is sometimes not what you want." },

  { n: "08", title: "Procurement & Suppliers",
    concept: "Uses the IDENTICAL landed-cost formula as Module 03 (Purchase Price + Freight + Duty + Other Landed Cost, divided by usable quantity) — applied once for your current supplier and once for each alternative you add, so every row is compared on exactly the same basis. The cheapest true landed cost is flagged automatically, not just the cheapest sticker price.",
    steps: ["Open any resource.", "\"Compare suppliers\" — add an alternative supplier's price, freight, and duty.", "The cheapest option is flagged automatically."],
    pitfall: "MOQ (minimum order quantity) and lead time are captured but not factored into the cost comparison — a cheaper supplier with a huge minimum order or a 3-month wait might not actually be practical.",
    troubleshoot: "If \"cheapest\" doesn't match your gut feeling, double check you've entered ALL landed costs (freight, duty) for the alternative, not just the sticker price." },

  { n: "09", title: "CAPEX & OPEX",
    concept: "THE FORMULA — straight-line depreciation, the simplest and most common method: Monthly Depreciation = (Purchase Cost − Salvage Value) ÷ Useful Life in Months. Current Book Value = Purchase Cost − (Monthly Depreciation × Months Since Purchase). This exists because a big one-time purchase shouldn't make ONE month look terrible and every other month look artificially better — spreading it evenly reflects that the machine helps you every month it's used, not just the month you bought it.",
    steps: ["In Setup, scroll to \"Big one-time purchases\".", "Enter what you paid, what it'll be worth at the end, and how many years you'll use it.", "It automatically adds a matching monthly cost to your Fixed Costs — nothing else to do."],
    pitfall: "Do not also manually add the same machine as a separate fixed cost — that double-counts it. Its monthly depreciation already appears automatically.",
    troubleshoot: "If fixed costs look too high, check Fixed Costs in Setup for a duplicate \"Depreciation — [item]\" entry alongside a manually-added one for the same thing." },

  { n: "10", title: "Working Capital",
    concept: "THE FORMULA: Cash Conversion Cycle (days) = Inventory Days + Receivables Days − Payables Days. Working Capital Required (₹) = (Daily Cost of Goods Sold × Inventory Days) + (Daily Revenue × Receivables Days) − (Daily Cost of Goods Sold × Payables Days). This exists because a profitable business can still run out of CASH if money is stuck in unsold stock or unpaid customer invoices longer than your own suppliers give you credit for.",
    steps: ["In Setup, below CAPEX, enter how many days of stock you hold, how long customers take to pay, and how long you take to pay suppliers."],
    pitfall: "This needs real revenue and cost data from Start Here to convert days into an actual rupee figure — filled in before any sales data exists, it will show ₹0.",
    troubleshoot: "If the cash-required number seems too low, make sure What You Sell actually has volume and price filled in for your products." },

  { n: "11", title: "Unit Economics",
    concept: "THE FORMULA: Contribution per Unit = Selling Price − Variable Cost per Unit. This is the money left from ONE sale after only the costs that scale with that sale — it deliberately excludes fixed costs like rent, because contribution answers \"does making one more actually help\" rather than \"is the whole business profitable.\"",
    steps: ["The \"Cost breakdown at a glance\" card on Start Here, and each product's own numbers in What You Sell, show this automatically once data exists."],
    pitfall: "None significant — this is a straightforward read of already-verified numbers.",
    troubleshoot: "If a product's cost per unit looks wrong, check its BOM (recipe) in What You Sell for a missing or duplicated ingredient line." },

  { n: "12", title: "Customer Economics",
    concept: "THE FORMULA: Lifetime Value (LTV) = Contribution per Unit × Expected Customer Lifetime in Months (or, if lifetime isn't given directly, derived from churn as 100 ÷ Monthly Churn%). LTV:CAC Ratio = LTV ÷ Cost to Acquire a Customer. Payback Period (months) = CAC ÷ Contribution per Unit. A ratio of 3x or higher is a widely-used rule of thumb for a healthy business — below that, you're spending close to (or more than) what a customer is actually worth.",
    steps: ["Open a product in What You Sell.", "Fill in monthly churn %, cost to win a customer, and expected customer lifetime.", "The LTV:CAC ratio appears automatically."],
    pitfall: "This section is hidden for products marked \"Sub-Assembly\" — those aren't sold directly to an end customer, so customer economics genuinely don't apply.",
    troubleshoot: "If the ratio seems off, double check \"expected customer lifetime\" — a small change here has an outsized effect on the result." },

  { n: "13", title: "Marketing & Sales",
    concept: "THE FORMULA: Net Realisation per Sale = Gross Price × (1 − Commission% − Discount% − Ad Spend% − Payment Gateway%) − Packaging Cost − Delivery Subsidy. This is what actually reaches you after every fee a sales channel takes — the \"leakage\" between your listed price and your real income.",
    steps: ["In Where You Sell, add each channel you sell through.", "Fill in commission %, ad spend %, and any other fees each one takes."],
    pitfall: "Leaving fields blank isn't the same as zero — an unfilled ad spend % will understate how much a channel actually costs you.",
    troubleshoot: "If \"what you actually keep\" seems too high for a marketplace channel, check every fee field is filled in, not just commission." },

  { n: "14", title: "Pricing Engine",
    concept: "THE FORMULA: Price for a Target Margin = Fully Loaded Cost per Unit ÷ (1 − Target Margin%). This is solved algebraically from the definition of margin (Margin% = (Price − Cost) ÷ Price) so the price you get back genuinely produces the margin you asked for — not an approximation.",
    steps: ["Open a product, set a \"Target Margin %\".", "A suggested price appears automatically, compared against your current price."],
    pitfall: "The suggested price is based on FULLY LOADED cost — including allocated overhead — so it can be noticeably higher than a simple \"cost plus a bit\" price you might expect.",
    troubleshoot: "If the suggested price seems too high, check Cost Allocation (Module 07) for this product — a large overhead share could be the reason." },

  { n: "15", title: "Break-even Analysis",
    concept: "THE FORMULA: Break-even Units = Fixed Cost Allocated to This Product ÷ Contribution per Unit. Break-even Revenue = Break-even Units × Net Selling Price. This is the exact point where total contribution equals the fixed cost this product carries — sell fewer than this and the product is losing money once its fair share of overhead is counted, even if it looks fine on a simple per-unit basis.",
    steps: ["Appears automatically on each product once cost, price, and volume are filled in — no separate action needed."],
    pitfall: "This is PER PRODUCT. Your overall business break-even (in Diagnostics) can differ — a product can be above its own break-even while the business overall isn't, or vice versa.",
    troubleshoot: "If a product shows red (below its own break-even) but your business looks fine overall, other products are currently subsidizing this one." },

  { n: "16", title: "Profitability & ROI",
    concept: "THE FORMULA: Operating Profit = Total Contribution (Revenue − Variable Cost) − Total Fixed Cost. Operating Margin% = Operating Profit ÷ Revenue. This is the actual bottom line for the whole business, built by summing every product's contribution and subtracting every fixed cost pool — the same numbers you've already entered elsewhere, combined once at the portfolio level.",
    steps: ["Check the Diagnostics tab for the overall profit, margin, and confidence picture of your whole business."],
    pitfall: "None significant.",
    troubleshoot: "If the confidence score is low, check for resources or products still marked \"NEEDS YOUR NUMBER\" — those pull the confidence score down." },

  { n: "17", title: "Time Value of Money",
    concept: "THE FORMULAS: Present Value = Future Value ÷ (1 + rate)^periods — what a future rupee is worth today. Net Present Value (NPV) = the sum of every cash flow, each divided by (1 + rate) raised to its own year number — year 0 (usually your initial spend, negative) counts fully, later years count for progressively less. Internal Rate of Return (IRR) is the exact discount rate at which NPV becomes zero — found by trying rates between −99% and 1000% and narrowing in (a method called bisection) until NPV is close enough to zero. This exists because ₹1,00,000 received in 5 years is genuinely worth less than ₹1,00,000 today — these formulas make that comparison honest.",
    steps: ["In Diagnostics, add an investment to check.", "Enter what you'd spend now (as a negative number) and what you expect back each following year.", "NPV and the real rate of return (IRR) appear automatically."],
    pitfall: "IRR shows \"Not calculable\" when your cash flows never actually turn from negative to positive (or vice versa) — that's mathematically correct, not a bug. Every real investment needs at least one sign change.",
    troubleshoot: "If NPV is negative but you expected a good investment, double check your \"cost of capital\" percentage — too high a rate will make almost anything look unprofitable on paper." },

  { n: "18", title: "Scenarios & What-If",
    concept: "THE FORMULA: Projected Revenue = Base Revenue × (1 + Price Change%) × (1 + Volume Change%) — multiplied together, not added, because raising price AND changing volume both affect revenue at the same time. Projected Variable Cost = Base Variable Cost × (1 + Volume Change%) × (1 + Cost Change%) — cost scales with volume too. Projected Fixed Cost = Base Fixed Cost × (1 + Fixed Cost Change%). This exists to let you test a real decision — a price rise, a cost increase — against your ACTUAL current numbers, before committing to it.",
    steps: ["In Diagnostics, add a scenario.", "Adjust price, volume, cost, or fixed cost percentages to see the effect on profit — without touching your real numbers."],
    pitfall: "This is a sandbox only — it never changes your actual saved data, no matter what you set the sliders to. To make a change real, edit the actual value in its own tile.",
    troubleshoot: "If the projected profit doesn't match your own mental math, remember price and volume changes compound together (multiply), not add." },

  { n: "19", title: "Optimization",
    concept: "Not a single formula — this scans your actual data for specific, named patterns worth acting on (for example: overhead running well above a typical benchmark for your industry, or a product selling below its own break-even) and estimates the annual rupee impact of fixing each one, then ranks them by that impact. Every suggestion traces back to real numbers already on this page, not a generic tip.",
    steps: ["Check Diagnostics for \"Savings identified\" and the ranked list of opportunities below it — this is generated automatically from your data."],
    pitfall: "None significant.",
    troubleshoot: "If no opportunities appear, your model may not have enough data yet — add more resources, products, or channels for it to analyze." },

  { n: "20", title: "Reports & Export",
    concept: "No formula — a clean read-out of numbers already calculated everywhere else on this page, laid out for printing or saving as a PDF via your browser's own print function.",
    steps: ["In Diagnostics, find \"Executive summary\".", "Press \"Print / Save as PDF\" — this uses your browser's own print dialog.", "Choose \"Save as PDF\" as the destination instead of a physical printer if you want a file."],
    pitfall: "Every browser's print dialog looks slightly different — the important part is picking \"Save as PDF\" (or similar) rather than an actual printer, if you want a file rather than a paper copy.",
    troubleshoot: "If the printed page looks wrong, only the summary itself is meant to print — everything else on the page is intentionally hidden during printing." },
];

const SOPDrawer: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [openN, setOpenN] = useState<string | null>(null);
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 400, background: "rgba(5,7,12,0.78)", display: "flex", justifyContent: "flex-end" }} onClick={onClose}>
      <div style={{ width: "min(760px, 95vw)", height: "100%", background: V("bg", "#0d1117"), borderLeft: "1px solid " + V("border", "#232838"), overflowY: "auto", padding: "18px 22px" }} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: "#14B8A6", fontSize: 12.5, fontWeight: 800, marginBottom: 16, padding: 0 }}>
          {"\u2190"} Back to Cost Architect
        </button>
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>How to use Cost Architect</div>
        <div style={{ fontSize: 11, color: V("muted", "#8b98a5"), marginBottom: 18, lineHeight: 1.6 }}>
          Written for someone who has never run a cost model before. Start with Module 01 and 02 in order — everything else builds on what you enter there.
          Click any module below to expand its steps, its one real pitfall to watch for, and what to do if something looks wrong.
        </div>

        {SOP_CONTENT.map((s) => {
          const isOpen = openN === s.n;
          return (
            <div key={s.n} style={{ marginBottom: 8, border: "1px solid " + V("border", "#232838"), borderRadius: 8, overflow: "hidden" }}>
              <button onClick={() => setOpenN(isOpen ? null : s.n)}
                style={{ width: "100%", textAlign: "left", padding: "10px 14px", background: isOpen ? "rgba(20,184,166,0.06)" : "transparent", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: V("ink", "#e6edf3") }}>{s.n}. {s.title}</span>
                <span style={{ fontSize: 12, color: "#14B8A6" }}>{isOpen ? "\u2212" : "+"}</span>
              </button>
              {isOpen && (
                <div style={{ padding: "4px 14px 14px" }}>
                  {/* THE FIX: the actual concept and formula, first and most
                      prominent — so the underlying calculation can be judged
                      independently of whether the screen "looks right". */}
                  <div style={{ marginTop: 8, padding: "10px 12px", background: "rgba(20,184,166,0.05)", borderRadius: 6, border: "1px solid rgba(20,184,166,0.25)" }}>
                    <div style={{ fontSize: 9, fontWeight: 800, color: "#14B8A6", textTransform: "uppercase", marginBottom: 4 }}>What this actually calculates</div>
                    <div style={{ fontSize: 11, lineHeight: 1.7 }}>{s.concept}</div>
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 800, color: V("muted", "#8b98a5"), textTransform: "uppercase", marginTop: 12, marginBottom: 4 }}>Steps</div>
                  <ol style={{ margin: 0, paddingLeft: 18, fontSize: 11, lineHeight: 1.7 }}>
                    {s.steps.map((step, i) => <li key={i}>{step}</li>)}
                  </ol>
                  <div style={{ marginTop: 10, padding: "8px 10px", background: WARN.bg, borderRadius: 6, border: "1px solid " + WARN.fg + "33" }}>
                    <div style={{ fontSize: 9, fontWeight: 800, color: WARN.fg, textTransform: "uppercase", marginBottom: 3 }}>{"\u26A0"} Watch out for</div>
                    <div style={{ fontSize: 10.5, lineHeight: 1.6 }}>{s.pitfall}</div>
                  </div>
                  <div style={{ marginTop: 8, padding: "8px 10px", background: "rgba(255,255,255,0.02)", borderRadius: 6 }}>
                    <div style={{ fontSize: 9, fontWeight: 800, color: V("muted", "#8b98a5"), textTransform: "uppercase", marginBottom: 3 }}>If something looks wrong</div>
                    <div style={{ fontSize: 10.5, lineHeight: 1.6, color: V("muted", "#8b98a5") }}>{s.troubleshoot}</div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <div style={{ marginTop: 20, padding: "12px 14px", background: "rgba(255,255,255,0.02)", borderRadius: 8, border: "1px solid " + V("border", "#232838") }}>
          <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>General troubleshooting</div>
          <div style={{ fontSize: 10.5, lineHeight: 1.7, color: V("muted", "#8b98a5") }}>
            <strong>"Saving..." never changes to "Saved":</strong> check your internet connection — the save will retry, and your work stays safely in the browser until it succeeds.<br/>
            <strong>Numbers seem inconsistent between tabs:</strong> everything on this page recalculates from the same underlying data automatically — if two tabs disagree, refresh the page once to rule out a display delay.<br/>
            <strong>Lost track of what you changed:</strong> check the History tab — completing a project freezes an exact copy of everything, which you can always look back at.
          </div>
        </div>
      </div>
    </div>
  );
};

const ExploreTheModel: React.FC<{ goTo:(t:TabKey)=>void; showToast?:(m:string,k?:string)=>void }> = ({ goTo, showToast }) => {
  const builtCount = MODULE_MAP.filter(m=>m.target!=null).length;
  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:10 }}>
        <div style={{ fontSize:13, fontWeight:800 }}>Explore the model</div>
        <div style={{ fontSize:10, color:"#5A6480" }}>{builtCount} of {MODULE_MAP.length} modules built and working</div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:10 }}>
        {MODULE_MAP.map((m) => {
          const built = m.target != null;
          return (
            <button key={m.n}
              onClick={() => built ? goTo(m.target as TabKey) : showToast?.(`Module ${m.n} — ${m.title} isn't built yet.`, "warning")}
              style={{ textAlign:"left", padding:"12px 14px", borderRadius:10, cursor:"pointer",
                background: built ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.01)",
                border: "1px solid " + (built ? "#232838" : "#1a1e28"),
                opacity: built ? 1 : 0.55, display:"flex", flexDirection:"column", gap:5 }}>
              <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                <span style={{ fontSize:9, fontWeight:800, color:"#5A6480" }}>{m.n}</span>
                <span style={{ fontSize:15 }}>{m.icon}</span>
                <span style={{ fontSize:11.5, fontWeight:800 }}>{m.title}</span>
                {!built && <span style={{ marginLeft:"auto", fontSize:8, fontWeight:800, color:"#8b98a5", background:"rgba(255,255,255,0.05)", padding:"2px 6px", borderRadius:6 }}>NOT BUILT</span>}
              </div>
              <div style={{ fontSize:9.5, color:"#8b98a5", lineHeight:1.4 }}>{m.desc}</div>
              {built && m.livesIn && <div style={{ fontSize:8.5, color:"#14B8A6", fontStyle:"italic" }}>Currently inside: {m.livesIn}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const StartTab: React.FC<{
  callAI?: (prompt: string, useWebSearch: boolean) => Promise<string>;
  ctx: CaBusinessContext | null;
  applyBlueprint: (bp: Blueprint, skip: { resources: Set<string>; offerings: Set<string>; channels: Set<string>; costPools: Set<string> }) => Promise<boolean>;
  hasData: boolean;
  goTo: (t: TabKey) => void;
  companyName?: string;
  openaiKey?: string;
  showToast?: (msg: string, kind?: string) => void;
  // THE FIX FOR "SHOW ME EVERYTHING FIRST, FRAGMENTED BY COST TYPE": this
  // screen previously said "Added to your model" and nothing else - the
  // user then had to go open several different tiles just to see what was
  // actually found. These let this screen show the real, complete,
  // categorized result right here, with a working delete on every single
  // line, before anyone has to touch another tile.
  resources: CaResource[]; delRes: (id: string) => void;
  costPools: CaCostPool[]; delPool: (id: string) => void;
  channels: CaChannel[]; delCh: (id: string) => void;
  offerings: CaOffering[];
  // HISTORICAL INTELLIGENCE REUSE — starting from a past completed project
  // instead of a blank AI discovery every time.
  completedProjects: CaProject[]; snapshots: CaProjectSnapshot[];
  startFromSnapshot: (s: CaProjectSnapshot) => void; delProject: (id: string) => void;
  resetWorkspace: () => void;
}> = ({ callAI, ctx, applyBlueprint, hasData, goTo, companyName, openaiKey, showToast,
        resources, delRes, costPools, delPool, channels, delCh, offerings,
        completedProjects, snapshots, startFromSnapshot, delProject, resetWorkspace }) => {
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  // A SAFETY NET, independent of the timeout fix in BusinessBlueprint.ts:
  // even if a future edge case slips past that timeout, the user must never
  // feel trapped watching a spinner with no way out. This lets them manually
  // walk away from a stuck request - it does not literally cancel the
  // underlying network call (that needs a deeper change to the shared AI
  // calling code used by every module, not just this one), but it
  // immediately frees the screen and ignores whatever that call eventually
  // returns.
  const cancelledRef = useRef(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [phase, setPhase] = useState("");
  const [res, setRes] = useState<BlueprintResult | null>(null);
  const [skipR, setSkipR] = useState<Set<string>>(new Set());
  const [skipO, setSkipO] = useState<Set<string>>(new Set());
  const [skipC, setSkipC] = useState<Set<string>>(new Set());
  const [skipP, setSkipP] = useState<Set<string>>(new Set());
  const [applied, setApplied] = useState(false);

  const bp = res?.ok ? res.blueprint : null;
  const cur = bp?.currency || ctx?.currency || "INR";
  const M = (v: number) => fmtMoney(v, cur);

  const toggle = (set: Set<string>, k: string, fn: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(k)) next.delete(k); else next.add(k);
    fn(next);
  };

  const run = async () => {
    if (!callAI) return;
    cancelledRef.current = false;
    setBusy(true); setRes(null); setApplied(false); setElapsedSec(0);
    setSkipR(new Set()); setSkipO(new Set()); setSkipC(new Set()); setSkipP(new Set());
    const steps = [
      "Working out what kind of business this is...",
      "Searching for current input prices in your market...",
      "Looking up normal wastage and utilisation for this trade...",
      "Checking real commission and fee structures...",
      "Finding where businesses like this usually lose margin...",
      "Building your draft cost model...",
      "Web search is slow - falling back to model knowledge...",
      "Trying a simplified request...",
      "Building a structural starter model for you...",
    ];
    let i = 0;
    setPhase(steps[0]);
    const timer = setInterval(() => { i = Math.min(i + 1, steps.length - 1); setPhase(steps[i]); }, 6000);
    // Honest elapsed time - a number that keeps moving proves the app is
    // still alive even during a slow network call, which the old fixed
    // phase-message list could not do once it reached its last entry.
    const clock = setInterval(() => { setElapsedSec((s) => s + 1); }, 1000);
    try {
      const r = await researchBlueprint(callAI, desc, {
        geography: ctx?.geography || "India", currency: cur, depth: "deep",
      });
      if (cancelledRef.current) return; // the user walked away - ignore this result entirely
      setRes(r);
      // THE ACTUAL BUG, FOUND AND FIXED: this used to only build a preview -
      // a SEPARATE "Apply draft" button further down the page was required
      // to actually save anything, and its own text ("Nothing is saved until
      // you press this") was easy to miss once a fully-formed-looking draft
      // was already on screen. Verified directly against the database: a
      // business context row existed, but zero resources and zero offerings -
      // proof the second step was never completing, not a guess. This now
      // saves automatically the instant research succeeds, matching what was
      // actually asked for from the start: one step, not two. Anything
      // wrongly discovered can still be deleted afterwards from the What You
      // Buy / What You Sell tabs, exactly as it already could be - nothing
      // about correcting a mistake gets harder, only "did it actually save"
      // stops depending on a click that was too easy to miss.
      if (r.ok && r.blueprint) {
        const ok = await applyBlueprint(r.blueprint, { resources: new Set(), offerings: new Set(), channels: new Set(), costPools: new Set() });
        if (ok) {
          setApplied(true);
          // THE OTHER CONFIRMED GAP: when every real research attempt fails
          // and the system falls back to a generic, no-AI starter template,
          // that fallback was being applied and shown as an ordinary
          // success - with nothing telling the user their result was NOT
          // actually researched. For a complex product where every rung
          // genuinely can fail, silence here is exactly what read as "this
          // doesn't work" instead of "this is a placeholder, fill it in."
          if (r.blueprint.confidence === "low" || (r.blueprint.warnings && r.blueprint.warnings.length)) {
            showToast?.(
              "Live research could not complete for this description, so a generic starter structure was used instead — every price shows \u201CEnter your price\u201D and needs your real numbers. Try Research again, or fill in What You Buy / What You Sell directly.",
              "warning"
            );
          }
        }
      }
    } catch (e: any) {
      if (cancelledRef.current) return;
      setRes({ ok: false, blueprint: null, rawLength: 0, stage: "failed", attempts: [], error: e?.message || "Research failed. Please try again." });
    } finally {
      clearInterval(timer); clearInterval(clock);
      if (!cancelledRef.current) { setBusy(false); setPhase(""); }
    }
  };

  const cancelRun = () => {
    cancelledRef.current = true;
    setBusy(false); setPhase(""); setElapsedSec(0);
    showToast?.("Stopped. You can try again, or edit the description first.", "warning");
  };

  const stats = bp ? blueprintStats(bp) : null;

  /* ---- no AI wired ---- */
  if (!callAI) {
    return (
      <div style={S.card}>
        <Empty title="Guided setup is not switched on"
          body="This screen can research your industry and draft the whole cost model for you, but it needs an AI provider key. Add one in Settings, or build the model by hand from the tabs above."
          action={<button style={S.btnGhost} onClick={() => goTo("inputs")}>Build it by hand instead</button>} />
      </div>
    );
  }

  return (
    <>
      {/* ---------------- input ---------------- */}
      <div style={S.card}>
        <div style={S.cardH}>Describe the business in your own words</div>
        <div style={{ ...S.note, marginBottom: 11 }}>
          You do not need to know this industry. Write what the business makes or does, roughly where it operates,
          and how it sells. The system researches current prices, normal wastage, real channel commissions and the
          usual margin leaks, then drafts the model for you to check and correct.
        </div>

        <textarea
          value={desc} onChange={(e) => setDesc(e.target.value)} rows={4} disabled={busy}
          placeholder={companyName ? `e.g. ${companyName} is a ...` : "e.g. A bakery in Lucknow making birthday cakes and bread, selling walk-in and on Swiggy"}
          style={{ ...S.inp, resize: "vertical", lineHeight: 1.55, minHeight: 84, fontSize: 12.5 }} />
        <div style={{ marginTop: 6 }}>
          <VoiceField value={desc} onChange={setDesc} disabled={busy} openaiKey={openaiKey} />
        </div>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "9px 0 12px" }}>
          {EXAMPLES.slice(0, 3).map((ex, i) => (
            <button key={i} disabled={busy} onClick={() => setDesc(ex)}
              style={{ ...S.btnGhost, fontSize: 10, opacity: busy ? 0.5 : 1, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {ex.slice(0, 44)}...
            </button>
          ))}
        </div>

        {/* HISTORICAL INTELLIGENCE REUSE — an alternative to running AI
            discovery fresh every time. Copies a completed project's real,
            already-corrected numbers in as a starting point, with brand new
            ids - editing this can never reach back and change the frozen
            history it came from. */}
        {completedProjects.length > 0 && (
          <div style={{ ...S.card, marginBottom: 14, background: "rgba(255,255,255,0.015)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>Or start from a past project</div>
            <div style={{ fontSize: 9.5, color: V("muted", "#8b98a5"), marginBottom: 8 }}>
              Making something similar to one you've already costed? Copy it in as a starting point instead of researching from scratch — every number stays fully editable.
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {completedProjects.map((p) => {
                const snap = snapshots.find((s) => s.project_id === p.id);
                if (!snap) return null;
                return (
                  <div key={p.id} style={{ position: "relative", display: "inline-block" }}>
                    <button onClick={() => startFromSnapshot(snap)}
                      style={{ ...S.btnGhost, fontSize: 10.5, textAlign: "left", padding: "8px 26px 8px 12px" }}>
                      <div style={{ fontWeight: 700 }}>{p.name}</div>
                      <div style={{ fontSize: 9, color: V("muted", "#8b98a5") }}>{snap.offerings.length} product{snap.offerings.length === 1 ? "" : "s"} \u00B7 {snap.resources.length} input{snap.resources.length === 1 ? "" : "s"}</div>
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); delProject(p.id); }} title="Delete this project"
                      style={{ position: "absolute", top: 4, right: 4, background: "none", border: "none",
                        color: V("muted", "#8b98a5"), cursor: "pointer", fontSize: 13, lineHeight: 1, padding: 2 }}>&times;</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* THE DIRECT FIX FOR THE REPORTED PROBLEM: a real, standalone
            reset — no longer buried inside "Complete Project", and calling
            a function that actually deletes the real database rows. */}
        {(resources.length > 0 || offerings.length > 0 || completedProjects.length > 0) && (
          <div style={{ marginBottom: 14, paddingTop: 10, borderTop: "1px dashed " + V("border","#232838") }}>
            <button onClick={resetWorkspace}
              style={{ background: "transparent", border: "1px solid #EF444455", color: "#EF4444",
                borderRadius: 6, padding: "6px 12px", fontSize: 10.5, fontWeight: 600, cursor: "pointer" }}>
              Reset Cost Architecture — delete everything and start fresh
            </button>
          </div>
        )}

        <div style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
          <button onClick={run} disabled={busy || desc.trim().length < 12}
            style={{ ...S.btn, opacity: busy || desc.trim().length < 12 ? 0.45 : 1, cursor: busy ? "wait" : "pointer" }}>
            {busy ? "Researching..." : "Research and build my cost model"}
          </button>
          {/* THE SAFETY NET: a real, moving elapsed-time counter (proof the
              app is still alive, not frozen) and a genuine way to stop
              waiting, requested directly after "it never comes up with a
              final result" - never make someone feel trapped again. */}
          {busy && (
            <>
              <span style={{ fontSize: 11, color: V("accent", "#4ADE80") }}>{phase} ({elapsedSec}s)</span>
              <button onClick={cancelRun} style={{ ...S.btnGhost, fontSize: 10.5, padding: "5px 10px" }}>Stop</button>
            </>
          )}
          {!busy && hasData && (
            <span style={{ ...S.note, fontSize: 10.5 }}>
              You already have data. Anything generated here is <strong style={{ color: V("ink", "#e6edf3") }}>added alongside</strong> it, never replacing it.
            </span>
          )}
        </div>
        {busy && <div style={{ ...S.note, marginTop: 9, fontSize: 10.5 }}>
          This takes 30-60 seconds because it is doing live research, not guessing.
          {elapsedSec > 45 && " If a provider is slow or unresponsive, this will automatically fall back to a faster method within 45 seconds of any single attempt - or press Stop and try again."}
        </div>}
      </div>

      {/* ---------------- error ---------------- */}
      {res && !res.ok && (
        <div style={{ ...S.card, borderColor: BAD.fg }}>
          <div style={{ ...S.cardH, color: BAD.fg, marginBottom: 6 }}>That did not work</div>
          <div style={{ fontSize: 12, lineHeight: 1.6 }}>{res.error}</div>
          {res.attempts.length > 0 && (
            <div style={{ ...S.note, marginTop: 9, fontSize: 10.5 }}>
              What was tried: {res.attempts.join(" \u2192 ")}
            </div>
          )}
          <button style={{ ...S.btnGhost, marginTop: 10 }} onClick={run}>Try again</button>
        </div>
      )}

      {/* ---------------- review ---------------- */}
      {bp && stats && (
        <>
          <div style={S.card}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
              <div style={{ flex: "1 1 320px" }}>
                <div style={S.cardH}>What we understood</div>
                <div style={{ fontSize: 12.5, lineHeight: 1.6, marginBottom: 8 }}>{bp.business_summary || "—"}</div>
                {bp.constraint_resource_label && (
                  <div style={S.note}>
                    Bottleneck identified: <strong style={{ color: V("ink", "#e6edf3") }}>{bp.constraint_resource_label}</strong>
                    {bp.constraint_reason ? " — " + bp.constraint_reason : ""}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5, alignItems: "flex-end" }}>
                <Chip tone={bp.confidence === "high" ? OK : bp.confidence === "medium" ? WARN : BAD}>
                  {bp.confidence} confidence
                </Chip>
                <Chip tone={res!.stage === "web_research" ? OK : res!.stage === "template" ? BAD : WARN}>
                  {res!.stage === "web_research" ? "Web researched"
                    : res!.stage === "model_knowledge" ? "From model knowledge"
                    : res!.stage === "compact" ? "Simplified draft"
                    : "Structural starter"}
                </Chip>
              </div>
            </div>

            <div style={{ display: "flex", gap: 9, flexWrap: "wrap", marginTop: 13 }}>
              <Tile label="Researched" value={String(stats.confirmed)} sub="figures we found" tone={OK.fg} />
              <Tile label="Check these" value={String(stats.needsCheck)} sub="typical, confirm them" tone={WARN.fg} />
              <Tile label="You must enter" value={String(stats.mustSupply)} sub="specific to this business" tone={BAD.fg} />
              <Tile label="Recipe lines" value={String(stats.recipeLines)} sub="drafted for you" />
            </div>
          </div>

          {/* ---- leaks: the consulting value ---- */}
          {bp.leaks.length > 0 && (
            <div style={S.card}>
              <div style={S.cardH}>Where this kind of business usually loses money</div>
              <div style={{ ...S.note, marginBottom: 11 }}>
                These are not calculated from your data — they are known failure patterns for this industry.
                Check each one against the business.
              </div>
              {bp.leaks.map((l, i) => (
                <div key={i} style={{ padding: "10px 0", borderBottom: i < bp.leaks.length - 1 ? `1px solid ${V("faint", "#16202c")}` : "none" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700 }}>{l.title}</div>
                    {l.typical_size && <Chip tone={WARN}>{l.typical_size}</Chip>}
                  </div>
                  <div style={{ ...S.note, marginTop: 4 }}>{l.what_happens}</div>
                  {l.how_to_spot_it && (
                    <div style={{ ...S.note, marginTop: 4, color: V("accent", "#4ADE80") }}>
                      How to check: {l.how_to_spot_it}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ---- checklist ---- */}
          {bp.checklist.length > 0 && (
            <div style={S.card}>
              <div style={S.cardH}>What you need to find out</div>
              <div style={{ ...S.note, marginBottom: 11 }}>
                Everything else is drafted. These are the answers only the business can give you — take this list to them.
              </div>
              {bp.checklist.map((c, i) => (
                <div key={i} style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: i < bp.checklist.length - 1 ? `1px solid ${V("faint", "#16202c")}` : "none" }}>
                  <div style={{ paddingTop: 2 }}>
                    <Chip tone={c.priority === "critical" ? BAD : c.priority === "important" ? WARN : NEU}>
                      {c.priority === "nice_to_have" ? "optional" : c.priority}
                    </Chip>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600 }}>{c.question}</div>
                    <div style={{ ...S.note, marginTop: 3 }}>{c.why_it_matters}{c.field_hint ? ` · Goes in: ${c.field_hint}` : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ---- inputs ---- */}
          {bp.resources.length > 0 && (
            <div style={S.card}>
              <div style={S.cardH}>Inputs we drafted ({bp.resources.length - skipR.size} of {bp.resources.length} selected)</div>
              <div style={S.scroll}>
                <table style={S.table}>
                  <thead><tr>
                    <th style={{ ...S.th, width: 34 }}>Use</th>
                    <th style={S.th}>Input</th>
                    <th style={{ ...S.th, width: 90 }}>Type</th>
                    <th style={{ ...S.th, width: 130, textAlign: "right" }}>Price</th>
                    <th style={{ ...S.th, width: 78, textAlign: "right" }}>Usable</th>
                    <th style={{ ...S.th, width: 105 }}>Status</th>
                  </tr></thead>
                  <tbody>
                    {bp.resources.map((r) => {
                      const off = skipR.has(r.name);
                      return (
                        <tr key={r.name} style={{ opacity: off ? 0.35 : 1 }}>
                          <td style={S.td} title="This has already been saved. Remove it from the What You Buy tab if it does not apply."><input type="checkbox" checked disabled style={{opacity:0.5,cursor:"not-allowed"}} /></td>
                          <td style={S.td}>
                            <div style={{ fontWeight: 600 }}>{r.name}</div>
                            {r.price_basis && <div style={{ fontSize: 9.5, color: V("muted", "#8b98a5") }}>{r.price_basis}</div>}
                          </td>
                          <td style={{ ...S.td, fontSize: 10.5, color: V("muted", "#8b98a5") }}>{r.resource_class.toLowerCase()}</td>
                          <td style={{ ...S.td, textAlign: "right", whiteSpace: "nowrap" }}>
                            {M(r.purchase_price)} <span style={{ color: V("muted", "#8b98a5"), fontSize: 10 }}>/ {r.purchase_qty} {r.purchase_uom}</span>
                          </td>
                          <td style={{ ...S.td, textAlign: "right" }}>
                            <div style={{ fontWeight: 600 }}>{r.effective_yield_pct}%</div>
                            {r.yield_reason && <div style={{ fontSize: 9, color: V("muted", "#8b98a5"), maxWidth: 150 }}>{r.yield_reason}</div>}
                          </td>
                          <td style={S.td}><Chip tone={VERIFY_STYLE[r.verify].tone}>{VERIFY_STYLE[r.verify].label}</Chip></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ---- products + recipes ---- */}
          {bp.offerings.length > 0 && (
            <div style={S.card}>
              <div style={S.cardH}>Products and recipes we drafted</div>
              {bp.offerings.map((o) => {
                const off = skipO.has(o.name);
                return (
                  <div key={o.name} style={{ opacity: off ? 0.35 : 1, padding: "10px 0", borderBottom: `1px solid ${V("faint", "#16202c")}` }}>
                    <div style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
                      <input type="checkbox" checked disabled style={{opacity:0.5,cursor:"not-allowed"}} title="This has already been saved. Remove it from What You Sell if it does not apply." />
                      <strong style={{ fontSize: 12.5 }}>{o.name}</strong>
                      <span style={{ fontSize: 10.5, color: V("muted", "#8b98a5") }}>per {o.output_uom}</span>
                      <Chip tone={VERIFY_STYLE[o.verify].tone}>
                        {o.list_price > 0 ? `${M(o.list_price)} · ${VERIFY_STYLE[o.verify].label}` : "Price needed"}
                      </Chip>
                      {o.constraint_minutes_per_unit ? (
                        <span style={{ fontSize: 10, color: V("muted", "#8b98a5") }}>{o.constraint_minutes_per_unit} bottleneck-min</span>
                      ) : null}
                    </div>
                    {o.recipe.length > 0 ? (
                      <div style={{ marginTop: 6, paddingLeft: 24 }}>
                        {o.recipe.map((l, j) => (
                          <div key={j} style={{ fontSize: 11, color: V("muted", "#8b98a5"), padding: "2px 0" }}>
                            <span style={{ color: V("ink", "#e6edf3") }}>{l.qty_per_unit} {l.uom}</span> {l.resource_name}
                            {l.process_scrap_pct > 0 ? ` · ${l.process_scrap_pct}% waste` : ""}
                            {l.basis ? ` — ${l.basis}` : ""}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ ...S.note, marginTop: 5, paddingLeft: 24, color: WARN.fg }}>No recipe drafted — you will need to add its inputs.</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ---- channels + fixed costs ---- */}
          {(bp.channels.length > 0 || bp.cost_pools.length > 0) && (
            <div style={S.card}>
              <div style={S.cardH}>Sales channels and fixed costs</div>
              <div style={S.scroll}>
                <table style={S.table}>
                  <thead><tr>
                    <th style={{ ...S.th, width: 34 }}>Use</th>
                    <th style={S.th}>Item</th>
                    <th style={{ ...S.th, width: 200 }}>Detail</th>
                    <th style={{ ...S.th, width: 105 }}>Status</th>
                  </tr></thead>
                  <tbody>
                    {bp.channels.map((c) => {
                      const off = skipC.has(c.name);
                      const drag = c.commission_pct + c.discount_pct + c.ad_spend_pct + c.payment_gateway_pct + c.returns_pct;
                      return (
                        <tr key={"ch" + c.name} style={{ opacity: off ? 0.35 : 1 }}>
                          <td style={S.td}><input type="checkbox" checked={!off} onChange={() => toggle(skipC, c.name, setSkipC)} /></td>
                          <td style={S.td}>
                            <div style={{ fontWeight: 600 }}>{c.name}</div>
                            {c.basis && <div style={{ fontSize: 9.5, color: V("muted", "#8b98a5") }}>{c.basis}</div>}
                          </td>
                          <td style={{ ...S.td, fontSize: 10.5 }}>
                            <span style={{ color: drag > 25 ? BAD.fg : drag > 12 ? WARN.fg : V("muted", "#8b98a5"), fontWeight: 700 }}>{drag.toFixed(1)}% drag</span>
                            <span style={{ color: V("muted", "#8b98a5") }}>
                              {c.commission_pct ? ` · ${c.commission_pct}% commission` : ""}
                              {c.discount_pct ? ` · ${c.discount_pct}% discount` : ""}
                            </span>
                          </td>
                          <td style={S.td}><Chip tone={VERIFY_STYLE[c.verify].tone}>{VERIFY_STYLE[c.verify].label}</Chip></td>
                        </tr>
                      );
                    })}
                    {bp.cost_pools.map((p) => {
                      const off = skipP.has(p.name);
                      return (
                        <tr key={"cp" + p.name} style={{ opacity: off ? 0.35 : 1 }}>
                          <td style={S.td}><input type="checkbox" checked={!off} onChange={() => toggle(skipP, p.name, setSkipP)} /></td>
                          <td style={S.td}>
                            <div style={{ fontWeight: 600 }}>{p.name}</div>
                            {p.basis && <div style={{ fontSize: 9.5, color: V("muted", "#8b98a5") }}>{p.basis}</div>}
                          </td>
                          <td style={{ ...S.td, fontSize: 10.5 }}>{M(p.amount)} / {p.period}</td>
                          <td style={S.td}><Chip tone={VERIFY_STYLE[p.verify].tone}>{VERIFY_STYLE[p.verify].label}</Chip></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ---- diagnostic: always visible when the AI did not fully succeed ---- */}
          {res!.stage !== "web_research" && res!.attempts.length > 0 && (
            <div style={{ ...S.card, borderColor: res!.stage === "template" ? BAD.fg : WARN.fg }}>
              <div style={{ ...S.cardH, color: res!.stage === "template" ? BAD.fg : WARN.fg, marginBottom: 6 }}>
                {res!.stage === "template" ? "The AI did not respond - here is exactly what happened" : "The AI partly succeeded - what was tried"}
              </div>
              <div style={{ ...S.note, marginBottom: 9 }}>
                {res!.stage === "template"
                  ? "Every attempt failed, so the model above was built without AI. The reason is below - send it to support and it can be fixed at source."
                  : "An earlier attempt failed and a later one succeeded. Figures are usable but less current."}
              </div>
              <div style={{ background: V("bg", "#070c18"), border: `1px solid ${V("border", "#1e2a38")}`,
                            borderRadius: 5, padding: 10, fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace",
                            fontSize: 10.5, lineHeight: 1.65, whiteSpace: "pre-wrap", wordBreak: "break-word",
                            maxHeight: 220, overflowY: "auto" }}>
                {res!.attempts.map((a, i) => `${i + 1}. ${a}`).join("\n")}
              </div>
              <button style={{ ...S.btnGhost, marginTop: 9 }}
                onClick={() => {
                  const txt = "OrchestrIQ Cost Architecture diagnostic\nStage: " + res!.stage +
                    "\nLast reply length: " + res!.rawLength + " chars\n\n" +
                    res!.attempts.map((a, i) => `${i + 1}. ${a}`).join("\n");
                  try {
                    const nav: any = navigator;
                    if (nav?.clipboard?.writeText) { nav.clipboard.writeText(txt); }
                    else {
                      const ta = document.createElement("textarea");
                      ta.value = txt; document.body.appendChild(ta); ta.select();
                      document.execCommand("copy"); document.body.removeChild(ta);
                    }
                  } catch { /* clipboard blocked - the text is on screen anyway */ }
                }}>
                Copy diagnostic
              </button>
            </div>
          )}

          {/* ---- honesty ---- */}
          {(bp.warnings.length > 0 || bp.research_notes || bp.sources.length > 0) && (
            <div style={S.card}>
              <div style={S.cardH}>What to be careful about</div>
              {bp.warnings.map((w, i) => (
                <div key={i} style={{ fontSize: 11.5, marginBottom: 5, color: WARN.fg, lineHeight: 1.55 }}>• {w}</div>
              ))}
              {bp.research_notes && <div style={{ ...S.note, marginTop: 8 }}>{bp.research_notes}</div>}
              {bp.sources.length > 0 && (
                <div style={{ ...S.note, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${V("faint", "#16202c")}` }}>
                  <strong style={{ color: V("ink", "#e6edf3") }}>Sources used:</strong> {bp.sources.join(" · ")}
                </div>
              )}
            </div>
          )}

          {/* ---- saved automatically - no separate click required anymore ---- */}
          <div style={{ ...S.card, borderColor: V("accent", "#4ADE80") }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, color: applied ? OK.fg : undefined }}>
              {applied ? "\u2713 Here\u2019s everything found for you" : "Saving\u2026"}
            </div>
            <div style={S.note}>
              {applied
                ? "Every cost below is already saved and grouped by type, so you can see the whole picture before opening any tile. Not right? Delete anything below with the \u2715, or fix the exact number later in its own tile."
                : "Saving what was found\u2026"}
            </div>
          </div>

          {/* THE ACTUAL FIX REQUESTED: "give me all the costing parameters,
              fragmented into different types of cost" - before this, the
              only way to see what was discovered was to open several
              different tiles one at a time. This groups every real,
              already-saved cost by its actual category (the same categories
              CostEngine.ts itself uses - not invented buckets), in plain
              language, with a working delete on every single line, right
              here, before anyone has to touch another tile. */}
          {applied && <FullCostBreakdown resources={resources} delRes={delRes} costPools={costPools}
            delPool={delPool} channels={channels} delCh={delCh} offerings={offerings} cur={cur} />}
        </>
      )}
    </>
  );
};

/* ============================================================================
 * THE FULL, FRAGMENTED COST BREAKDOWN - every discovered item, grouped by
 * real cost type, in plain language, editable inline.
 * ========================================================================== */
const RESOURCE_CLASS_INFO: Record<string, { label: string; explain: string; icon: string }> = {
  MATERIAL:    { label: "Materials & Ingredients", explain: "What goes directly into making your product.", icon: "\uD83C\uDF3E" },
  LABOUR:      { label: "Labour & People", explain: "What you pay people to do the work.", icon: "\uD83D\uDC65" },
  PACKAGING:   { label: "Packaging", explain: "Boxes, jars, labels, wrapping - anything around the product itself.", icon: "\uD83D\uDCE6" },
  LOGISTICS:   { label: "Logistics & Delivery", explain: "Getting your product from where it's made to where it's sold.", icon: "\uD83D\uDE9A" },
  EQUIPMENT:   { label: "Equipment & Machinery", explain: "Tools and machines you use to make or deliver your product.", icon: "\u2699\uFE0F" },
  FACILITY:    { label: "Facility & Space", explain: "Rent, workspace, and anywhere the work physically happens.", icon: "\uD83C\uDFE2" },
  ENERGY:      { label: "Energy & Utilities", explain: "Electricity, gas, water, and similar running costs.", icon: "\u26A1" },
  DIGITAL:     { label: "Software & Technology", explain: "Apps, subscriptions, and digital tools you pay for.", icon: "\uD83D\uDCBB" },
  SUBCONTRACT: { label: "Outside Help & Contractors", explain: "Work you pay someone else to do for you.", icon: "\uD83E\uDD1D" },
  OTHER:       { label: "Other Costs", explain: "Anything that didn't fit a more specific category.", icon: "\uD83D\uDCC4" },
};

const FullCostBreakdown: React.FC<{
  resources: CaResource[]; delRes: (id: string) => void;
  costPools: CaCostPool[]; delPool: (id: string) => void;
  channels: CaChannel[]; delCh: (id: string) => void;
  offerings: CaOffering[]; cur: string;
}> = ({ resources, delRes, costPools, delPool, channels, delCh, offerings, cur }) => {
  const M = (v: number) => fmtMoney(v, cur);
  const byClass = new Map<string, CaResource[]>();
  resources.forEach((r) => {
    const k = r.resource_class || "OTHER";
    if (!byClass.has(k)) byClass.set(k, []);
    byClass.get(k)!.push(r);
  });

  const Row: React.FC<{ name: string; cost: string; onDelete: () => void }> = ({ name, cost, onDelete }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid " + V("border", "#1a2030") }}>
      <span style={{ fontSize: 11.5 }}>{name}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700 }}>{cost}</span>
        <button onClick={onDelete} title="Remove this" style={{ background: "none", border: "none", color: BAD.fg, cursor: "pointer", fontSize: 12, padding: 0 }}>{"\u2715"}</button>
      </div>
    </div>
  );

  return (
    <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
      {Array.from(byClass.entries()).map(([cls, items]) => {
        const info = RESOURCE_CLASS_INFO[cls] || RESOURCE_CLASS_INFO.OTHER;
        return (
          <div key={cls} style={S.card}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 16 }}>{info.icon}</span>
              <span style={{ fontSize: 12.5, fontWeight: 800 }}>{info.label}</span>
            </div>
            <div style={{ fontSize: 10, color: V("muted", "#8b98a5"), marginBottom: 8 }}>{info.explain}</div>
            {items.map((r) => (
              <Row key={r.id} name={r.name} cost={M(effectiveCostPerBaseUnit(r)) + " / " + (r.base_uom || "unit")} onDelete={() => delRes(r.id)} />
            ))}
          </div>
        );
      })}

      {costPools.length > 0 && (
        <div style={S.card}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 16 }}>{"\uD83D\uDCC4"}</span>
            <span style={{ fontSize: 12.5, fontWeight: 800 }}>Overhead & Fixed Costs</span>
          </div>
          <div style={{ fontSize: 10, color: V("muted", "#8b98a5"), marginBottom: 8 }}>Bills you pay every month regardless of how much you sell - rent, salaries, software.</div>
          {costPools.map((p) => (
            <Row key={p.id} name={p.name} cost={M(num(p.amount)) + " / " + (p.period || "month")} onDelete={() => delPool(p.id)} />
          ))}
        </div>
      )}

      {channels.length > 0 && (
        <div style={S.card}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 16 }}>{"\uD83D\uDED2"}</span>
            <span style={{ fontSize: 12.5, fontWeight: 800 }}>Marketing & Sales Channels</span>
          </div>
          <div style={{ fontSize: 10, color: V("muted", "#8b98a5"), marginBottom: 8 }}>What it costs to find and sell to a customer through each place you sell.</div>
          {channels.map((c) => (
            <Row key={c.id} name={c.name} cost={(num(c.commission_pct) + num(c.ad_spend_pct)).toFixed(0) + "% of each sale"} onDelete={() => delCh(c.id)} />
          ))}
        </div>
      )}

      {offerings.length > 0 && (
        <div style={{ fontSize: 10.5, color: V("muted", "#8b98a5"), padding: "8px 4px" }}>
          {offerings.length} product{offerings.length===1?"":"s"} or service{offerings.length===1?"":"s"} found and saved - open "What you sell" to see the full price, profit and break-even for each one.
        </div>
      )}
    </div>
  );
};

/* ============================================================================
 * DATA QUALITY PANEL
 * Flags values that cannot be true, says why in plain language, shows what each
 * choice does to the answer, and lets the user knowingly keep their own number.
 * ========================================================================== */

const DataQualityBar: React.FC<{
  summary: { errors: number; warnings: number; infos: number; total: number; trustable: boolean; headline: string };
  findings: Finding[];
  overrideCount: number;
  goTo: (t: TabKey) => void;
  onUndoAll: () => void;
}> = ({ summary, findings, overrideCount, goTo, onUndoAll }) => {
  if (!findings.length && !overrideCount) return null;

  const bucket: Record<string, TabKey> = {
    resource: "inputs", offering: "products", bom: "products",
    channel: "channels", pool: "setup", portfolio: "diagnostics",
  };
  const TAB_NAME: Record<string, string> = {
    inputs: "What you buy", products: "What you sell",
    channels: "Where you sell", setup: "Setup", diagnostics: "Diagnostics",
  };
  const where = new Map<TabKey, number>();
  for (const f of findings) {
    if (f.severity === "info") continue;
    const t = bucket[f.scope] as TabKey; if (!t) continue;
    where.set(t, (where.get(t) || 0) + 1);
  }

  const tone = summary.errors > 0 ? BAD : summary.warnings > 0 ? WARN : OK;

  return (
    <div style={{ ...S.card, borderColor: summary.errors > 0 ? BAD.fg : summary.warnings > 0 ? WARN.fg : V("border", "#1e2a38"),
                  marginBottom: 14, padding: "11px 14px" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <Chip tone={tone}>{summary.errors > 0 ? "Data check" : summary.warnings > 0 ? "Data check" : "All clear"}</Chip>
        <div style={{ flex: "1 1 240px" }}>
          <div style={{ fontSize: 12.5, fontWeight: 700 }}>{summary.headline}</div>
          <div style={{ fontSize: 10.5, color: V("muted", "#8b98a5"), marginTop: 2 }}>
            {findings.length
              ? <>Each one is marked with a red <strong style={{ color: BAD.fg }}>!</strong> on the exact box it refers to. Click the marker to see why.</>
              : "No problems found in what you have entered."}
            {overrideCount > 0 && ` \u00B7 ${overrideCount} accepted as correct by you`}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {Array.from(where.entries()).map(([t, n]) => (
            <button key={t} style={{ ...S.btnGhost, fontSize: 10.5 }} onClick={() => goTo(t)}>
              {TAB_NAME[t]} <strong style={{ color: BAD.fg }}>{n}</strong>
            </button>
          ))}
          {overrideCount > 0 && (
            <button style={{ ...S.btnGhost, fontSize: 10.5 }} onClick={onUndoAll}>Un-accept all</button>
          )}
        </div>
      </div>
    </div>
  );
};
